import { Component, computed, effect, ElementRef, HostListener, inject, Injector, OnDestroy, OnInit, Signal, signal, TemplateRef, untracked, ViewChild, WritableSignal } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { DndDropEvent } from 'ngx-drag-drop';
import Konva from 'konva';
import { tinykeys } from 'tinykeys';
import moment from 'moment';
import * as jsts from 'jsts/dist/jsts';
import { BehaviorSubject, EMPTY, firstValueFrom, forkJoin, from, lastValueFrom, map, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, last, switchMap, tap } from 'rxjs/operators';

// models
import { Alignment, AlignmentTypes } from '../models/alignment';
import { Block } from '../models/block';
import { BoxElement } from '../models/elements/boxElement';
import { ComponentCanDeactivate } from '../../core/models/canDeactivate';
import { CopyingData } from '../../core/models/copyingData';
import { CopyingNotification } from '../../core/models/copyingNotification';
import { DwUserActivityNames } from '../../core/models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../core/models/dwUsersActivityTypes';
import { Element } from '../../equipment/models/elements/element';
import { EMPTY_PRODUCT_IMAGE_PATH, ProductsService } from '../../core/services/products.service';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { GroupByCategory } from './components/layout-rules/models/group-base';
import { Figure } from '../../equipment/models/figures/figure';
import { FunctionalType } from '../../core/models/blockedFunctionalType';
import { GoldenShelf } from '../../core/models/goldenShelf';
import { HangBarElement } from '../models/elements/hangBarElement';
import { HighlightOptionsValue, HighlightProductsService } from '../../core/services/highlight-products.service';
import { HistoryInstance } from '../models/history-instance';
import { IAutoLayoutParams, IReportParams } from './components/autolayout/auto-layout-panel.component';
import { ICanPutProductsResult } from '../models/canPutProductsResult';
import { IChatUser, LayoutService } from '../../core/services/layout.service';
import { IndicatorVisual, IPlanogramIndicator, resolveIndicatorVisual } from '../../core/models/indicators';
import { IColumnType, ITableData, ITableRowClickData } from '../../core/models/table';
import { IConfirmModalAnswer } from '../../core/modals/confirm-modal/models/confirmModalAnswer';
import { IConfirmModalData } from '../../core/modals/confirm-modal/models/confirmModalData';
import { IInfoModalData } from '../../core/modals/info-modal/models/infoModalData';
import { CanvasDropRequest, CanvasUpdateRequest, ILabelsState, IPlanogramHistoryState, NORMALIZED_ELEMENTS, PlanogramActivity } from '../services/planogram-activity.service';
import { PlanogramCanvasLayer } from '../models/planogram-canvas-layer';
import { ILayoutConfigFormParams } from './components/layout-config/layout-config.component';
import { elementsGenerator } from '../../equipment/models/elements/elementGenerator';
import { Equipment } from '../../equipment/models/equipment';
import { EquipmentBase } from '../models/equipment-base';
import { Extension, ExtensionType, RailsParams } from '../../equipment/models/elements/extension';
import { IEquipment, IEquipmentConfigurations } from '../../equipment/models/models';
import { IOnboardingModalData } from '../../core/models/onBoardingModalData';
import { ICompressionParams, IProductCard, IProductConf } from '../../product/models/models';
import { IPlanogram } from '../models/planogram';
import { IPlanogramData, IPlanogramPlanData } from '../models/planogramData';
import { IProductPacking } from '../../product/models/product-card';
import { IProductsTableItem } from './models/productsTableItem';
import { IStandConversationDone, IStandConversationImage, IStandConversationMessage, PlanogramsService, StandConf } from '../../core/services/planograms.service';
import { konvaSettings } from '../../equipment/models/konvaSettings';
import { LayoutRule } from './components/layout-rules/models/layoutRule';
import { MeasurementSystem } from '../../core/models/measurementSystem';
import { MixpanelEvents } from '../../core/models/mixpanelEvents';
import { PlanogramViewBehavior } from '../models/planogramViewBehavior';
import { PlanogramViewBehaviorFactory } from '../models/planogramViewBehaviorFactory';
import { PlatformApplications } from '../../core/models/platformApplications';
import { Point } from '../../core/models/point';
import { POSTextMaterial } from '../models/POSMaterials/POSTextMaterial';
import { POSMaterial } from '../models/POSMaterials/POSMaterial';
import { Product } from '../models/product';
import { CompressionType, ProductErrors, ProductGroup } from '../models/product-group';
import { PersonalSettings, SearchAlgorithmType } from '../../core/models/personalSettings';
import { ProductGroupRotation } from '../models/product-group-rotation';
import { Range } from '../../core/models/range';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { RuleLevel } from './components/layout-rules/models/rule-level';
import { ServiceExtensions } from '../../core/models/serviceExtensions';
import { ServiceFeatures } from '../../core/models/serviceFeatures';
import { ShelfElement } from '../models/elements/shelfElement';
import { Side } from '../../core/models/side';
import { SocketGroup, SocketService } from '../../core/services/socket.service';
import { SortParams } from './components/brand-info-panel/brand-info-panel.component';
import { Wall } from '../../plan/models/wall';
import { IUserSave, UsersActivityData } from '../../core/models/usersActivityData';
import { UnitConverter } from '../../core/utils/unitConverter';
import { MeasurementUnitPipe } from '../../core/pipes/measurement-unit.pipe';
import { MeasurementUnitTranslatePipe } from '../../core/pipes/measurement-unit-translate.pipe';
import { Bounding } from '../../core/models/bounding';
import { DefaultProductParams } from '../../core/models/defaultProductParams';
import { POSImageMaterial, POSMaterialImageSaveObject } from '../models/POSMaterials/POSImageMaterial';

// components
import { ConfirmModalComponent } from '../../core/modals/confirm-modal/confirm-modal.component';
import { CopyPlanogramModalComponent } from '../modals/copy-planogram-modal/copy-planogram-modal.component';
import { ExcelToLayoutComponent } from './modals/excel-to-layout/excel-to-layout.component';
import { EquipmentCreateNameModalComponent } from '../../equipment/modals/equipment-create-name-modal/equipment-create-name-modal.component';
import { EquipmentModelComponent } from '../../core/components/equipment-model/equipment-model.component';
import { PlanogramCanvasComponent } from '../../core/components/planogram-canvas/planogram-canvas.component';
import { PlanogramGroupsPanelComponent } from '../../core/components/planogram-groups-panel/planogram-groups-panel.component';
import { IPlanPlanogramGroup } from '../../core/models/planogramGroups';
import { PhSidePanelService } from '../../core/services/ph-side-panel.service';
import { InfoModalComponent } from '../../core/modals/info-modal/info-modal.component';
import { LayoutHistoryComponent } from '../../core/components/layout-history/layout-history.component';
import { OnboardingModalComponent } from '../../core/modals/onboarding-modal/onboarding-modal.component';
import { PhSimpleTableComponent } from '../../core/components/ph-simple-table/ph-simple-table.component';
import { ProductsAddExcelModalComponent } from './modals/products-add-excel-modal/products-add-excel-modal.component';

// services
import { AddProductsService } from '../../core/services/add-products.service';
import { IAddProductsPanelParams } from './models/addProductsPanelFilters';
import { BlockedFunctionalService } from '../../core/services/blocked-functional.service';
import { CanvasDrawerService } from '../../core/services/canvas-drawer.service';
import { DragOutsideService } from '../../core/services/drag-outside.service';
import { DwUsersActivityService } from '../../core/services/dw-users-activity.service';
import { EquipmentService, IEquipmentUpdatePayload } from '../../core/services/equipment.service';
import { FiltersService } from '../../core/services/filters.service';
import { LayoutRulesService } from '../../core/services/layout-rules.service';
import { MixpanelService } from '../../core/services/mixpanel.service';
import { PlanogramLayoutService } from '../services/planogram-layout.service';
import { PlanogramTooltipService } from '../services/planogram-tooltip.service';
import { IPlanInfo, PlansService } from '../../core/services/plans.service';
import { ProductCardService } from '../../core/services/product-card.service';
import { RulesService } from './components/layout-rules/layout-rules.service';
import { SessionService } from '../../core/services/session.service';
import { ChatService } from '../../core/services/chat.service';
import { SidePanelService } from '../../core/services/side-panel.service';
import { SnackBarService } from '../../core/services/snack-bar.service';
import { Utils } from '../../core/services/utils.service';
import { POSMaterialsService } from '../services/pos-materials.service';
import { POSImageMaterialConfigData } from './components/pos-image-material-config/pos-image-material-config.component';
import { GlobalSettings } from '../../core/models/globalSettings';
import { NotificationsService } from '../../core/services/notifications.service';
import { DetailedElementWidgetOverlayService } from '../services/detailed-element-widget-overlay.service';
import { OverlayZIndexService } from '../services/overlay-z-index.service';
import { PlanogramCanvasOverlayService } from '../services/planogram-canvas-overlay.service';
import { PlanogramGroupUsersActivityService } from '../services/planogram-group-users-activity.service';
import { PlanogramModes } from '../services/planogram-modes.service';
import { PlanogramResolverService } from '../services/planogram-resolver.service';
import { HoleElement } from '../models/elements/holeElement';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HookElement } from '../models/elements/hookElement';
import { PalletElement } from '../models/elements/palletElement';
import { PlanogramCanvasWidgetComponent } from './components/planogram-canvas-widget/planogram-canvas-widget.component';

const LEFT_PANEL_DEFAULT_WIDTH_PX: number = 550,
    RENDER_DEBOUNCE_TIME: number = 1e3,
	PRODUCTS_TABLE_PAGE_SIZE: number = 30,
    Coordinate = jsts.geom.Coordinate,
    GEOMETRY_FACTORY = jsts.geom.GeometryFactory,
    EPS_3 = 1e-3,
    geometryFactory = new GEOMETRY_FACTORY();

const DRAGGABLE_ELEMENTS: { front: Array<EquipmentElements>; top: Array<EquipmentElements> } = {
	front: [
		EquipmentElements.Shelf,
		EquipmentElements.AngleShelf,
		EquipmentElements.HangBar,
		EquipmentElements.HookPanel,
		EquipmentElements.Hook,
		EquipmentElements.Perforation
	],
	top: [
		EquipmentElements.Box,
		EquipmentElements.SlicedBox,
		EquipmentElements.Partition
	]
};

interface AutoLayoutParams {
	stands: any[];
	blocks: Block[];
	productOptions: IAutoLayoutParams;
	percentDictionary: any;
	hierarchy: any[];
	cachedPlacePredict: any;
	dataGrouped?: boolean;
	groupProperty?: string;
	plannedBrands?: string[];
	plannedBrandsPercent?: any;
	plannedCategories?: string[];
	plannedCategoriesPercent?: any;
	hasReceipts: boolean;
	personalSettings: PersonalSettings;
}

type ConversationCacheEntry = {
	chatMessages: Array<IStandConversationMessage>;
	next_messages: number | string | null;
};

@Component({
    selector: 'app-planogram-edit',
    templateUrl: './planogram-edit.component.html',
    styleUrls: ['./planogram-edit.component.scss'],
    providers: [HighlightProductsService, RulesService, SidePanelService, ProductCardService, PlanogramActivity, PlanogramModes, CanvasDrawerService, DragOutsideService, OverlayZIndexService, DetailedElementWidgetOverlayService, PlanogramCanvasOverlayService, PlanogramGroupUsersActivityService, PlanogramResolverService, PhSidePanelService, ChatService],
    animations: [
        trigger('productListState', [
            state('inactive', style({ height: '100%' })),
            state('active', style({ height: 'calc(100% - 120px)' })),
            transition('inactive => active', animate('300ms')),
            transition('active => inactive', animate('300ms'))
        ]),
        trigger('productSelectState', [
            state('inactive', style({ left: '-300px' })),
            state('active', style({ left: '231px' })),
            transition('inactive => active', animate('300ms')),
            transition('active => inactive', animate('300ms'))
        ])
    ],
    standalone: false
})
export class PlanogramEditComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

	@ViewChild('productPreview') productPreview: ElementRef;
	@ViewChild('standLayoutWorkplace') standLayoutWorkplace: ElementRef<HTMLElement>;
	@ViewChild('saveTemplate') saveTemplate: EquipmentModelComponent;
	@ViewChild('layoutHistoryComponent') layoutHistoryComponent: LayoutHistoryComponent;
	@ViewChild('extendedProductErrors') extendedProductErrors: TemplateRef<any>;
	@ViewChild('planogramCanvas') planogramCanvas: PlanogramCanvasComponent;

	hasAssortment: boolean = false;
	hasProducts: boolean = false;
	scrollToMessage: number;
    planograms: WritableSignal<Array<IPlanogram>> = signal([]);
	socketSubscriptions: Array<Subscription> = [];
	selectedStand: any = -1;
	selectedPlanogram: any = -1;
	blockAutolayout: boolean = false;
	isRenderingReversed: boolean = false;
	equipmentUsage: Array<number> = [];
	planAccess: string = '';
	planogramScheduledDate: WritableSignal<string> = signal('');
	masterRuleIndicator: WritableSignal<IndicatorVisual | null> = signal(null);
	standId: number;
	standUpdatedAt: WritableSignal<string | null> = signal(null);
	planogramList: Array<{ id: number, name: string }> = [];
	chatMessages: Array<IStandConversationMessage> = [];
	chatUsers: Array<IChatUser> = [];
	changedProducts: any;
	readonly productAddPanelOpen = signal(false);
	readonly productAddTargetPlanogram = signal<IPlanogram | null>(null);
	readonly productAddExistsProducts = computed((): Array<Product> => {
		const targetPlanogram: IPlanogram | null = this.productAddTargetPlanogram();

		if (targetPlanogram) return targetPlanogram.products();

		return this.products();
	});
	readonly productAddPlanogramId = computed((): number | null => {
		const targetPlanogram: IPlanogram | null = this.productAddTargetPlanogram();

		if (targetPlanogram) return targetPlanogram.main_stand;

		if (this.planograms().length === 1) return this.planograms()[0].main_stand;

		return null;
	});
	planFilters = [];
	planogramFilters: Array<any> = [];
	planogramGroup: IPlanPlanogramGroup;
	public modes: PlanogramModes = inject(PlanogramModes);
	readonly planogramResolver: PlanogramResolverService = inject(PlanogramResolverService);
	private readonly sessionService: SessionService = inject(SessionService);
	public readonly activity: PlanogramActivity = inject(PlanogramActivity);
	readonly isBlockedReversed = computed(() => {
		this.planograms();

		const planogram: IPlanogram = this.activity.planogram();

		if (!planogram) return true;

		return this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)
			&& !!planogram.stand.blocks?.length;
	});
	readonly hasEquipmentBoxes = computed(() => {
		const planograms: Array<IPlanogram> = this.planograms();
		if (!planograms.length) return false;
		return planograms.every((planogram: IPlanogram) => planogram.stand.hasBoxes);
	});
	readonly allPlanogramsAreIsland = computed(() => {
		const planograms: Array<IPlanogram> = this.planograms();
		if (!planograms.length) return false;
		return planograms.every((planogram: IPlanogram) =>
			planogram.stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))
		);
	});
	readonly isSaveNewEquipmentDisabled = computed((): boolean => {
		const planograms: Array<IPlanogram> = this.planograms();
		if (planograms.length < 2) return false;

		for (const equipmentId of this.getChangedPlanogramsByEquipmentId().keys()) {
			const usageInGroup: number = planograms.filter(
				(planogram: IPlanogram) => planogram.stand.id === equipmentId
			).length;

			if (usageInGroup >= 2) return true;
		}

		return false;
	});
	readonly products = computed((): Array<Product> => {
		return this.planograms().flatMap((planogram: IPlanogram) => planogram.products());
	});
	readonly equipmentEditStands = computed(() => this.planograms().map((planogram: IPlanogram) => planogram.stand));
	changeWidth: boolean = true;
	allowedAutoLayout: boolean = false;
	isManager: boolean = false;
	brandsInfo: any[] = [];
	brandsInfoCopy: any[] = [];
	categoriesInfo: any[] = [];
	categoriesInfoCopy: any[] = [];
	reportParams: IReportParams = { turnover: 0.5, qty: 0.2, receipts_qty: 0.2, profit: 0.1 };
	saveAble: WritableSignal<boolean> = signal(false);
    ignoreOtherSave: WritableSignal<boolean> = signal(true); // false if the planogram has been saved and the user has not ignored it
    ignoreOtherEquipmentSave: boolean = true; // false if the equipment has been saved and the user has not ignored it
	loader = signal(false);
	readonly showLoader: Signal<boolean> = computed(() =>
		this.planogramResolver.state$().loading || this.loader()
	);
	layoutRules: boolean = false;
	readonly layoutRulesPlanogram = signal<IPlanogram | null>(null);
	private layoutRulesOpenedPlanogramIds: Array<number> | null = null;
	readonly layoutRulesProducts = computed((): Array<Product> => {
		const planogram: IPlanogram | null = this.layoutRulesPlanogram() ?? this.activity.planogram();

		if (!planogram) return [];

		planogram.products();

		return planogram.products();
	});
	readonly analyticsPlanogram = computed((): IPlanogram | undefined => {
		this.activity.planogram();
		this.planograms();

		if (this.planograms().length > 1) {
			return this.activity.planogram() ?? this.planograms()[0];
		}

		this.activity.focusedCanvas();

		return this.resolveActivePlanogram() ?? this.planograms()[0];
	});
	next_messages: number | string | null = 1;
	isNeedApplyChanges: boolean = false;
	POSMaterialsClipboard: Array<POSMaterial> = [];
	productClipboard: ProductGroup[] = [];
	saveTimeout: any = null;
	private saveObserver: Observable<void> | null = null;
	saveInProgress: WritableSignal<boolean> = signal(false);
	readonly isSaveDisabled: Signal<boolean> = computed((): boolean =>
        this.showLoader()
		|| !this.saveAble()
		|| this.saveInProgress()
		|| this.modes.equipmentEditMode()
		|| !this.ignoreOtherSave()
	);
	pingInterval = null;
	cachedReportData: any = {};
	cachedReportParams: any = {};
	cachedAutolayoutParams: IAutoLayoutParams = {
		groupBy: 'none',
		sortBy: 'none',
		sortType: 'asc',
		maxFacesQty: null,
		checkSizes: true,
		useMasterPositions: true,
		useSales: false,
		hasBrands: true,
		hasSuppliers: true
	};
	autoLayoutExtraMapper: any = {
		brand: 'category',
		category: 'brand',
		supplier: 'brand',
		none: 'brand'
	};
	static autoLayoutGroupMapper: any = {
		category: 'categoriesUniqueId',
		brand: 'brandsUniqueId',
		supplier: 'suppliersUniqueId'
	};
	autoLayoutHierachy: any = {
		1: 'brand',
		2: 'supplier',
		3: 'category'
	};
	cachedPlacePredict: any = {};
	layoutOpen: boolean = false;
	private conversationPlanogramId: number = null;
	private readonly conversationCache: Map<number, ConversationCacheEntry> = new Map();
	private readonly subscribedConversationStandIds: Set<number> = new Set();
	analysisOpen: boolean = false;
	productsAnalytic: any = [];
	panelLoader: boolean = false;
	planConf: IPlanInfo;
	userIsStoreManager: boolean = true;
	brandsRecommendedPercent: any = {};
	categoriesRecommendedPercent: any = {};
	brandsPanelDataGrouped: boolean = false;
	brandsPanelSorting: SortParams = null;
	private _placePredictCache: Record<number, Record<string, any>> = {};
	productsStock: any = {};
	abcSubscription: Subscription = Subscription.EMPTY;
	abcDataByStand: Record<number, any> = {};
	pausedInterval = signal(false);
	activeRule: any;
	minBlockSize: number;
    minStep: number;
    minShiftStep: number;
	labelsDate: any = {
		date_to: this.filtersService.filters.date_to || moment(this.sessionService.defaults.date_to),
		date_from: this.filtersService.filters.date_from || moment(this.sessionService.defaults.date_to).subtract(29, 'day')
	};
	renderBehavior: BehaviorSubject<any> = new BehaviorSubject(null);
	tinyKeysSubscription: any;

	// products panel block
	categoriesLevels: number = 0;
	clickProductsTableSubject: Subject<ITableRowClickData<IProductsTableItem>> =
		new Subject<ITableRowClickData<IProductsTableItem>>();
	filterProductTypes: Array<{ value: string, translate: string }> = [];
	searchQuery: WritableSignal<string> = signal('');
	selectedProductFilter: WritableSignal<string> = signal('all');
	sortingProductsTableSubject: Subject<string> = new Subject<string>();
	sortProductsParams = { order: true, property: '' };
	startDraggingProductsSubject: Subject<Array<Product>> = new Subject<Array<Product>>();
	panelProducts: Array<Product>;
	panelProductsItems: Array<IProductsTableItem>;
	productsGroupingAvailable: boolean = true;
	productsTableData: ITableData<IProductsTableItem>;
	productsTablePage: number = 1;
	productsTableSubscriptions: Array<Subscription> = [];
	updateProductsTableSubject: Subject<void> = new Subject<void>();
    usersActivitySubject: BehaviorSubject<UsersActivityData>;
	@ViewChild('productsTable') productsTable: PhSimpleTableComponent;

	// resize left panel block
	private isResizing: boolean = false;
	private startX: number = 0;
	private startWidth: number = 0;
	leftPanelSavedWidth: number = LEFT_PANEL_DEFAULT_WIDTH_PX;
	leftPanelClosed: boolean = false;
	@ViewChild('leftResizablePanel') leftResizablePanel: ElementRef<HTMLDivElement>;

	protected readonly extensions: typeof ServiceExtensions = ServiceExtensions;
	protected readonly features: typeof ServiceFeatures = ServiceFeatures;
	static getNextPage(str) {
		return str ? (str.match(/page=(\d+)/i) || [])[1] || null : null;
	}

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private translate: TranslateService,
		private injector: Injector,
		private overlay: Overlay,
		private planogramEditTooltipService: PlanogramTooltipService,
		private addProductsFilterService: AddProductsService,
		private blockedFunctionalService: BlockedFunctionalService,
		private canvasDrawerService: CanvasDrawerService,
		private dragOutsideService: DragOutsideService,
		private equipmentService: EquipmentService,
		private filtersService: FiltersService,
		private highlightProductsService: HighlightProductsService,
		private layoutRulesService: LayoutRulesService,
		private layoutService: LayoutService,
		private notificationsService: NotificationsService,
		private mixPanelService: MixpanelService,
		private planogramLayoutService: PlanogramLayoutService,
		private planogramsService: PlanogramsService,
		private plansService: PlansService,
		private productsService: ProductsService,
		private productCardService: ProductCardService,
		private rulesService: RulesService,
		private snackBarService: SnackBarService,
		private socketService: SocketService,
		private dwUsersActivityService: DwUsersActivityService,
		private measurementUnitPipe: MeasurementUnitPipe,
		private measurementUnitTranslatePipe: MeasurementUnitTranslatePipe,
		private POSMaterialsService: POSMaterialsService,
		private detailedElementWidgetOverlayService: DetailedElementWidgetOverlayService,
        private overlayZIndexService: OverlayZIndexService,
		readonly planogramCanvasOverlayService: PlanogramCanvasOverlayService,
		private planogramGroupUsersActivityService: PlanogramGroupUsersActivityService,
		private phSidePanelService: PhSidePanelService,
	) {
		this.activity.setEditMode(true);
		this.hasAssortment = this.sessionService.defaults.has_assortment;
		this.scrollToMessage = this.route.snapshot.queryParams.message;

		this.filterProductTypes.push(
			{ value: 'all', translate: 'ALL' },
			{ value: 'laid_out', translate: 'LAID_OUT' },
			{ value: 'not_laid_out', translate: 'NOT_LAID_OUT' }
		);

		if (this.sessionService.hasFeature(ServiceFeatures.AssortmentTypes)) {
			this.filterProductTypes.push(
				{ value: 'active', translate: 'COMMON.ASSORTMENT.ACTIVE_MANY' },
				{ value: 'inactive', translate: 'COMMON.ASSORTMENT.INACTIVE_MANY' }
			);
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
			this.filterProductTypes.push(
				{ value: 'stock_qty', translate: 'WITH_STOCK' },
				{ value: 'not_stock_qty', translate: 'WITHOUT_STOCK' }
			);
		}

		const elementMoveSteps: Map<MeasurementSystem, number> = new Map<MeasurementSystem, number>([
			[MeasurementSystem.Metrics, 0.001],
			[MeasurementSystem.Imperial, UnitConverter.getSaveConvertValue(0.01)]
		]);
		const elementShiftMoveSteps: Map<MeasurementSystem, number> = new Map<MeasurementSystem, number>([
			[MeasurementSystem.Metrics, 0.025],
			[MeasurementSystem.Imperial, UnitConverter.getSaveConvertValue(0.25)]
		]);
		this.minStep = elementMoveSteps.get(this.sessionService.defaults.measurement_system);
		this.minShiftStep = elementShiftMoveSteps.get(this.sessionService.defaults.measurement_system);
		this.minBlockSize = UnitConverter.convertAndRoundToSaveValue(0.05);
		
		this.renderBehavior
			.pipe(debounceTime(RENDER_DEBOUNCE_TIME))
			.subscribe((data) => {
				if (!data) return;

				const planogram: IPlanogram | undefined = data.planogram
					?? this.findPlanogramByStand(data.stand)
					?? this.resolveActivePlanogram()
					?? this.planograms()[0];
				if (!planogram) return;

				planogram.stand = data.stand;
				this.activity.planogramUpdate$.next(planogram);

				if (this.activeRule?.hash != data.rule.hash || !this.layoutRules) {
					this.activeRule = data.rule;
					this.historyGenerate(planogram.main_stand);
				} else if (this.activity.highlightActive()) {
					this.activity.highlightUpdate$.next();
				}

				this.activity.requestCanvasUpdate().subscribe({
					complete: () => {
						if (this.activity.activeBlocks().length) {
							const block = planogram.stand.blocks
								.find((block: Block) => block.localId == this.activity.activeBlocks()[0].localId);
							this.activity.setBlockActive(block);
						}

						if (this.activity.selectedBlock()) {
							const block = planogram.stand.blocks
								.find((block: Block) => block.localId == this.activity.selectedBlock().localId);
							this.activity.setSelectedBlock(block);
						}

						if (this.activity.activeProducts().length) this.activity.setProductActive();
					}
				});
			});

		this.tinyKeysSubscription = tinykeys(window, {
			'$mod+KeyZ': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();

				return this.historyBack();
			},
			'$mod+Shift+KeyZ': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();

				return this.historyForward();
			},
			'$mod+KeyS': (event: KeyboardEvent) => {
				event.preventDefault();

				if (!this.modes.equipmentEditMode()) {
                    if (!this.isSaveDisabled()) this.performSaveAction().subscribe();
				} else {
					this.saveNewEquipment().finally();
				}
			},
			'$mod+KeyA': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();

				this.selectAllStandProducts();
			},
			'$mod+KeyC': (event: KeyboardEvent) => {
				if (!this.isPlanogramClipboardShortcut(event)) return;
				if (this.hasTextSelection()) return;
				if (!this.activity.activeProducts().length && !this.activity.activePosMaterials().length) return;

				event.preventDefault();
				this.productClipboard = [...this.activity.activeProducts()];
				this.POSMaterialsClipboard = [...this.activity.activePosMaterials()];
			},
			'$mod+KeyV': (event: KeyboardEvent) => {
				if (!this.isPlanogramClipboardShortcut(event)) return;
				if (!this.productClipboard.length && !this.POSMaterialsClipboard.length) return;

				event.preventDefault();
				this.pasteProducts();
				this.pastePOSMaterials();
			},
			'ArrowLeft': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyLeftEvent(this.minStep);
				this.moveProducts(-this.minStep, 0);
				this.movePOSMaterials(-this.minStep, 0);
			},
			'Shift+ArrowLeft': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(-this.minShiftStep, 0);

				const focusedElement = this.getFocusedPlanogram()?.stand.getElementById(this.activity.focusedCanvas().element);
				if (focusedElement?.is(HookElement)) {
					this.updateDetailedProductGroup('placementDepth', true);
				} else {
					this.updateProductGroup('FacesQty');
				}

				this.keyLeftEvent(this.minShiftStep);
			},
			'ArrowRight': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyRightEvent(this.minStep);
				this.moveProducts(this.minStep, 0);
				this.movePOSMaterials(this.minStep, 0);
			},
			'Shift+ArrowRight': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(this.minShiftStep, 0);

				const focusedElement = this.getFocusedPlanogram()?.stand.getElementById(this.activity.focusedCanvas().element);
				if (focusedElement?.is(HookElement)) {
					this.updateDetailedProductGroup('placementDepth');
				} else {
					this.updateProductGroup('FacesQty', true);
				}

				this.keyRightEvent(this.minShiftStep);
			},
			'ArrowUp': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyUpEvent(this.minStep);
				this.moveProducts(0, -this.minStep);
				this.movePOSMaterials(0, -this.minStep);
			},
			'Shift+ArrowUp': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(0, -this.minShiftStep);

				const focusedElement = this.getFocusedPlanogram()?.stand.getElementById(this.activity.focusedCanvas().element);
				if (focusedElement?.is(ShelfElement, PalletElement)) {
					this.updateDetailedProductGroup('placementDepth', true);
				} else if (!this.activity.focusedCanvas().element) {
					this.updateProductGroup('placementHeight', true);
				}

				this.keyUpEvent(this.minShiftStep);
			},
			'ArrowDown': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyDownEvent(this.minStep);
				this.moveProducts(0, this.minStep);
				this.movePOSMaterials(0, this.minStep);
			},
			'Shift+ArrowDown': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(0, this.minShiftStep);

				const focusedElement = this.getFocusedPlanogram()?.stand.getElementById(this.activity.focusedCanvas().element);
				if (focusedElement?.is(ShelfElement, PalletElement)) {
					this.updateDetailedProductGroup('placementDepth');
				} else if (!this.activity.focusedCanvas().element) {
					this.updateProductGroup('placementHeight');
				}

				this.keyDownEvent(this.minShiftStep);
			},
			'$mod+Alt+KeyL': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment('left');
			},
			'$mod+Alt+KeyR': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment('right');
			},
			'$mod+Alt+KeyW': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment(null);
			},
			'$mod+Alt+KeyJ': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment('stretch');
			},
			'Escape': (event: KeyboardEvent) => {
				event.preventDefault();

				if (this.modes.drawBlockMode()) {
					return this.changeDrawBlockMode();
				}

				if (this.activity.selectedBlock()) {
					if (this.activity.activeProducts().length) this.activity.setProductActive();
					this.activity.clearSelectedBlock();
                    return;
				}

				if (this.activity.targetElement()) this.activity.clearTargetElement();

				if (this.activity.selectedElement()) this.activity.clearSelectedElement();

				if (this.activity.activeBlocks().length) {
					this.activity.closePanel();
					this.activity.setBlockActive();
					return;
				}

				if (this.activity.activePosMaterials().length) {
					this.activity.closePanel();
					this.activity.setPOSMaterialActive();
					return;
				}

				if (this.activity.panelOpened()) {
					return;
				}

				this.activity.clearActivePanelProducts();
				this.activity.setProductActive();
				return;
			},
			'Delete': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				const hadPosMaterials: boolean = this.activity.activePosMaterials().length > 0;
				return this.activity.deleteItems().then(() => {
					if (hadPosMaterials) this.POSMaterialsClipboard = [];
				});
			},
			'Shift+Delete': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				return this.deleteBlockWithoutProducts();
			},
			'Alt+Delete': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				void this.deleteProduct(Utils.unique(this.activity.activeProducts().map((product) => product.product)));
			}
		});

		effect(() => {
			this.activity.activeBlocks();

			if (!this.planograms().length) return;

			this.setBrandsInfoVisibility();
			this.setCategoriesInfoVisibility();
		});



		effect(() => {
			const productMove: boolean = this.activity.productMove();
			const equipmentEditMode: boolean = this.modes.equipmentEditMode();

			if (!this.planograms().length) return;
			untracked(() => {
				if (!productMove && !equipmentEditMode && this.pausedInterval() && !this.saveInProgress()) {
					this.performSaveAction(true).subscribe({
						complete: () => this.pausedInterval.set(false)
					});
				}
			});
		});

		effect(() => {
			this.activity.productsGrouping();
			this.selectedProductFilter();
			this.searchQuery();

			untracked(() => {
				if (this.productsTableData) this.buildProductsTable(true);
			});
		});

		effect(() => {
			this.activity.activeBlocks();
			this.activity.selectedBlock();
			for (const planogram of this.planograms()) {
				planogram.products();
			}

			untracked(() => {
				if (this.productsTableData) this.buildProductsTable(false);
				if (this.analysisOpen && this.productsAnalytic && Object.keys(this.productsAnalytic).length) {
					this.productsAnalytic = Object.assign({}, this.productsAnalytic);
				}
			});
		});

        effect(() => {
            const opened = this.planogramCanvasOverlayService.opened().map(item => item.planogram.main_stand);
            untracked(() => {
                if (!this.activity.planogram()) return;
                if (!opened.includes(this.activity.planogram().main_stand)) {
                    if (this.planogramCanvasOverlayService.hasOpened()) {
                        this.activity.setPlanogram(this.planogramCanvasOverlayService.opened()[0].planogram);
                    } else {
                        this.activity.setPlanogram(null);
                    }
                }
            });
        });

        effect(() => {
            const planogram: IPlanogram | null = this.activity.planogram();

            untracked(() => {
                if (planogram) return;

                const panelType: string = this.activity.panelType();

                if (panelType === 'autolayout' || panelType === 'layoutrules') {
                    this.activity.closePanel();
                }

                if (this.layoutRules) {
                    this.exitLayoutRulesMode();
                    this.layoutRules = false;
                    this.activity.clearLayoutRulesProducts();
                }
            });
        });

		effect(() => {
			this.activity.labelsState().abcTurnover;
			this.activity.labelsState().abcReceipts;

			untracked(() => {
				if (!this.planograms().length) return;

				this.applyAbcLabelsToProducts();
			});
		});

		effect(() => {
			if (this.planograms().length <= 1) return;

			const planogram: IPlanogram | undefined = this.analyticsPlanogram();
			if (!planogram) return;

			untracked(() => {
				this.syncBrandStatisticsFromPlanogram(planogram);
				this.setRealProductPercents();
			});
		});

		effect(() => {
			const state = this.planogramResolver.state$();
			if (state.loading) return;

			untracked(() => {
				this.initPlanogram(state.planograms);
			});
		});

		effect(() => {
			const planogram: IPlanogram = this.activity.planogram();

			untracked(() => {
				void this.onActivePlanogramConversationChange(planogram);
			});
		});

		effect(() => {
			const planograms: Array<IPlanogram> = this.planograms();
			if (!planograms.length) return;
			planograms.forEach((planogram: IPlanogram) => planogram.products());
			untracked(() => void this.setProductsPercents());
		});

		effect(() => {
			const hasChanges: boolean = this.activity.hasAnyHistoryChanges();
			const equipmentEditMode: boolean = this.modes.equipmentEditMode();

			untracked(() => {
				if (hasChanges && this.ignoreOtherSave() && !equipmentEditMode && !this.saveTimeout) {
					this.setAutoSaveTimeout();
				}

				if ((!hasChanges || !this.ignoreOtherSave() || equipmentEditMode) && this.saveTimeout) {
					clearTimeout(this.saveTimeout);
					this.saveTimeout = null;
				}

				this.saveAble.set(hasChanges);
			});
		});

		this.activity.buildProductsTable$
			.pipe(takeUntilDestroyed())
			.subscribe((reset: boolean) => this.buildProductsTable(reset));

		this.activity.canvasDrop$
			.pipe(takeUntilDestroyed())
			.subscribe(({ event, planogram }: CanvasDropRequest) => this.onCanvasDrop(event, planogram));

		this.activity.planogramUpdate$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.refreshPlanogramsState());

		this.activity.historyGenerate$
			.pipe(takeUntilDestroyed())
			.subscribe((planogramId?: number) => this.historyGenerate(planogramId));

		this.activity.setRealProductPercents$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.setRealProductPercents());

		this.activity.rebuildProductsTable$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.rebuildProductsTable());

		this.activity.reloadABCReport$
			.pipe(takeUntilDestroyed())
			.subscribe(() => void this.getAbcReport());

		effect(() => {
			const planogram: IPlanogram = this.analyticsPlanogram();
			if (!this.analysisOpen || !planogram) return;

			untracked(() => void this.reloadEfficiencyAnalysisData());
		});

		this.activity.reloadEfficiencyAnalysis$
			.pipe(takeUntilDestroyed())
			.subscribe(() => {
				if (this.analysisOpen) void this.reloadEfficiencyAnalysisData();
			});

		this.activity.showDeleteMessage$
			.pipe(takeUntilDestroyed())
			.subscribe((values: { block: Block, errors: Array<ProductGroup> }) => {
				this.snackBarService.setSnackBarNotification({
					messageText: 'DELETE_BLOCK_PRODUCTS_APPROVE',
					translateParams: { block: values.block.name },
					durationSeconds: 60 * 60,
					closeButton: false,
					closeActions: [
						{
							action: 'OK',
							callback: () => {
								const planogram: IPlanogram | undefined = this.findPlanogramByStand(values.block.stand);
								if (!planogram) return;

								const productsToDelete: Array<ProductGroup> = values.errors;
								planogram.stand.products = planogram.stand.products.filter(
									(pg: ProductGroup) => !productsToDelete.includes(pg)
								);
								productsToDelete.forEach((p: ProductGroup) => {
									const dependedProduct = planogram.stand.products.find(
										(pg) => pg.dependProduct == p
									);
									if (dependedProduct) {
										dependedProduct.dependProduct = p.element.getDroppedProduct(
											p.begin.x,
											p.begin.y,
											p.begin.z,
											this.activity.activeProducts(),
											p
										);
										dependedProduct.begin.y = dependedProduct.dependProduct
											? dependedProduct.dependProduct.getBoundingY().max
											: dependedProduct.element.getBoundingY().max;
									}
								});
								const elements: Array<Element> = this.activity.normalizeElements(
									planogram.stand,
									Utils.unique(productsToDelete.map((pg) => pg.element))
								);
								this.activity.requestCanvasUpdate(this.planograms(), elements.map((el: Element) => el.id));

								this.activity.popLastHistoryEntry(planogram.main_stand);
								this.historyGenerate(planogram.main_stand);
							}
						},
						{
							action: 'CHANGE_BLOCK_SETTINGS',
							callback: () => {
								this.activity.setBlockActive(values.block);
								this.openSettingsPanel();
							}
						}
					]
				});
			});

	}

	getLayoutAccessibility(): boolean {
		if (!this.planograms().length) return false;

		return this.planograms().some((planogram: IPlanogram) =>
			planogram.stand.allElements
				.filter((element: Element) => element.parent.is(EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze))
				.some((element: Element) => element.allowedAutoLayout)
		);
	}

	moveProducts(horizontal: number, vertical: number): void {
		if (this.modes.equipmentEditMode()) return;

		const planogram: IPlanogram | undefined = this.resolveActivePlanogram();
		if (!planogram) return;

		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		this.activity.moveProducts(planogram, behavior, horizontal, vertical);
	}

	movePOSMaterials(horizontal: number, vertical: number): void {
		if (this.modes.equipmentEditMode()) return;

		const planogram: IPlanogram = this.activity.planogram();
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		this.activity.movePOSMaterials(planogram, behavior, horizontal, vertical);
	}

	keyLeftEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const planogram: IPlanogram | undefined = this.resolveEquipmentEditPlanogram();
		if (!planogram) return;

		const element: Element = planogram.stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (element.depend?.parent.is(EquipmentElements.CentralPillar)) return;
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;
		if (element.shapes[0].type == 'trapeze') return;

		const elementBounding: number = element.getBoundingX().min;
		const dependBounding: number = element.depend?.getBoundingX().min;
		if (Utils.round(elementBounding - step) < dependBounding) {
			step = Utils.round(elementBounding - dependBounding);
		}

		if (step) {
			this.moveEquipmentElement(element, -step, 0, 0);
			this.finalizeEquipmentMove(planogram);
		}
	}

	keyRightEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const planogram: IPlanogram | undefined = this.resolveEquipmentEditPlanogram();
		if (!planogram) return;

		const element: Element = planogram.stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (element.depend?.parent.is(EquipmentElements.CentralPillar)) return;
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;
		if (element.shapes[0].type == 'trapeze') return;

		const elementBounding: number = element.getBoundingX().max;
		const dependBounding: number = element.depend?.getBoundingX().max;
		if (Utils.round(elementBounding + step) > dependBounding) {
			step = Utils.round(dependBounding - elementBounding);
		}

		if (step) {
			this.moveEquipmentElement(element, step, 0, 0);
			this.finalizeEquipmentMove(planogram);
		}
	}

	keyUpEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const planogram: IPlanogram | undefined = this.resolveEquipmentEditPlanogram();
		if (!planogram) return;

		const element: Element = planogram.stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;

		if (this.activity.focusedCanvas().element) {
			step = this.keyUpTopEvent(element, step);
		} else {
			step = this.keyUpFrontEvent(element, step);
		}

		if (step) {
			this.finalizeEquipmentMove(planogram);
		}
	}

	keyDownEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const planogram: IPlanogram | undefined = this.resolveEquipmentEditPlanogram();
		if (!planogram) return;

		const element: Element = planogram.stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;

		if (this.activity.focusedCanvas().element) {
			step = this.keyDownTopEvent(element, step);
		} else {
			step = this.keyDownFrontEvent(element, step);
		}

		if (step) {
			this.finalizeEquipmentMove(planogram);
		}
	}

	private keyUpFrontEvent(element: Element, step: number): number {
		const elementBounding: number = element.getBoundingY().max;
		const dependBounding: number = element.depend?.getBoundingY().max;
		if (Utils.round(elementBounding + step) > dependBounding) {
			step = Utils.round(dependBounding - elementBounding);
		}

		if (element.parent.is(EquipmentElements.Shelf, EquipmentElements.AngleShelf, EquipmentElements.HangBar)) {
			if (element.depend.isExtensionActive(ExtensionType.Rails)) {
				const rails: Extension<RailsParams> = element.depend.getExtension<RailsParams>(ExtensionType.Rails);
				const points: Array<number> = elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element.depend);

				step = Utils.round(rails.params.railHolesHeight + rails.params.distanceBetweenRailHoles);

				if (points.every((point: number) => point != Utils.round(element.getBoundingY().min + step))) {
					step = 0;
				}
			}
		}

		this.moveEquipmentElement(element, 0, step, 0);
		return step;
	}

	private keyUpTopEvent(element: Element, step: number): number {
		const elementBounding: number = element.getAbsoluteBoundingZ().min;
		const dependBounding: number = element.depend?.getAbsoluteBoundingZ().min;
		if (Utils.round(elementBounding - step) < dependBounding) {
			step = Utils.round(elementBounding - dependBounding);
		}

		this.moveEquipmentElement(element, 0, 0, -step);
		return step;
	}

	private keyDownFrontEvent(element: Element, step: number): number {
		const elementBounding: number = element.getBoundingY().min;
		const dependBounding: number = element.depend?.getBoundingY().min;
		if (Utils.round(elementBounding - step) < dependBounding) {
			step = Utils.round(elementBounding - dependBounding);
		}

		if (element.parent.is(EquipmentElements.Shelf, EquipmentElements.AngleShelf, EquipmentElements.HangBar)) {
			if (element.depend.isExtensionActive(ExtensionType.Rails)) {
				const rails: Extension<RailsParams> = element.depend.getExtension<RailsParams>(ExtensionType.Rails);
				const points: Array<number> = elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element.depend);

				step = Utils.round(rails.params.railHolesHeight + rails.params.distanceBetweenRailHoles);

				if (points.every((point: number) => point != Utils.round(element.getBoundingY().min - step))) {
					step = 0;
				}
			}
		}

		this.moveEquipmentElement(element, 0, -step, 0);
		return step;
	}

	private keyDownTopEvent(element: Element, step: number): number {
		const elementBounding: number = element.getAbsoluteBoundingZ().max;
		const dependBounding: number = element.depend?.getAbsoluteBoundingZ().max;
		if (Utils.round(elementBounding + step) > dependBounding) {
			step = Utils.round(dependBounding - elementBounding);
		}

		this.moveEquipmentElement(element, 0, 0, step);
		return step;
	}

	private moveEquipmentElement(
		element: Element,
		xStep: number,
		yStep: number,
		zStep: number,
		planogram: IPlanogram = this.resolveEquipmentEditPlanogram()
	): void {
		const relatedElements: Array<Element> = element.relatedElements;
		const relatedProducts: Array<ProductGroup> = [
			...element.products,
			...relatedElements.map((el: Element) => el.products).flat()
		].flat();

		element.shapes[0].drag(xStep, yStep, zStep);

		if (element.parent.is(EquipmentElements.AngleShelf) && planogram) {
			(element.shapes[0] as Rectangle).updateRotationPoint(planogram.stand, element);
		}

		relatedElements.forEach((el: Element) => el.shapes[0].drag(xStep, yStep, zStep));
		relatedProducts.forEach((pg: ProductGroup) => pg.move(xStep, yStep, zStep));
	}

	private finalizeEquipmentMove(planogram: IPlanogram): void {
		this.activity.finalizeSharedEquipmentMove(planogram);
	}

	private refreshPlanogramsState(): void {
		this.planograms.set([...this.planograms()]);
	}

	deleteBlockWithoutProducts() {
		if (!this.activity.activeBlocks().length) return;

		const planogram: IPlanogram | undefined = this.findPlanogramByStand(this.activity.activeBlocks()[0].stand);
		if (!planogram) return;

		planogram.stand.blocks = planogram.stand.blocks.filter((b) => !this.activity.isBlockActive(b));
		planogram.stand.products
			.filter((pg) => this.activity.activeBlocks()[0].products.includes(pg))
			.forEach((pg) => (pg.block = null));

		this.activity.normalizeElements(planogram.stand, this.activity.activeBlocks()[0].elements);

		this.activity.setBlockActive();
		this.activity.closePanel();
		this.refreshPlanogramsState();

		// Block Konva nodes have no element id — partial update cannot remove them.
		this.activity.requestCanvasUpdate(this.planograms(), []).subscribe(() => {
			this.activity.setProductActive();
		});

		this.historyGenerate(planogram.main_stand);
	}

	updateDetailedProductGroup(key: string, increase: boolean = false) {
		if (!this.activity.activeProducts().length) return;

		const productsByPlanogram: Map<IPlanogram, Array<ProductGroup>> = new Map();

		this.activity.activeProducts().forEach((pg: ProductGroup) => {
			const planogram: IPlanogram | undefined = this.planograms().find((item: IPlanogram) => item.stand === pg.element.stand);
			if (!planogram) return;

			const products: Array<ProductGroup> = productsByPlanogram.get(planogram) ?? [];
			products.push(pg);
			productsByPlanogram.set(planogram, products);
		});

		const normalizedElementIds: Array<string> = [];

		productsByPlanogram.forEach((products: Array<ProductGroup>, planogram: IPlanogram) => {
			const stand = planogram.stand;

			products
				.filter((pg: ProductGroup) => !(pg.element.is(HangBarElement) && ['placementHeight', 'placementDepth'].includes(key)))
				.filter((pg: ProductGroup) => !(pg.element.is(HookElement) && ['FacesQty', 'placementHeight'].includes(key)))
				.filter((pg: ProductGroup) => !pg.element.is(HoleElement) && !pg.isLooseForm)
				.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x)
				.forEach((pg: ProductGroup) => {
					const { FacesQty, qty } = pg,
						_qty = FacesQty;

					pg[key] = increase ? pg[key] + 1 : pg[key] - 1 > 0 ? pg[key] - 1 : 1;
					pg.qty = increase ? qty + _qty : pg[key] - 1 > 0 ? qty - _qty : _qty;

					const block = stand.findBlock(pg.begin.x, pg.begin.y, pg.Width);
					if (block && (pg.block != block.id || !block.id)) {
						if (!block.isProductSuitable(pg.product)) {
							this.activity.showDeleteMessage$.next({ block: block, errors: [pg] });
						}
					}
				});

			planogram.layerObjects.boxGroup.destroyChildren();

			if (key == 'placementHeight') {
				let dependedProducts: Array<ProductGroup> = [];

				products.forEach((el: ProductGroup) => {
					dependedProducts = [
						...dependedProducts,
						...Utils.findDependProducts(el, stand.products)
					];
				});

				stand.products.forEach((p: ProductGroup) => {
					if (dependedProducts.includes(p)) {
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});
			}

			const elements: Array<Element> = this.activity.normalizeElements(
				stand,
				Utils.unique(products.map((pg: ProductGroup) => pg.element))
			);
			normalizedElementIds.push(...elements.map((el: Element) => el.id));
		});

		this.planograms.set([...this.planograms()]);
		this.activity.refreshActiveProducts();
		this.activity.requestCanvasUpdate([...productsByPlanogram.keys()], Utils.unique(normalizedElementIds));
		[...productsByPlanogram.keys()].forEach((planogram: IPlanogram) => this.historyGenerate(planogram.main_stand));
	}

	updateProductGroup(key: string, increase: boolean = false) {
		if (!this.activity.activeProducts().length) return;

		const productsByPlanogram: Map<IPlanogram, Array<ProductGroup>> = new Map();

		this.activity.activeProducts().forEach((pg: ProductGroup) => {
			const planogram: IPlanogram | undefined = this.planograms().find((item: IPlanogram) => item.stand === pg.element.stand);
			if (!planogram) return;

			const products: Array<ProductGroup> = productsByPlanogram.get(planogram) ?? [];
			products.push(pg);
			productsByPlanogram.set(planogram, products);
		});

		const normalizedElementIds: Array<string> = [];

		productsByPlanogram.forEach((products: Array<ProductGroup>, planogram: IPlanogram) => {
			const stand = planogram.stand;

			products
				.filter((pg: ProductGroup) => !(pg.element.is(HangBarElement) && ['placementHeight', 'placementDepth'].includes(key)))
				.filter((pg: ProductGroup) => !(pg.element.is(HookElement) && ['FacesQty', 'placementHeight'].includes(key)))
				.filter((pg: ProductGroup) => !pg.element.is(HoleElement) && !pg.isLooseForm)
				.sort((a, b) => a.begin.x - b.begin.x)
				.forEach((pg) => {
					if (!increase && pg[key] == 1) return;
					const group = pg.element.products.find((g) => g.some((p) => p == pg)),
						prevWidth = Element.calculateGroupWidth(group);
					pg[key] = increase ? pg[key] + 1 : pg[key] - 1;
					if (increase) {
						const diff =
								prevWidth < pg.Width ? Utils.round(pg.Width - prevWidth) : 0,
							prods = pg.element.products.filter((group) =>
								group.some((product) => pg == product)
							);
						if (diff) pg.element.shiftProductGroups(prods, Utils.to1DArray(prods));
					}

					const block = stand.findBlock(pg.begin.x, pg.begin.y, pg.Width);
					if (block && (pg.block != block.id || !block.id)) {
						if (!block.isProductSuitable(pg.product)) {
							this.activity.showDeleteMessage$.next({ block: block, errors: [pg] });
						}
					}
				});

			planogram.layerObjects.boxGroup.destroyChildren();

			if (key == 'placementHeight') {
				let dependedProducts: Array<ProductGroup> = [];

				products.forEach((el: ProductGroup) => {
					dependedProducts = [
						...dependedProducts,
						...Utils.findDependProducts(el, stand.products)
					];
				});

				stand.products.forEach((p: ProductGroup) => {
					if (dependedProducts.includes(p)) {
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});
			}

			const elements: Array<Element> = this.activity.normalizeElements(
				stand,
				Utils.unique(products.map((pg: ProductGroup) => pg.element))
			);
			normalizedElementIds.push(...elements.map((el: Element) => el.id));
		});

		this.planograms.set([...this.planograms()]);
		this.activity.refreshActiveProducts();
		this.activity.requestCanvasUpdate([...productsByPlanogram.keys()], Utils.unique(normalizedElementIds));
		[...productsByPlanogram.keys()].forEach((planogram: IPlanogram) => this.historyGenerate(planogram.main_stand));
	}

	async addProducts(products: Product[], generate_history: boolean = true) {
		const targetPlanogram: IPlanogram | null = this.productAddTargetPlanogram();
		const planograms: Array<IPlanogram> = targetPlanogram ? [targetPlanogram] : this.planograms();
		if (!planograms.length || !products.length) return;

		const updatedPlanograms: Array<IPlanogram> = [];

		planograms.forEach((planogram: IPlanogram) => {
			const newProducts: Array<Product> = products
				.filter((product: Product) => !planogram.products().find((p: Product) => p.id == product.id))
				.map((product: Product) => product.copy());

			if (!newProducts.length) return;

			updatedPlanograms.push(planogram);
			planogram.products.set(newProducts.concat(planogram.products()));
		});

		if (!updatedPlanograms.length) return;

		this.setRealProductPercents();

		if (generate_history) {
			updatedPlanograms.forEach((planogram: IPlanogram) => this.historyGenerate(planogram.main_stand));
		}

		if (this.activity.highlightActive()) {
			this.highlightProductsService.setColors(this.products());
			const applyFunc = this.highlightProductsService.applyHighlight(
				this.products(),
				this.planograms().map((planogram: IPlanogram) => planogram.main_stand),
				this.planConf?.id
			);
			this.activity.setHighlightedProducts(await applyFunc[this.activity.highlightValue()]());
			await firstValueFrom(this.activity.requestCanvasUpdate());
		}
	}

	openProductCard(id: number, name: string): void {
		this.productCardService.open({ key: id, name: name });
	}

	updateProductCard(productCard: IProductCard): void {
		const updObj: any = {};
		const defaultParams: DefaultProductParams = new DefaultProductParams();

		updObj.width = productCard.width || defaultParams.width;
		updObj.height = productCard.height || defaultParams.height;
		updObj.depth = productCard.depth || defaultParams.depth;
		updObj.photo = productCard.photo || EMPTY_PRODUCT_IMAGE_PATH;
		updObj.is_loose = productCard.is_loose;
		updObj.packings = productCard.packing;
		updObj.width_compression_coef = productCard.width_compression_coef;
		updObj.height_compression_coef = productCard.height_compression_coef;
		updObj.depth_compression_coef = productCard.depth_compression_coef;
		updObj.name = productCard.name;
		updObj.article = productCard.article;
		updObj.barcode = productCard.barcode;
		updObj.description = productCard.description;

		const hasProduct: boolean = this.planograms().some((planogram: IPlanogram) =>
			planogram.products().some((product: Product) => product.id === productCard.id)
		);
		if (!hasProduct) return;

		if (productCard.set_compression_to_global_everywhere) {
			this.planograms().forEach((planogram: IPlanogram) => {
				planogram.stand.products
					.filter((pg: ProductGroup) => pg.product.id === productCard.id)
					.forEach((pg: ProductGroup) => {
						pg.height_compression_coef = null;
						pg.width_compression_coef = null;
						pg.depth_compression_coef = null;
					});
			});
		}

		this.planograms().forEach((planogram: IPlanogram) => {
			const catalogProduct: Product | undefined = planogram.products()
				.find((product: Product) => product.id === productCard.id);

			if (!catalogProduct) return;

			catalogProduct.update(updObj);
			catalogProduct.setPreviewSizes();
		});

		const productId: number = productCard.id;
		const affectedPlanograms: Array<IPlanogram> = this.planograms().filter((planogram: IPlanogram) =>
			planogram.stand.products.some((pg: ProductGroup) => pg.product.id === productId)
		);

		affectedPlanograms.forEach((planogram: IPlanogram) => {
			const elementsToNormalize: Array<Element> = [];

			planogram.stand.products.forEach((pg: ProductGroup) => {
				if (pg.product.id === productId && !elementsToNormalize.includes(pg.element)) {
					elementsToNormalize.push(pg.element);
				}

				if (pg.selectPacking && !pg.product.packings.find((packing: IProductPacking) => packing.id === pg.selectPacking)) {
					pg.selectPacking = null;
				}
			});

			elementsToNormalize.forEach((element: Element) => {
				const groupsToShift: Array<Array<ProductGroup>> = element.products.filter((group: Array<ProductGroup>) =>
					group.some((pg: ProductGroup) => productId === pg.product.id)
				);
				groupsToShift.forEach((group: Array<ProductGroup>) =>
					element.shiftProductGroups([group], Utils.to1DArray(group))
				);
			});

			const elements: Array<Element> = this.activity.normalizeElements(planogram.stand, elementsToNormalize);
			this.activity.requestCanvasUpdate([planogram], elements.map((el: Element) => el.id)).subscribe({
				complete: () => {
					if (!this.activity.activeBlocks().length) this.activity.setProductActive();
				}
			});
		});

		this.setRealProductPercents();

		if (this.productsTableData) {
			this.buildProductsTable(false);
		}
	}

	selectAllStandProducts() {
		const selected_stands = this.activity.activeProducts().map((pg) => pg.element.stand);
		this.activity.setProductActive(null, false, false);

		const groups = PlanogramEditComponent.to1D(
			this.planograms().filter((s) => selected_stands.includes(s.stand)).map((s) => s.stand.products)
		);

		groups.forEach((pg: ProductGroup) => this.activity.setProductActive(pg, true));

		this.activity.clearActivePanelProducts();
		this.activity.setActivePanelProducts(groups.map((pg: ProductGroup) => pg.product));
	}

	createNewPOSTextMaterial(): void {
        if (!this.activity.planogram()) return;
		const material: POSTextMaterial = new POSTextMaterial();
		this.activity.planogram().stand.POSMaterials.push(material);
        this.activity.historyGenerate$.next();
		this.activity.requestCanvasUpdate([], [], undefined, [PlanogramCanvasLayer.PosMaterials])
			.subscribe(() => this.activity.setPOSMaterialActive(material));
        this.mixPanelService.trackPage(MixpanelEvents.POSTextAdd);
	}

    createPOSImageMaterial(data: POSMaterialImageSaveObject): void {
		if (!this.activity.planogram()) return;
		const material: POSImageMaterial = new POSImageMaterial(data);
		this.activity.planogram().stand.POSMaterials.push(material);
        this.activity.historyGenerate$.next();
		this.activity.requestCanvasUpdate([], [], undefined, [PlanogramCanvasLayer.PosMaterials])
			.subscribe(() => this.activity.setPOSMaterialActive(material));
		this.mixPanelService.trackPage(MixpanelEvents.POSAdd);
	}

	onCanvasDrop(event: DndDropEvent, targetPlanogram?: IPlanogram): void {
		switch (event.type) {
			case 'rule':
				void this.dropRule(event, targetPlanogram);
				break;
			case 'product':
				this.dropProductOnCanvas(event, targetPlanogram);
				break;
			case 'POSImageMaterial':
				this.dropPOSImageMaterial(event, targetPlanogram);
				break;
		}
	}

	dropProductOnCanvas(event: DndDropEvent, targetPlanogram?: IPlanogram): void {
		const planogram: IPlanogram = targetPlanogram ?? this.activity.planogram();
		if (!planogram) return;

		this.activity.setPlanogram(planogram);

		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		this.activity.dropProduct(planogram, behavior, event);
	}

    dropPOSImageMaterial(event: DndDropEvent, targetPlanogram?: IPlanogram): void {
		const planogram: IPlanogram = targetPlanogram ?? this.activity.planogram();
        if (!planogram) return;

		this.activity.setPlanogram(planogram);
		const data: POSMaterialImageSaveObject = event.data;
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		const point: Point = behavior.convertCanvasPointToPlanogram({ x: event.event.offsetX, y: event.event.offsetY });
		this.createPOSImageMaterial({
			...data,
			x: point.x,
			y: planogram.stand.Height - point.y
		});
	}

	changeDrawBlockMode(): void {
		if (this.sessionService.hasFeature(ServiceFeatures.PlanogramBlocks)) {
			this.modes.toggleDrawBlockMode();

			if (this.modes.drawBlockMode()) {
				this.activity.clearSelectedBlock();
				this.activity.setBlockActive();
			}
		} else {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.Blocks);
		}
	}


	static blockIntersects(active_block, all_blocks) {
		const active_block_poly = geometryFactory.createPolygon(
				active_block.coordinates.map((i) => new Coordinate(i.x, i.y))
			),
			all_blocks_poly = all_blocks.map((b) =>
				geometryFactory.createPolygon(
					b.coordinates.map((i) => new Coordinate(i.x, i.y))
				)
			);

		return all_blocks_poly
			.filter(
				(i) => active_block_poly.intersects(i) && !active_block_poly.touches(i)
			)
			.map((i) => active_block_poly.intersection(i));
	}

	canDrawBlock(copy, all_blocks: Block[] = []) {
		const intersections = PlanogramEditComponent.blockIntersects(
			copy,
			all_blocks
		);

		return intersections.filter((poly) => !poly.isEmpty());
	}

	canMoveBlock(b, copy) {
		if (!b.elements.length) return true;
		return Utils.equalLists(
			copy.elements.map((el) => el.constructor.name),
			b.elements.map((el) => el.constructor.name)
		);
	}

	canvasGetPoint(x: number, y: number, ignoreWall?: Wall): Point {
		// // вираховуємо відстань 0.15 метра на яку дотягувати точку.
		// const n = Math.floor(0.15 * this.scale), cellWidth = this.scale / 2.0;
		// //const n = 10, cellWidth = this.scale / 2.0;
		//
		// // прораховуємо відстані до найближчих ліній сітки з усіх сторін
		// const left = Math.abs(cellWidth * Math.floor(x / cellWidth) - x),
		//     top = Math.abs(cellWidth * Math.floor(y / cellWidth) - y),
		//     right = Math.abs(cellWidth * Math.floor(x / cellWidth + 1) - x),
		//     bottom = Math.abs(cellWidth * Math.floor(y / cellWidth + 1) - y);
		//
		// // порівнюєм з `n` та дотягаємо точку до сітки якщо потрібно
		// if (top <= n && left <= n) {
		//     x -= left;
		//     y -= top;
		// }
		// if (top <= n && right <= n) {
		//     x += right;
		//     y -= top;
		// }
		// if (bottom <= n && left <= n) {
		//     x -= left;
		//     y += bottom;
		// }
		// if (bottom <= n && right <= n) {
		//     x += right;
		//     y += bottom;
		// }
		// ділимо на масштаб щоб зберегти координату в метрах
		x = Utils.round(x / this.planograms()[0].scale);
		y = Utils.round(y / this.planograms()[0].scale);

		return { x, y };
	}

	private isPlanogramClipboardShortcut(event: KeyboardEvent): boolean {
		if (['INPUT', 'TEXTAREA'].includes((event.target as HTMLElement)?.nodeName)) return false;
		return this.activity.focusedCanvas().canvas != null;
	}

	private hasTextSelection(): boolean {
		const selection = window.getSelection();
		return selection != null && !selection.isCollapsed && selection.toString().trim().length > 0;
	}

	private getFocusedPlanogram(): IPlanogram | undefined {
		const focusedPlanogramId = this.activity.focusedCanvas().planogram;
		if (focusedPlanogramId == null) return;

		return this.planograms().find((planogram: IPlanogram) => planogram.main_stand === focusedPlanogramId);
	}

	private resolveActivePlanogram(): IPlanogram | undefined {
		return this.getFocusedPlanogram() ?? this.activity.planogram();
	}

	private getBrandStatisticsPlanograms(): Array<IPlanogram> {
		if (this.planograms().length <= 1) {
			return this.planograms();
		}

		const planogram: IPlanogram | undefined = this.analyticsPlanogram();

		return planogram ? [planogram] : [];
	}

	private syncBrandStatisticsFromPlanogram(planogram: IPlanogram): void {
		this.brandsRecommendedPercent = this.replaceEmptyKeyWithMinusOne(planogram.conf?.brandsRecommendedPercent || {});
		this.categoriesRecommendedPercent = this.replaceEmptyKeyWithMinusOne(planogram.conf?.categoriesRecommendedPercent || {});
		this.brandsPanelDataGrouped = planogram.conf?.brandsPanelDataGrouped ?? false;

		if (planogram.conf?.reportParams) {
			this.reportParams = planogram.conf.reportParams;
		}
	}

	private findPlanogramByStand(stand: EquipmentBase): IPlanogram | undefined {
		return this.planograms().find((planogram: IPlanogram) => planogram.stand === stand);
	}

	private resolveEquipmentEditPlanogram(): IPlanogram | undefined {
		const selectedElementId = this.activity.selectedElement();
		if (!selectedElementId) return;

		const focusedPlanogram: IPlanogram | undefined = this.getFocusedPlanogram();
		if (focusedPlanogram?.stand.getElementById(selectedElementId)) return focusedPlanogram;

		const activePlanogram: IPlanogram | undefined = this.activity.planogram();
		if (activePlanogram?.stand.getElementById(selectedElementId)) return activePlanogram;

		return this.planograms().find((planogram: IPlanogram) =>
			!!planogram.stand.getElementById(selectedElementId)
		);
	}

	pasteProducts() {
		if (!this.productClipboard?.length) return;

		const planogram: IPlanogram | undefined = this.getFocusedPlanogram();
		if (!planogram) return;

		const stand = planogram.stand;
		let element: Element = this.activity.targetElement();
		if (!element || element.stand !== stand) {
			const sourceElement: Element = this.productClipboard[0]?.element;
			element = sourceElement ? stand.getElementById(sourceElement.id) : null;
		}
		if (!element) return;

		const siblings = element.getSiblingElements,
			elementSegments = element.getSiblingSegments(),
			groupsOnShelf = element.products.sort(
				(a, b) => b[0].begin.x - a[0].begin.x
			),
			normalizeList = [],
			pgAccordance = [];

		if (!elementSegments.length)
			elementSegments.push({
				min: element.getBoundingX().min,
				max: element.getBoundingX().max
			});

		if (
			this.productClipboard.some((pg) => pg.isLooseForm) &&
			element.constructor.name != 'BoxElement'
		) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'WEIGHT_PRODUCT_ON_SHELF_ERROR'
			});
		}

		let elementMin = Utils.max([
				Utils.min(elementSegments.map((seg) => seg.min)),
				element.shapes[0].begin.x
			]),
			beginX = groupsOnShelf.length
				? Element.calculateGroupMax(groupsOnShelf[0])
				: elementMin,
			activeProducts = this.productClipboard.sort(
				(a, b) =>
					(['Hook', 'Hole'].includes(a.element.parent.type) &&
					['Hook', 'Hole'].includes(b.element.parent.type)
						? 0
						: a.begin.x - b.begin.x) ||
					(['Hook', 'Hole'].includes(element.parent.type)
						? a.begin.z - b.begin.z
						: b.begin.z - a.begin.z) ||
					(['Hook', 'Hole'].includes(a.element.parent.type) &&
					['Hook', 'Hole'].includes(b.element.parent.type)
						? 0
						: a.begin.y - b.begin.y)
			);

		if (['Perforation', 'PerforationWall'].includes(element.parent.type)) {
			const holes = this.getFreeHoles(element);
			activeProducts = activeProducts
				.slice(0, holes.length)
				.map((product, idx) => {
					const productCopy = product.copy();

					productCopy.Element = holes[idx];
					productCopy.begin.x = holes[idx].shapes[0].begin.x;
					productCopy.dependProduct = null;
					productCopy.qty = 1;
					productCopy.placementDepth = 1;
					productCopy.placementHeight = 1;

					if (!normalizeList.includes(holes[idx])) normalizeList.push(holes[idx]);
					return productCopy;
				});
		} else {
			activeProducts = activeProducts.map((product) => {
				const productCopy = product.copy();

				if (element.is(HangBarElement)) {
					productCopy.rotation = ProductGroupRotation.Side;
					productCopy.image.rotation = 0;
				}

				if (!this.productClipboard.includes(product.dependProduct)) {
					productCopy.dependProduct = null;
				} else {
					const pgIndex = pgAccordance.findIndex(
						(item) => item.prev == product.dependProduct
					);
					productCopy.dependProduct =
						pgIndex >= 0 ? pgAccordance[pgIndex].current : null;
				}

				if (productCopy.dependProduct) {
					productCopy.Element = productCopy.dependProduct.element;
					productCopy.begin.x = productCopy.dependProduct.begin.x;
					productCopy.begin.y = productCopy.dependProduct.getBoundingY().max;
				} else {
					const sameXElement = this.productClipboard.filter(
						(p) =>
							p.element == product.element &&
							p.begin.x == product.begin.x &&
							p.product.id !== product.product.id &&
							!p.dependProduct
					);

					let correctElement = element.shapes[0].isPointOn(beginX)
						? element
						: siblings.find((el) => el.shapes[0].isPointOn(beginX));
					if (!correctElement)
						correctElement = [element, ...siblings].sort(
							(a, b) => a.shapes[0].begin.x - b.shapes[0].begin.x
						)[0];
					productCopy.Element = correctElement;

					productCopy.begin.x = beginX;

					if (element.parent.type == 'Hook') {
						productCopy.begin.x = element.shapes[0].begin.x;
						productCopy.dependProduct = null;
						productCopy.qty = productCopy.placementDepth;
						productCopy.placementHeight = 1;
					}

					if (sameXElement.length && element.parent.type != 'Hook') {
						const existProduct = sameXElement
							.filter((pg) => pgAccordance.some((item) => item.prev == pg))
							.map((pg) => pgAccordance.find((item) => item.prev == pg).current)
							.sort((a, b) => a.begin.z - b.begin.z);

						if (existProduct.length) {
							productCopy.Element = existProduct[0].element;
							productCopy.begin.x = existProduct[0].begin.x;
							productCopy.begin.z = existProduct[0].getBoundingZ().min;
						}
					}
				}

				pgAccordance.push({ prev: product, current: productCopy });
				if (element.parent.type != 'Hook')
					beginX = Utils.max([beginX, productCopy.getBoundingX().max]);
				return productCopy;
			});
		}

		if (activeProducts.some((pg) => !pg)) return;

		if (activeProducts.some((pg) => !element.canDrop(pg))) return;

		// remove products if there is a weight product in the box or active product is weight
		if (
			element.constructor.name == 'BoxElement' &&
			(element as BoxElement).isNeedRemoveProducts(this.productClipboard[0])
		) {
			element.stand.products = element.stand.products.filter(
				(pg) => !Utils.to1DArray(element.products).includes(pg)
			);
		}

		// remove depend products if product target is hook
		if (element.constructor.name == 'HookElement') {
			activeProducts.forEach((pg) => (pg.dependProduct = null));
		}

		this.activity.ensureProductsInPlanogramCatalog(planogram, activeProducts);

		activeProducts.forEach((pg) => {
			element.stand.products.push(pg);
		});

		element.shiftProductGroups(
			Utils.groupByChildProperty(activeProducts, 'begin', 'x'),
			activeProducts
		);

		const elements: Array<Element> = this.activity.normalizeElements(stand, [element, ...normalizeList]);
		this.activity.setNumbersForPerforation$.next(elements);

		this.activity.requestCanvasUpdate(
			[planogram],
			elements.map((el: Element) => el.id)
		).subscribe({
			complete: () => {
				this.activity.setProductActive();
				activeProducts.forEach((pg) => this.activity.setProductActive(pg, true));
			}
		});
		this.historyGenerate(planogram.main_stand);
	}

	pastePOSMaterials(): void {
		if (!this.POSMaterialsClipboard.length) return;

		const planogram: IPlanogram | undefined = this.getFocusedPlanogram();
		if (!planogram) return;

        const newMaterials: Array<POSMaterial> = [];
		for (let material of this.POSMaterialsClipboard) {
			const newMaterial: POSMaterial = material.copy();
			newMaterial.y += 0.1;
			newMaterials.push(newMaterial);
		}
		planogram.stand.POSMaterials.push(...newMaterials);
		this.activity.requestCanvasUpdate([planogram], [], 'PASTE_POS_MATERIALS', [PlanogramCanvasLayer.PosMaterials]);
        this.activity.historyGenerate$.next(planogram.main_stand);
	}

	async checkRuleProducts(rule: any, callback, targetPlanogram?: IPlanogram) {
		const planogram: IPlanogram | undefined = targetPlanogram
			?? this.resolveActivePlanogram()
			?? this.planograms()[0];
		const planogramProducts: Array<Product> = planogram?.products() ?? [];
		const planogramId: number | undefined = planogram?.planogram_id;

		const addRuleProductsToPlanogram = async (): Promise<void> => {
			if (!planogramId) return;

			const previousTarget: IPlanogram | null = this.productAddTargetPlanogram();
			if (this.planograms().length > 1) {
				this.productAddTargetPlanogram.set(planogram);
			}

			try {
				const products = await this.productsService.getLayoutRulesProducts(planogramId, groups);
				await this.addProducts(products.map((p) => new Product(p)), false);
			} finally {
				this.productAddTargetPlanogram.set(previousTarget);
			}
		};

		const getObj = (ids, level) => {
			if (level.group.type) {
				const list = [];
				level.children.forEach((child) => {
					const obj = child.id
						? Object.assign({}, ids, { [level.group.type]: Number(child.id) })
						: ids;
					list.push(...getObj(obj, child));
				});
				return list;
			}
			return Object.keys(ids).length ? [Object.assign({}, ids)] : [];
		};

		const groups = getObj({}, rule.conf.ruleData).filter((group) => {
			if (Object.keys(group).length == 1 && group.hasOwnProperty('shelf'))
				return false;
			return !planogramProducts.some((product) => {
				let exists = [];
				if (group.brand) exists.push(product.brandsUniqueId == group.brand);
				if (group.category) exists.push(product.categoriesUniqueIds.includes(group.categories));
				if (group.supplier) exists.push(product.suppliersUniqueId == group.supplier);
				if (group.characteristic)
					exists.push(
						Utils.to1DArray(product.markers).some(
							(cha) => cha.id == group.characteristic
						)
					);
				return exists.every((state) => state);
			});
		});

		if (groups.length) {
			if (
				!this.planConf.shops.length &&
				groups.some((group) => group.hasOwnProperty('supplier'))
			) {
				const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '810px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'RULES.MODAL.SUPPLIERS_GROUPS_INACTIVE_PLAN',
						twoAnswers: false,
						confirmLabel: 'CONTINUE',
						labelTranslateParams: {
							rule_name: rule.name
						}
					}
				});
				dialogRef.afterClosed().subscribe();
			} else {
				if (!planogramProducts.length) {
					this.loader.set(true);
					await addRuleProductsToPlanogram();
					this.loader.set(false);
					callback();
				} else {
					const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
						width: '574px',
						autoFocus: false,
						backdropClass: 'back-drop',
						data: {
							label: 'RULES.MODAL.PRODUCTS',
							twoAnswers: true,
							confirmLabel: 'YES',
							cancelLabel: 'NO'
						}
					});

					dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
						if (result.answer) {
							this.loader.set(true);
							await addRuleProductsToPlanogram();
							this.loader.set(false);
						}
						callback();
					});
				}
			}
		} else {
			if (!planogramProducts.length) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'RULES.POPUP.CANT_APPLY'
				});
			} else {
				callback();
			}
		}
	}

	async editRule(rule: any) {
		const planogram: IPlanogram | undefined = this.resolveActivePlanogram() ?? this.planograms()[0];

		if (planogram?.stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'RULES.HANG_BAR_WARNING'
			});
		}

		if (planogram?.stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'RULES.ISLAND_RACK_WARNING'
			});
		}

		await this.checkRuleProducts(rule, () => this.toggleLayoutRules(rule), planogram);
	}

	async updateRuleProducts(event: any) {
		await this.checkRuleProducts(
			event.rule,
			() => this.layoutRulesService.updateRule$.next(),
			this.layoutRulesPlanogram() ?? this.activity.planogram()
		);
	}

	editEquipmentHandler(trigger: MatMenuTrigger): void {
		if (!this.modes.equipmentEditMode()) this.mixPanelService.trackPage(MixpanelEvents.EquipmentEditOnPlanogram);
		if (this.modes.equipmentEditMode() && this.getPlanogramsWithEquipmentChanges().length) {
			trigger.openMenu();
		} else {
			this.toggleEquipmentEditMode();
		}
	}

	toggleEquipmentEditMode(): void {
		const entering: boolean = !this.modes.equipmentEditMode();

		this.modes.toggleEquipmentEditMode();
		this.activity.clearActivePanelProducts();
		this.activity.setProductActive();
		this.activity.clearSelectedBlock();
		this.activity.setBlockActive();
		this.activity.setPOSMaterialActive();
		this.analysisOpen = false;

		if (entering) {
			if (this.saveTimeout) {
				clearTimeout(this.saveTimeout);
				this.saveTimeout = null;
			}

			const planogramsToSave: Array<IPlanogram> = this.getPlanogramsToSave();

			if (planogramsToSave.length) {
				this.performSaveAction(true).subscribe({
					complete: () => this.initEquipmentEditHistory()
				});
			} else {
				this.initEquipmentEditHistory();
			}
		} else {
			this.activity.clearSelectedElement();
		}

		this.saveAble.set(false);
		this.activity.requestCanvasUpdate();
	}

	private initEquipmentEditHistory(): void {
		this.planograms().forEach((planogram: IPlanogram) => {
			this.activity.clearHistory(planogram.main_stand);
			this.historyGenerate(planogram.main_stand);
			this.ensureEquipmentEditBeginState(planogram);
		});
	}

	private ensureEquipmentEditBeginState(planogram: IPlanogram): void {
		const historyState: IPlanogramHistoryState = this.activity.getHistoryState(planogram.main_stand);

		if (!historyState.beginState && historyState.entries[0]) {
			this.activity.setBeginState(historyState.entries[0], planogram.main_stand);
		}
	}

	private getEquipmentEditBeginState(planogram: IPlanogram): HistoryInstance | null {
		const historyState: IPlanogramHistoryState = this.activity.getHistoryState(planogram.main_stand);

		return historyState.beginState ?? historyState.entries[0] ?? null;
	}

	private finalizeEquipmentEditHistory(): void {
		this.planograms().forEach((planogram: IPlanogram) => {
			this.activity.setBeginStateFromIndex(planogram.main_stand);
			this.activity.clearHistory(planogram.main_stand);
			this.historyGenerate(planogram.main_stand);
		});
	}

	private getPlanogramsWithEquipmentChanges(): Array<IPlanogram> {
		return this.planograms().filter((planogram: IPlanogram) =>
			this.activity.hasHistoryChanges(planogram.main_stand)
		);
	}

	private getEquipmentUsageForPlanogram(planogram: IPlanogram): Array<number> {
		return planogram.instrument?.instrument_usage?.length
			? planogram.instrument.instrument_usage
			: [planogram.id];
	}

	private getChangedPlanogramsByEquipmentId(
		planograms: Array<IPlanogram> = this.getPlanogramsWithEquipmentChanges()
	): Map<number, IPlanogram> {
		const changedByEquipmentId: Map<number, IPlanogram> = new Map();
		const focusedMainStand: number | null = this.activity.focusedCanvas().planogram;

		planograms.forEach((planogram: IPlanogram) => {
			const equipmentId: number = planogram.stand.id;
			const existing: IPlanogram | undefined = changedByEquipmentId.get(equipmentId);

			if (!existing || planogram.main_stand === focusedMainStand) {
				changedByEquipmentId.set(equipmentId, planogram);
			}
		});

		return changedByEquipmentId;
	}

	private getTotalAffectedPlanogramsCount(changedPlanograms?: Array<IPlanogram>): number {
		const affectedIds: Set<number> = new Set();
		const representatives: Iterable<IPlanogram> = changedPlanograms?.length
			? this.getChangedPlanogramsByEquipmentId(changedPlanograms).values()
			: this.getChangedPlanogramsByEquipmentId().values();

		for (const planogram of representatives) {
			this.getEquipmentUsageForPlanogram(planogram).forEach((id: number) => affectedIds.add(id));
		}

		return affectedIds.size;
	}

	private buildPlanogramStandConf(planogram: IPlanogram): StandConf {
		const products: Array<IProductConf> = this.productsService.getProductsToConf(
			planogram.id,
			planogram.stand.products
		);

		return this.planogramsService.getConf(planogram, products, {
			id: planogram.id,
			main_stand: planogram.main_stand,
			conf: {
				products: planogram.products().map((p: Product) => ({
					id: p.id,
					planedPercent: p.planedPercent
				}))
			}
		});
	}

	private async fetchExternalPlanograms(planogramIds: Array<number>): Promise<Array<IPlanogram>> {
		if (!planogramIds.length) return [];

		return lastValueFrom(
			this.planogramsService
				.bulkFetch(
					planogramIds.map((id: number) => ({ planogram_id: id, plan_id: null }))
				)
				.pipe(
					map((data: Array<IPlanogramData>): Array<IPlanogram> =>
						data.map((item: IPlanogramData) =>
							this.planogramLayoutService.getPlanogramObject(
								item,
								[],
								item.planogram_stand[0]?.planogram_id,
								false
							)
						)
					)
				)
		);
	}

	private getPlanogramById(planogramId: number): IPlanogram | undefined {
		return this.planograms().find((planogram: IPlanogram) => planogram.id === planogramId);
	}

	toggleLayoutRules(rule?: any) {
		if (
			!rule &&
			this.activeRule &&
			this.activeRule.hasOwnProperty('saved') &&
			!this.activeRule.saved
		) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					twoAnswers: true,
					label: 'RULES.MODAL.CLOSE_WARNING',
					confirmLabel: 'YES',
					cancelLabel: 'NO'
				}
			});

			dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
				if (!result.answer) return;

				this.activeRule = rule;
				this.layoutRules = !this.layoutRules;

				if (this.layoutRules) {
					this.toggleLayoutRulesPanel(true);
					this.enterLayoutRulesMode();
				} else {
					this.exitLayoutRulesMode();
					this.activity.clearLayoutRulesProducts();
				}
			});

			return;
		}

		this.activeRule = rule;
		this.layoutRules = !this.layoutRules;

		if (this.layoutRules) {
			this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleCreate);
			this.activity.closePanel();

			const planogram: IPlanogram | undefined = this.resolveActivePlanogram() ?? this.planograms()[0];

			if (planogram?.stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'RULES.HANG_BAR_WARNING'
				});
			}

			if (planogram?.stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'RULES.ISLAND_RACK_WARNING'
				});
			}

			this.enterLayoutRulesMode();
		} else {
			this.exitLayoutRulesMode();
			this.activity.clearLayoutRulesProducts();
		}
	}

	private enterLayoutRulesMode(): void {
		const planogram: IPlanogram | undefined = this.resolveActivePlanogram() ?? this.planograms()[0];

		if (!planogram) return;

		this.layoutRulesPlanogram.set(planogram);
		this.activity.setPlanogram(planogram);
		this.detailedElementWidgetOverlayService.closeAll();

		if (this.planograms().length > 1) {
			this.layoutRulesOpenedPlanogramIds = this.planogramCanvasOverlayService.opened()
				.map((item) => item.planogram.main_stand);

			const activeMainStand: number = planogram.main_stand;

			[...this.planogramCanvasOverlayService.opened()]
				.filter((item) => item.planogram.main_stand !== activeMainStand)
				.forEach((item) => this.planogramCanvasOverlayService.close(item.overlay));

			if (!this.planogramCanvasOverlayService.isOpened(planogram)) {
				this.openPlanogramOverlay(planogram);
			}

			this.planogramCanvasOverlayService.scrollToPlanogram(activeMainStand);
		}

		this.modes.setLayoutRulesLocked(true);
	}

	private exitLayoutRulesMode(): void {
		this.modes.setLayoutRulesLocked(false);
		this.layoutRulesPlanogram.set(null);

		if (this.planograms().length > 1 && this.layoutRulesOpenedPlanogramIds) {
			const idsToRestore: Array<number> = this.layoutRulesOpenedPlanogramIds;

			this.layoutRulesOpenedPlanogramIds = null;

			idsToRestore.forEach((mainStandId: number) => {
				const planogramToRestore: IPlanogram | undefined = this.planograms()
					.find((item: IPlanogram) => item.main_stand === mainStandId);

				if (planogramToRestore && !this.planogramCanvasOverlayService.isOpened(planogramToRestore)) {
					this.openPlanogramOverlay(planogramToRestore);
				}
			});
		} else {
			this.layoutRulesOpenedPlanogramIds = null;
		}
	}

	toggleLayoutRulesPanel(forceClose: boolean = false): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.LayoutRules)) {
			this.mixPanelService.trackPage(MixpanelEvents.BlockedFunctionalPage, { type: 'layoutRules' });

			let functionalType: string;
			this.translate
				.get('INFO_MODAL.USE_IN_TARIFF', { tariff: 'Enterprise' })
				.subscribe((text: string) => functionalType = text);

			this.dialog
				.open<InfoModalComponent, IInfoModalData<InfoModalComponent>>(InfoModalComponent, {
					width: '60vw',
					height: '80vh',
					autoFocus: false,
					backdropClass: 'back-drop',
					panelClass: 'full-width-modal-actions',
					data: {
						media: '/static/gif/blocked-functional/layoutRules.gif',
						mediaType: 'image',
						type: functionalType,
						title: 'DEMO.BLOCKED_FUNCTIONAL.TITLES.LAYOUT_RULES',
						description: 'DEMO.BLOCKED_FUNCTIONAL.LAYOUT_RULES',
						secondaryButtonText: 'GO_BACK',
						secondaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							ref.close();
						},
						primaryButtonText: 'COMMON.CHOOSE_TARIFF',
						primaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							this.mixPanelService.trackPage(MixpanelEvents.ChooseTariff, { page: 'Blocked functional' });
							window.open(
								this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/tariff-plans'),
								'_blank'
							);
							ref.close();
						}
					}
				});
			return;
		}

		if (!this.sessionService.hasExtension(ServiceExtensions.LayoutRules)) {
			this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleBlocked);
			this.dialog
				.open<InfoModalComponent, IInfoModalData<InfoModalComponent>>(InfoModalComponent, {
					width: '60vw',
					height: '80vh',
					autoFocus: false,
					backdropClass: 'back-drop',
					panelClass: 'full-width-modal-actions',
					data: {
						media: '/static/gif/blocked-functional/layoutRules.gif',
						mediaType: 'image',
						type: 'INFO_MODAL.ADDITIONAL_FEATURES',
						title: 'DEMO.BLOCKED_FUNCTIONAL.TITLES.LAYOUT_RULES',
						description: 'DEMO.BLOCKED_FUNCTIONAL.LAYOUT_RULES',
						secondaryButtonText: 'GO_BACK',
						secondaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							ref.close();
						},
						primaryButtonText: 'COMMON.LEAVE_REQUEST',
						primaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleRequested);
							window.$crisp.push(['do', 'chat:open']);
							ref.close();
						}
					}
				});
			return;
		}

		if (this.layoutRules && !forceClose) return;

		if (this.activity.panelType() == 'layoutrules' && this.activity.panelOpened()) {
			this.activity.closePanel();
		} else {
			this.activity.openPanel('layoutrules');
			this.layoutOpen = false;
		}
	}

	closePanel() {
		this.activity.closePanel();
	}

	deletePOSForever(id: number): void {
        for (const planogram of this.planograms()) {
            planogram.stand.POSMaterials = planogram.stand.POSMaterials.filter(
				(material: POSMaterial) => !(material instanceof POSImageMaterial) || material.id !== id
			);
			this.activity.setActivePOSMaterials(
				this.activity.activePosMaterials().filter(
					(material: POSMaterial) => !(material instanceof POSImageMaterial) || material.id !== id
				)
			);
			this.activity.getHistoryState(planogram.main_stand).entries.forEach((instance: HistoryInstance) => {
				instance.POSMaterials = instance.POSMaterials.filter(
					(material: POSMaterial) => !(material instanceof POSImageMaterial) || material.id !== id
				);
			});
			this.POSMaterialsClipboard = [];
        }

		this.activity.requestCanvasUpdate();
	}

	async deleteProduct(products: Array<Product>) {
		let elements: Array<Element> = [];
		const byPlanogram: boolean = this.activity.productsGrouping() === 'planograms';
		const productIds: Set<number> = new Set(products.map((product: Product) => product.id));

		this.activity.closePanel();
		products.forEach((product: Product) => this.activity.removeActivePanelProduct(product));

		const standsToProcess: Array<IPlanogram> = [];
		const deleteFromStand = (planogram: IPlanogram, shouldDelete: (pg: ProductGroup) => boolean): void => {
			const elementsToNormalize: Array<Element> = [];

			planogram.stand.products.forEach((pg: ProductGroup) => {
				if (shouldDelete(pg) && !elementsToNormalize.includes(pg.element)) elementsToNormalize.push(pg.element);
			});
			planogram.stand.products = planogram.stand.products.filter((pg: ProductGroup) => !shouldDelete(pg));
			planogram.stand.products.forEach((pg: ProductGroup) => {
				if (
					pg.dependProduct &&
					(this.activity.isProductActive(pg.dependProduct) || shouldDelete(pg.dependProduct))
				) {
					pg.dependProduct = pg.element.getDroppedProduct(
						pg.begin.x,
						pg.begin.y,
						pg.begin.z,
						this.activity.activeProducts(),
						pg
					);
					pg.begin.y = pg.dependProduct ? pg.dependProduct.getBoundingY().max : pg.element.getBoundingY().max;
				}
			});
			elements.push(...this.activity.normalizeElements(planogram.stand, elementsToNormalize));
			this.activity.setNumbersForPerforation$.next(elementsToNormalize);
			standsToProcess.push(planogram);
		};

		if (byPlanogram) {
			const productsByPlanogram: Map<IPlanogram, Set<Product>> = new Map();

			products.forEach((product: Product) => {
				const planogram: IPlanogram | undefined = this.planograms()
					.find((item: IPlanogram) => item.products().includes(product));

				if (!planogram) return;

				if (!productsByPlanogram.has(planogram)) productsByPlanogram.set(planogram, new Set());

				productsByPlanogram.get(planogram).add(product);
			});

			productsByPlanogram.forEach((planogramProducts: Set<Product>, planogram: IPlanogram) => {
				planogram.products.set(
					planogram.products().filter((product: Product) => !planogramProducts.has(product))
				);
				deleteFromStand(planogram, (pg: ProductGroup) => planogramProducts.has(pg.product));
			});
		} else {
			this.planograms().forEach((planogram: IPlanogram) => {
				planogram.products.set(planogram.products().filter((product: Product) => !productIds.has(product.id)));
			});
			this.planograms().forEach((planogram: IPlanogram) => {
				deleteFromStand(planogram, (pg: ProductGroup) => productIds.has(pg.product.id));
			});
		}

		this.activity.reloadEfficiencyAnalysis$.next();

		this.setRealProductPercents();
		this.setBrandsInfoVisibility();
		this.setCategoriesInfoVisibility();
		this.activity.requestCanvasUpdate(standsToProcess, elements.map((el: Element) => el.id)).subscribe({
			complete: () => this.activity.setProductActive()
		});
		standsToProcess.forEach((planogram: IPlanogram) => this.historyGenerate(planogram.main_stand));
	}

	deleteAllProduct() {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'ARE_YOU_SURE_YOU_WANT_DELETE_ALL_PRODUSCTS',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
			if (!result.answer) return;

			const productsToDelete: Array<Product> = [...(this.panelProducts || [])];

			if (!productsToDelete.length) return;

			await this.deleteProduct(productsToDelete);
		});
	}

	async dropRule(event: DndDropEvent, targetPlanogram?: IPlanogram): Promise<void> {
		this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleDrop, {
			ruleName: event.data.name
		});
		this.activeRule = event.data;

		const planogram: IPlanogram | undefined = targetPlanogram ?? this.resolveActivePlanogram();
		if (!planogram) return;

		this.activity.setPlanogram(planogram);

		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		const point: Point = behavior.convertCanvasPointToPlanogram({ x: event.event.offsetX, y: event.event.offsetY });
		const block: Block = planogram.stand.findBlock(point.x, point.y);
		this.activity.setBlockActive(block ?? null, false);
		if (this.activity.blockForRule().id && !this.activity.blockForRule().shelves.length) return;
		await this.checkRuleProducts(event.data, () => {
			const ruleLevels: RuleLevel = new RuleLevel(event.data.conf.ruleData);
			ruleLevels.recommendedPercent = 100;
			ruleLevels.plannedPercent = 100;
			ruleLevels.calculatedPercent = 100;

			this.rulesService
				.applyRule({
					planogram,
					activeBlock: this.activity.blockForRule(),
					ruleLevels: ruleLevels,
					products: planogram.products(),
					reportParams: event.data.conf.reportParams,
					selectedStores: event.data.conf.shops,
					dateOptions: event.data.conf.dateOpt,
					useSales: event.data.conf.useSales,
					expandFaces: event.data.conf.expandFaces,
					checkDistance: event.data.conf.checkDistance,
					names: {
						brand: new Map(),
						category: new Map(),
						supplier: new Map(),
						characteristic: new Map()
					}
				})
				.then((stand: EquipmentBase) => {
					if (stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
						this.snackBarService.setSnackBarNotification({
							messageText: 'RULES.HANG_BAR_WARNING'
						});
					}

					if (stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
						this.snackBarService.setSnackBarNotification({
							messageText: 'RULES.ISLAND_RACK_WARNING'
						});
					}

					this.applyLayoutRule({
						data: this.rulesService.getColorsSet(ruleLevels),
						rule: event.data,
						stand,
						planogram
					});
				});
		}, planogram);
	}

	getAbcReport(): void {
		const { abcTurnover, abcReceipts } = this.activity.labelsState();
		const planograms: Array<IPlanogram> = this.planograms();

		if (!abcTurnover && !abcReceipts) {
			this.abcDataByStand = {};
			this.applyAbcLabelsToProducts();
			this.activity.requestCanvasUpdate();
			return;
		}

		if (!planograms.length) return;

		this.abcSubscription.unsubscribe();
		this.abcSubscription = forkJoin(
			planograms.map((planogram: IPlanogram) =>
				from(
					this.planogramsService.getAbcReport({
						date_from: this.activity.labelsState().dateFrom,
						date_to: this.activity.labelsState().dateTo,
						planogram: this.planConf?.id,
						stand: planogram.main_stand,
						metrics: ['turnover', 'profit'],
						colors: this.filtersService.filters.colors,
						percents: this.filtersService.filters.percents,
						products: planogram.stand.products.map((pg: ProductGroup) => pg.product.id),
						all_shops: this.activity.labelsState().allShops
					})
				).pipe(map((result: any) => ({ mainStand: planogram.main_stand, result })))
			)
		).subscribe((results: Array<{ mainStand: number; result: any }>) => {
			this.abcDataByStand = {};

			results.forEach(({ mainStand, result }: { mainStand: number; result: any }) => {
				this.abcDataByStand[mainStand] = result;
			});

			this.applyAbcLabelsToProducts();
			this.activity.requestCanvasUpdate();
		});
	}

	getPreviewImage() {
		const photo = this.activity.previewProduct()?.photo || '';
		const filename = photo.split('/').slice(-1)[0];
		return ['img404.jpg', '0'].includes(filename) ? EMPTY_PRODUCT_IMAGE_PATH : photo;
	}

	setAlignment(type: string) {
        const planogram: IPlanogram = this.activity.planogram();
		if (!planogram) return;
		planogram.stand.alignment = type as Alignment;

		this.activity.normalizeElements(planogram.stand);
		this.activity.requestCanvasUpdate();
		this.activity.historyGenerate$.next();
	}

	// застосування даних з HistoryInstance
	async historyApply(historyObj: HistoryInstance, planogram: IPlanogram = this.activity.planogram()) {
        if (!planogram) return;

		//if (historyObj.rule && this.layoutRules) this.activeRule = historyObj.rule;

		const stand = planogram.stand;
		this.activity.clearPlanogramSelection(stand);

        planogram.stand = historyObj.stand.copy();
        planogram.products.set([...historyObj.products]);
		planogram.stand.blocks = historyObj.blocks.map((b) => b.copy());
		planogram.stand.POSMaterials = historyObj.POSMaterials.map((b) => b.copy());
		planogram.stand.blocks.forEach((b) => {
			b.stand = planogram.stand;
			b.setProducts(
				Utils.to1DArray(
					planogram.stand.blocks.filter((block) => block != b).map((block) => block.products)
				)
			);
		});
        this.activity.planogramUpdate$.next(planogram);

		planogram.stand.allElements.forEach((el) => {
			el.stand = planogram.stand;
			if (el.constructor.name == 'ShelfElement') (el as ShelfElement).setPlacementHeight(planogram.stand);
		});

		this.activity.normalizeElements(planogram.stand);
        this.activity.requestCanvasUpdate([planogram]);

		// if (!(historyObj.rule && this.layoutRules) || !Object.keys(historyObj.rule).length) {
		// 	this.activeRule = null;
		// 	this.layoutRules = false;
		// 	this.activity.clearLayoutRulesProducts();
		// }
	}

	// історія назад
	async historyBack() {
		if (this.activity.historyIndex() == 0 || this.activity.historyApplying() || this.saveInProgress() || this.showLoader()) return;

		const historyObj: HistoryInstance = this.activity.navigateHistory(-1);
		if (!historyObj) return;

		this.activity.setHistoryApplying(true);
		await this.historyApply(historyObj);
		this.activity.setHistoryApplying(false);
	}

	// історія вперед
	async historyForward() {
		if (
			this.activity.historyIndex() + 1 >= this.activity.historyEntries().length
			|| this.activity.historyApplying()
			|| this.saveInProgress()
			|| this.showLoader()
		) return;

		const historyObj: HistoryInstance = this.activity.navigateHistory(1);
		if (!historyObj) return;

		this.activity.setHistoryApplying(true);
		await this.historyApply(historyObj);
		this.activity.setHistoryApplying(false);
	}

	applyAbcLabelsToProducts(): void {
		const { abcTurnover, abcReceipts } = this.activity.labelsState();

		this.planograms().forEach((planogram: IPlanogram) => {
			const abcData: any = this.abcDataByStand[planogram.main_stand];

			planogram.stand.products.forEach((pg: ProductGroup) => {
				if (!abcTurnover && !abcReceipts) {
					delete pg.product.abc_turnover;
					delete pg.product.abc_receipts;
					return;
				}

				if (!abcData) return;

				if (abcTurnover) {
					pg.product.abc_turnover = (abcData['turnover']?.[pg.product.id] || {}).abc;
				} else {
					delete pg.product.abc_turnover;
				}

				if (abcReceipts) {
					pg.product.abc_receipts = (abcData['receipts']?.[pg.product.id] || {}).abc;
				} else {
					delete pg.product.abc_receipts;
				}
			});
		});
	}

	// створення з теперішніх даних HistoryInstance і запис його в історію.
	private historyGenerate(planogramId?: number) {
		const planogram: IPlanogram | undefined = planogramId
			? this.planograms().find((item: IPlanogram) => item.main_stand == planogramId)
			: this.activity.planogram();
		if (!planogram) return;

		this.syncProductsActiveState(planogram);
        this.activity.highlightUpdate$.next();

		if (this.productsTableData) {
			this.buildProductsTable(false);
		}

		const isActive: boolean = planogram.main_stand === this.activity.planogram()?.main_stand;
		const historyObj: HistoryInstance = {
			stand: planogram.stand.copy(),
			products: [...planogram.products()],
			blocks: planogram.stand.blocks.map((b) => b.copy()),
			POSMaterials: planogram.stand.POSMaterials.map((b) => b.copy()),
			rule: isActive ? Utils.deepCopy(this.activeRule) as LayoutRule : undefined
		};
		this.activity.pushHistory(historyObj, planogram.main_stand);
	}

	private syncProductsActiveState(planogram: IPlanogram): void {
		planogram.products().forEach((product: Product) => {
			product.active = planogram.stand.products
				.some((pg: ProductGroup) => pg.product.id === product.id && pg.availability);
		});
	}

	private isProductLaidOut(product: Product): boolean {
		const ownerPlanogram: IPlanogram | undefined = this.planograms()
			.find((planogram: IPlanogram) => planogram.products().includes(product));

		if (this.activity.productsGrouping() === 'planograms' && ownerPlanogram) {
			return ownerPlanogram.stand.products
				.some((pg: ProductGroup) => pg.product.id === product.id && pg.availability);
		}

		return this.planograms().some((planogram: IPlanogram) =>
			planogram.stand.products.some((pg: ProductGroup) => pg.product.id === product.id && pg.availability)
		);
	}

    setAutoSaveTimeout(): void {
		if (this.modes.equipmentEditMode()) return;

		const autosaveIntervalSeconds: number = this.sessionService.settings?.planogramAutosaveIntervalSeconds ?? GlobalSettings.AUTOSAVE_DEFAULT_TIME_S;
		
		if (autosaveIntervalSeconds > 0) {
			this.saveTimeout = setTimeout(async () => {
				if (this.modes.equipmentEditMode()) return;

				await lastValueFrom(this.performSaveAction(true), { defaultValue: undefined });
			}, 1000 * autosaveIntervalSeconds);
		}
	}

	changeProductsGrouping(grouping: string): void {
		if (this.productsGroupingAvailable) {
			if (this.activity.productsGrouping() === grouping) {
				this.activity.setProductsGrouping('');
			} else {
				this.activity.setProductsGrouping(grouping);
			}
		}
	}

	buildProductsTable(resetPage: boolean = true): void {
		let products: Array<Product> = [...this.products()];
        if (this.activity.productsGrouping() !== 'planograms') {
            products = products.filter((product: Product, index: number, array: Array<Product>) => array.findIndex((p: Product) => p.id === product.id) === index);
        }

		const categories: Array<any> = Utils.to1DArray(products.map((product: Product) => product.categories));

		let maxLevel: number = 0;
		for (const category of categories) {
			if (category.level > maxLevel) {
				maxLevel = category.level;
			}
		}
		this.categoriesLevels = Math.max(maxLevel - 1, 0);

		if (resetPage) this.productsTablePage = 1;

		// filtering for block
		if (this.activity.activeBlocks().length || this.activity.selectedBlock()) {
			const block: Block = this.activity.selectedBlock() || this.activity.activeBlocks()[0];
			products = products.filter((product: Product) => block.isProductSuitable(product));
		}

		// searching
		if (this.searchQuery()) {
			products = products.filter((product: Product) => {
				const categories: Array<any> = Utils.to1DArray(product.categories.map(c => c.slice(1))), // slice(1) - delete root category (ex: 'MegaTest')
					searchAlgorithm: SearchAlgorithmType = this.sessionService.personalSettings.searchAlgorithm;

				return Utils.searchItem<Product>(product, ['article', 'barcode', 'name'], this.searchQuery(), searchAlgorithm) ||
					product.brands.some(brand => Utils.searchItem(brand, ['name'], this.searchQuery(), searchAlgorithm)) ||
					categories.some(category => Utils.searchItem(category, ['name'], this.searchQuery(), searchAlgorithm));
			});
		}

		// filtering
		if (this.selectedProductFilter() !== 'all') {
			products = products.filter((product: Product) => {
				switch (this.selectedProductFilter()) {
					case 'laid_out': return this.isProductLaidOut(product);
					case 'not_laid_out': return !this.isProductLaidOut(product);
					case 'stock_qty': return product.stock_qty && product.stock_qty > 0;
					case 'not_stock_qty': return !product.stock_qty || product.stock_qty < 0;
					case 'inactive': return !product.is_active && this.sessionService.defaults.has_assortment;
					case 'active': return product.is_active && this.sessionService.defaults.has_assortment;
					default: return true;
				}
			});
		}

		// sorting
		const property: string = this.sortProductsParams.property;
		if (property) {
			products = products.sort((a: Product, b: Product) => {
				switch (property) {
					case 'brand':
						return Utils.generalSorting({
							firstEl: a.brandsUniqueName || '',
							secondEl: b.brandsUniqueName || '',
							order: this.sortProductsParams.order
						});
					case 'assortment_type':
						return Utils.generalSorting({
							firstEl: a.assortment_type?.name || '',
							secondEl: b.assortment_type?.name || '',
							order: this.sortProductsParams.order
						});
					case 'status':
						return Utils.generalSorting({
							firstEl: a.statusesUniqueName || '',
							secondEl: b.statusesUniqueName || '',
							order: this.sortProductsParams.order
						});
					case 'article':
					case 'stock_qty':
					case 'barcode':
					case 'name':
					case 'sales_qty':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: a.avgPrice,
							secondEl: b.avgPrice,
							order: this.sortProductsParams.order
						});
					case 'is_new':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: true
						});
				}
			});

			// Now we move the empty elements to the end of the list
			const emptyItems: Array<Product> = products.filter((item: Product) => {
				if (property === 'is_new') return false;
				return property === 'brand' ? !item.brands.length : (!item[property] && item[property] !== 0);
			});
			products = products
				.filter((item: Product) => {
					if (property === 'is_new') return true;
					return property === 'brand' ? item.brands.length : (item[property] || item[property] === 0);
				})
				.concat(emptyItems);
		}

		this.panelProducts = products;
		const needOpenGroups: boolean = !!this.searchQuery();

		if (this.productsGroupingAvailable) {
			const categoryLevel: number = Number(this.activity.productsGrouping().split('_')[1]);
			if (categoryLevel && categoryLevel > this.categoriesLevels) {
				untracked(() => this.activity.setProductsGrouping(products.length ? 'categories_' + this.categoriesLevels : ''));
			}

			if (this.activity.productsGrouping() === 'planograms') {
				this.panelProductsItems = this.buildProductsItemsByPlanograms(products, resetPage, needOpenGroups);
			} else if (this.activity.productsGrouping() === 'brands') {
				this.panelProductsItems = this.buildProductsItemsByBrands(products, resetPage, needOpenGroups);
			} else if (this.activity.productsGrouping().includes('categories')) {
				this.panelProductsItems = this.buildProductsItemsByCategories(products, categoryLevel, resetPage, needOpenGroups);
			} else {
				this.panelProductsItems = this.buildProductsItems(products);
			}
		} else {
			this.panelProductsItems = this.buildProductsItems(products);
		}

		const columns: Array<IColumnType> = [
			{
				sorter: true,
				key: 'name',
				size: 26,
				default_visible: true,
				title: 'NAME',
				button: false,
				always_visible: true,
				dtype: 'product_name',
				show_tooltip: true
			},
			{
				sorter: true,
				key: 'article',
				size: 8,
				default_visible: true,
				title: 'ARTICLE_VALUE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			},
			{
				sorter: true,
				key: 'barcode',
				size: 10,
				default_visible: this.sessionService.hasFeature(ServiceFeatures.LoadAssortment),
				title: 'BARCODE_VALUE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			}
		];

		if (this.sessionService.hasFeature(ServiceFeatures.AssortmentTypes)) {
			columns.push({
				sorter: true,
				key: 'assortment_type',
				size: 10,
				default_visible: false,
				title: 'ASSORTMENT_TYPE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStatuses)) {
			columns.push({
				sorter: true,
				key: 'status',
				size: 10,
				default_visible: false,
				title: 'PRODUCT_STATUS_TYPE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Brands)) {
			columns.push({
				sorter: true,
				key: 'brand',
				size: 12,
				default_visible: true,
				title: 'BRAND_NAME',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
			columns.push({
				sorter: true,
				key: 'stock_qty',
				size: 8,
				default_visible: true,
				title: 'STOCK_QTY',
				button: false,
				always_visible: false,
				dtype: 'number',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductPrice)) {
			columns.push({
				sorter: true,
				key: 'price',
				size: 8,
				default_visible: false,
				title: 'PRICE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Receipts)) {
			columns.push({
				sorter: true,
				key: 'sales_qty',
				size: 8,
				default_visible: false,
				title: 'SALES_QTY',
				button: false,
				always_visible: false,
				dtype: 'number',
				show_tooltip: true
			});
		}

		this.productsTableData = {
			count: products.length,
			has_data: this.planograms().length > 1 || this.products().length > 0,
			next: 'yes',
			results: {
				columns: columns,
				dataSource: this.panelProductsItems.slice(0, this.productsTablePage * PRODUCTS_TABLE_PAGE_SIZE)
			}
		};

		if (!resetPage || needOpenGroups) {
			for (let item of this.productsTableData.results.dataSource) {
				if (!item.opened || !item.children) continue;
				const index: number = this.productsTableData.results.dataSource.indexOf(item);
				this.productsTableData.results.dataSource.splice(index + 1, 0, ...item.children);
			}
		}
	}

	buildProductsItems(products: Array<Product>): Array<IProductsTableItem> {
		return products.map((product: Product) => {
			return {
				key: product.id,
				name: {
					value: product.name,
					clickable: false,
					tags: this.buildProductsTableTags([product]),
					is_master_position: product.is_master_position
				},
				clickable_params: {
					dragData: {
						element: [product],
						type: 'product'
					}
				},
				article: product.article,
				barcode: product.barcode,
				brand: product.brandsUniqueName,
				stock_qty: product.stock_qty,
				price: product.price,
				assortment_type: product.assortment_type?.name,
				status: product.statusesUniqueName,
				sales_qty: product.sales_qty
			}
		});
	}

	buildProductsTableTags(products: Array<Product>, hideTitle: boolean = false): { colors: Array<string>, title: string } {
		let title: string;
		const tags: Array<string> = [];
        if (!products.length) return { colors: tags, title: '' };
		if (products.every((product: Product) => !product.is_active)) tags.push('black');
		if (products.every((product: Product) => this.isProductLaidOut(product))) {
			tags.push('orange');
		} else {
			if (!products.some((product: Product) => this.isProductLaidOut(product)) && products.every((product: Product) => product.is_new)) {
				tags.push('green');
				title = 'PRODUCT_CARD.ACTIVE_NOT_LAYOUT';
			}
		}
		if (tags.includes('black') && tags.includes('orange')) {
			title = 'PRODUCT_CARD.NOT_ACTIVE_LAYOUT_THIS';
		} else {
			if (tags.includes('black')) title = 'PRODUCT_CARD.NOT_ACTIVE_NOT_LAYOUT_THIS';
			if (tags.includes('orange')) title = 'PRODUCT_CARD.ACTIVE_LAYOUT_THIS';
		}

		return {
			colors: tags,
			title: hideTitle ? '' : title
		}
	}

	buildProductsItemsByBrands(
		products: Array<Product>,
		resetPage: boolean,
		needOpenGroups: boolean
	): Array<IProductsTableItem> {
		const grouping: Array<Array<Product>> = Utils.groupBy(products, 'brandsUniqueId'),
			property: string = this.sortProductsParams.property;
		let items: Array<IProductsTableItem> = grouping.map((group: Array<Product>) => {
			return {
				key: group[0].brandsUniqueId,
				name: {
					value: group[0].brandsUniqueName || 'NO_BRAND',
					tags: this.buildProductsTableTags(group, true),
				},
				article: null,
				barcode: null,
				brand: group[0].brandsUniqueName,
				stock_qty: group.some((p: Product) => p.stock_qty !== null)
					? group.reduce((a: number, b: Product) => a + b.stock_qty, 0)
					: null,
				sales_qty: group.some((p: Product) => p.sales_qty !== null)
					? group.reduce((a: number, b: Product) => a + b.sales_qty, 0)
					: null,
				price: null,
				assortment_type: null,
				status: null,
				opened: needOpenGroups ||
					(
						!resetPage &&
						Boolean(this.panelProductsItems.find((p: IProductsTableItem) => p.key === group[0].brandsUniqueId)?.opened)
					),
				children: group.map((product: Product) => {
					return {
						key: product.id,
						name: {
							value: product.name,
							tags: this.buildProductsTableTags([product]),
							is_master_position: product.is_master_position
						},
						article: product.article,
						barcode: product.barcode,
						brand: product.brandsUniqueName,
						stock_qty: product.stock_qty,
						sales_qty: product.sales_qty,
						price: product.price,
						assortment_type: product.assortment_type?.name,
						status: product.statusesUniqueName,
						level: 2,
						clickable_params: {
							dragData: {
								element: [product],
								type: 'product'
							}
						}
					}
				}),
				clickable_params: {
					dragData: {
						element: group,
						type: 'product'
					}
				}
			}
		});

		if (property) {
			items = items.sort((a: IProductsTableItem, b: IProductsTableItem) => {
				switch (property) {
					case 'brand':
					case 'article':
					case 'stock_qty':
					case 'sales_qty':
					case 'barcode':
					case 'assortment_type':
					case 'status':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: Utils.avgProductPrice(a.price),
							secondEl: Utils.avgProductPrice(b.price),
							order: this.sortProductsParams.order
						});
					case 'name':
						return Utils.generalSorting({
							firstEl: a.name.value,
							secondEl: b.name.value,
							order: this.sortProductsParams.order
						});
				}
			});

			// Тепер переміщуємо порожні елементи в кінець списку.
			const emptyItems: Array<IProductsTableItem> = items.filter((item: IProductsTableItem) => {
				if (property === 'is_new') return false;
				return property === 'name' ? !item.brand : (!item[property] && item[property] !== 0);
			});
			items = items
				.filter((item: IProductsTableItem) => {
					if (property === 'is_new') return true;
					return property === 'name' ? item.brand : (item[property] || item[property] === 0);
				})
				.concat(emptyItems);
		}

		return items;
	}

	buildProductsItemsByCategories(
		products: Array<Product>,
		categoryLevel: number,
		resetPage: boolean,
		needOpenGroups: boolean
	): Array<IProductsTableItem> {
		const groupBy: GroupByCategory = new GroupByCategory('category', String(categoryLevel)),
			property: string = this.sortProductsParams.property;
		let items: Array<IProductsTableItem> = groupBy
			.group(products)
			.map((group: { id: string, name: string, products: Array<Product> }) => {
				return {
					key: +group.id,
					name: {
						value: group.name || 'WITHOUT_CATEGORY',
						tags: this.buildProductsTableTags(group.products, true)
					},
					article: null,
					barcode: null,
					brand: null,
					stock_qty: group.products.some((p: Product) => p.stock_qty !== null)
						? group.products.reduce((a: number, b: Product) => a + b.stock_qty, 0)
						: null,
					sales_qty: group.products.some((p: Product) => p.sales_qty !== null)
						? group.products.reduce((a: number, b: Product) => a + b.sales_qty, 0)
						: null,
					price: null,
					assortment_type: null,
					status: null,
					opened: needOpenGroups ||
						(
							!resetPage &&
							Boolean(this.panelProductsItems.find((p: IProductsTableItem) => p.key === +group.id)?.opened)
						),
					children: group.products.map((product: Product) => {
						return {
							key: product.id,
							name: {
								value: product.name,
								tags: this.buildProductsTableTags([product]),
								is_master_position: product.is_master_position
							},
							article: product.article,
							barcode: product.barcode,
							brand: product.brandsUniqueName,
							stock_qty: product.stock_qty,
							price: product.price,
							assortment_type: product.assortment_type?.name,
							status: product.statusesUniqueName,
							sales_qty: product.sales_qty,
							level: 2,
							clickable_params: {
								dragData: {
									element: [product],
									type: 'product'
								}
							}
						}
					}),
					clickable_params: {
						dragData: {
							element: group.products,
							type: 'product'
						}
					}
				}
			});

		if (property) {
			items = items.sort((a: IProductsTableItem, b: IProductsTableItem) => {
				switch (property) {
					case 'brand':
					case 'article':
					case 'stock_qty':
					case 'sales_qty':
					case 'barcode':
					case 'assortment_type':
					case 'status':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: Utils.avgProductPrice(a.price),
							secondEl: Utils.avgProductPrice(b.price),
							order: this.sortProductsParams.order
						});
					case 'name':
						return Utils.generalSorting({
							firstEl: a.name.value,
							secondEl: b.name.value,
							order: this.sortProductsParams.order
						});
				}
			});

			// Тепер переміщуємо порожні елементи в кінець списку.
			const emptyItems: Array<IProductsTableItem> = items.filter((item: IProductsTableItem) => {
				if (property === 'is_new') return false;
				return property === 'name' ? !item.name.value : (!item[property] && item[property] !== 0);
			});
			items = items
				.filter((item: IProductsTableItem) => {
					if (property === 'is_new') return true;
					return property === 'name' ? item.name.value : (item[property] || item[property] === 0);
				})
				.concat(emptyItems);
		}

		return items;
	}

	buildProductsItemsByPlanograms(
		products: Array<Product>,
		resetPage: boolean,
		needOpenGroups: boolean,
	): Array<IProductsTableItem> {
		const hasFilters: boolean = !!this.searchQuery()
			|| this.selectedProductFilter() !== 'all'
			|| !!(this.activity.activeBlocks().length || this.activity.selectedBlock());
		const productsSet: Set<Product> = new Set(products);
		const property: string = this.sortProductsParams.property;
		let items: Array<IProductsTableItem> = this.planograms()
			.filter((planogram: IPlanogram) =>
				!hasFilters || planogram.products().some((product: Product) => productsSet.has(product))
			)
			.map((planogram: IPlanogram) => {
				const planogramProducts: Set<Product> = new Set(planogram.products());
				const group: Array<Product> = products.filter((product: Product) => planogramProducts.has(product));

				return {
					key: planogram.id,
					name: {
						value: planogram.name,
						tags: this.buildProductsTableTags(group, true),
					},
					article: null,
					barcode: null,
					brand: null,
					stock_qty: group.some((p: Product) => p.stock_qty !== null)
						? group.reduce((a: number, b: Product) => a + b.stock_qty, 0)
						: null,
					sales_qty: group.some((p: Product) => p.sales_qty !== null)
						? group.reduce((a: number, b: Product) => a + b.sales_qty, 0)
						: null,
					price: null,
					assortment_type: null,
					status: null,
					...(group.length ? {
						opened: needOpenGroups ||
							(
								!resetPage &&
								Boolean(this.panelProductsItems.find((p: IProductsTableItem) => p.key === planogram.id)?.opened)
							),
						children: group.map((product: Product) => {
							return {
								key: `${planogram.id}-${product.id}`,
								name: {
									value: product.name,
									tags: this.buildProductsTableTags([product]),
									is_master_position: product.is_master_position
								},
								article: product.article,
								barcode: product.barcode,
								brand: product.brandsUniqueName,
								stock_qty: product.stock_qty,
								sales_qty: product.sales_qty,
								price: product.price,
								assortment_type: product.assortment_type?.name,
								status: product.statusesUniqueName,
								level: 2,
								clickable_params: {
									dragData: {
										element: [product],
										type: 'product'
									}
								}
							};
						}),
					} : {}),
					clickable_params: {
                        isGroup: true,
						dragData: {
							element: group,
							type: 'product'
						}
					}
				};
			});

		const emptyItems: Array<IProductsTableItem> = items.filter((item: IProductsTableItem) => !item.children?.length);
		items = items.filter((item: IProductsTableItem) => item.children?.length);

		if (property) {
			const sortItems = (a: IProductsTableItem, b: IProductsTableItem): number => {
				switch (property) {
					case 'name':
						return Utils.generalSorting({
							firstEl: a.name.value,
							secondEl: b.name.value,
							order: this.sortProductsParams.order
						});
					case 'article':
					case 'stock_qty':
					case 'sales_qty':
					case 'barcode':
					case 'brand':
					case 'assortment_type':
					case 'status':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: Utils.avgProductPrice(a.price),
							secondEl: Utils.avgProductPrice(b.price),
							order: this.sortProductsParams.order
						});
				}
			};

			items = items.sort(sortItems);
			emptyItems.sort(sortItems);
		}

		return [...items, ...emptyItems];
	}

	rebuildProductsTable(resetPage: boolean = false): void {
		if (resetPage && this.productsTable) this.productsTable.scrollToTop();
		this.buildProductsTable(resetPage);
	}

	ngOnDestroy() {
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();

        if (this.planograms()?.length) {
            this.teardownStandConversationSubscriptions();
            this.planogramGroupUsersActivityService.disconnect(
                this.planograms().map((planogram: IPlanogram) => Number(planogram.main_stand))
            );
            this.socketService.deleteSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(this.planograms()[0]?.stand?.id )});
        }
		this.socketSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
		this.productsTableSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
		this.detailedElementWidgetOverlayService.closeAll();
		this.planogramCanvasOverlayService.closeAll();

		if (this.saveTimeout) {
			clearTimeout(this.saveTimeout);
			this.saveTimeout = null;
		}
		if (this.pingInterval) clearInterval(this.pingInterval);
		this.dragOutsideService.clear();
		this.dragOutsideService.clearPos();
	}

	private fixProductPhoto(product: Product): void {
		const productURL: Array<string> = product.photo.split('/');

		if (productURL[productURL.length - 1] == '0') {
			productURL[productURL.length - 1] = 'img/img404.jpg';
			product.photo = productURL.join('/');
		}
	}

	private replaceEmptyKeyWithMinusOne(obj: Record<string, any>): Record<string, any> {
		const newObj: Record<string, any> = {};

		for (const key in obj) {
			const newKey: string = key === '' ? '-1' : key;
			newObj[newKey] = obj[key];
		}

		return newObj;
	}

	ngOnInit() {
		this.modes.setImagesMode(this.sessionService.personalSettings.planogramImagesMode);
		this.modes.setSequenceNumberMode(this.sessionService.personalSettings.sequenceNumberMode);
		this.modes.setTransparentBoxesMode(this.sessionService.personalSettings.transparentBoxesMode);

		if (this.sessionService.hasAccess('ph-plan-view')) this.planAccess = 'view';
		if (this.sessionService.hasAccess('ph-plan-edit')) this.planAccess = 'edit';
        this.activity.updateLabelsState({ dateTo: this.labelsDate.date_to, dateFrom: this.labelsDate.date_from });
		this.isManager = this.sessionService.defaults.isManager;

		this.productsService.getProductsCount().subscribe((response: { count: number }) => {
			this.hasProducts = !!response.count;

			if (
				!this.hasProducts
				&& this.sessionService.hasFeature(ServiceFeatures.LoadAssortment)
			) {
				this.dialog
					.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
						width: '720px',
						autoFocus: false,
						backdropClass: 'back-drop',
						data: {
							twoAnswers: true,
							label: 'ASSORTMENT_UPLOAD.YOU_DONT_HAVE_PRODUCTS',
							secondLabel: 'ASSORTMENT_UPLOAD.WANT_TO_LOAD_NEW',
							confirmLabel: 'ASSORTMENT_UPLOAD.LOAD_NEW',
							cancelLabel: 'DEMO.ASSORTMENT.USE_TEST'
						}
					})
					.afterClosed()
					.subscribe((result: IConfirmModalAnswer) => {
						if (result.answer === null) return;
						if (result.answer) {
							this.router
								.navigate(this.sessionService.getLink(['product', 'assortment-upload']))
								.finally();
						} else {
							this.router
								.navigate(this.sessionService.getLink(['product']), {
									queryParams: {
										needLoad: true
									}
								})
								.finally();
						}
					});
			}
		});

		//setTimeout(() => this.openInitOnboardingModal(), 1000);

		this.userIsStoreManager = this.sessionService.defaults.isManager;

		const leftPanelSavedWidth: number = this.sessionService.settings.planogramEditLeftPanelWidth;
		if (leftPanelSavedWidth) this.leftPanelSavedWidth = leftPanelSavedWidth;
		this.leftPanelClosed = !!this.sessionService.settings.planogramEditLeftPanelClosed;

		void this.layoutService.getUsers().then((users: Array<IChatUser>) => {
			this.chatUsers = users;
		});
	}

	private async initPlanogram(planograms: Array<IPlanogram>): Promise<void> {
        this.loader.set(true);
		this.planograms.set(planograms);
		this.activity.setAllPlanograms(planograms);
        this.activity.setPlanogram(planograms[0]);
        const group = this.planogramResolver.state$().group;
		if (group) this.planogramGroup = { id: group.id, name: group.name, updated_at: '' };

		const planId: number = Number(this.route.snapshot.queryParams['planogram']) || planograms[0].planogram_id;
		this.planConf = await lastValueFrom(this.plansService.getItem$(planId));
        this.setProductsPercents();

        if (planograms.length === 1) {
            const planogram: IPlanogram = planograms[0];

            const planList = planogram.planogram_stand
                .map((p) => ({ id: p.planogram_id, name: p.planogram_name, active: false }))
                .sort(Utils.sortBy('name'));

            const planIndex = planList.findIndex((item: { id: number }) => item.id == this.planConf?.id);
            this.planFilters = ~planIndex ? this.changeIndex(planList, planIndex, 0) : planList;

            this.planogramList = planogram.planogram_stand.find((e: IPlanogramPlanData) => e.planogram == planId)?.stands
                .map((s) => ({ id: s.main_stand_id, name: s.name, active: false }))
                .sort(Utils.sortBy('name'));

            const planogramIndex = this.planogramList.findIndex((item: { id: number }) => item.id == planogram.main_stand);
            this.planogramFilters = ~planogramIndex ? this.changeIndex(this.planogramList, planogramIndex, 0) : this.planogramList;

            this.planogramScheduledDate.set(planogram.scheduled_date ?? '');
            this.syncMasterRuleIndicator(planogram);

            if (planogram.conf.productFilters) {
				this.addProductsFilterService.filters = planogram.conf.productFilters;
			}

			if (planogram.conf.reportParams) {
				this.reportParams = planogram.conf.reportParams;
			}

			this.brandsRecommendedPercent = this.replaceEmptyKeyWithMinusOne(planogram.conf.brandsRecommendedPercent || {});
			this.categoriesRecommendedPercent = this.replaceEmptyKeyWithMinusOne(planogram.conf.categoriesRecommendedPercent || {});

            this.equipmentUsage = planogram.instrument.instrument_usage ?? [];
        } else {
            const planData = planograms
                .flatMap((p) => p.planogram_stand ?? [])
                .find((p) => p.planogram_id == planId);

            this.planFilters = [{
                id: planId,
                name: planData?.planogram_name ?? this.planConf?.name,
                active: false
            }];

            this.activity.setProductsGrouping('planograms');
            this.syncBrandStatisticsFromPlanogram(planograms[0]);
            this.masterRuleIndicator.set(null);
        }

        this.productsGroupingAvailable = this.planograms().length > 1 || this.sessionService.hasFeature(ServiceFeatures.Brands)
			|| this.sessionService.hasFeature(ServiceFeatures.Categories);

		for (let planogram of planograms) {
            const indicators: Array<IPlanogramIndicator> = planogram.indicators ?? [];

            if (!indicators.length) continue;

            const isInnerUser: boolean = SessionService.isStaff(this.sessionService.username)
                || SessionService.isNIQ(this.sessionService.username);

            if (isInnerUser && !SessionService.isInnerClient(this.sessionService.clientId)) continue;

            indicators
                .filter((indicator: IPlanogramIndicator) => indicator.type !== 'MASTER_RULE' && indicator.id != null)
                .forEach((indicator: IPlanogramIndicator) => {
                    this.planogramsService.deleteIndicator(indicator.id).subscribe();
                });
        }

        this.usersActivitySubject = this.planogramGroupUsersActivityService.connect(
            planograms.map((planogram: IPlanogram) => Number(planogram.main_stand))
        );

        this.setupStandConversationSubscriptions(planograms);





		if ('highlight' in this.route.snapshot.queryParams)
			await this.changeHighlightOption(
				this.route.snapshot.queryParams.highlight
			);

		if ('rotation' in this.route.snapshot.queryParams) {
			const standId = this.route.snapshot.params.id,
				notificationId = this.route.snapshot.queryParams.rotation;

			await this.getChangedProducts(notificationId, standId, this.planConf?.id);
		}



		this.setRealProductPercents();

		planograms.forEach((planogram: IPlanogram) => {
			if (!this.activity.hasHistory(planogram.main_stand)) {
				this.historyGenerate(planogram.main_stand);
			}
			this.activity.setBeginStateFromFirst(planogram.main_stand);
		});

		this.syncStandUpdatedAt();

        this.buildProductsTable(true);
		this.loader.set(false);

		if (planograms.length > 1) {
			setTimeout(() => this.openPlanogramCanvasOverlays());
		}

		this.allowedAutoLayout = this.getLayoutAccessibility();

		this.planograms().forEach((planogram: IPlanogram) => {
			planogram.hasBrands = this.sessionService.defaults.has_brands;
			planogram.hasSuppliers = this.planConf.has_suppliers;
		});
		this.cachedAutolayoutParams.hasBrands = this.sessionService.defaults.has_brands;
		this.cachedAutolayoutParams.hasSuppliers = this.planConf.has_suppliers;

		if (this.route.snapshot.queryParams.sort_new) {
			this.sortProducts('is_new', true);
		}

		if (
			this.sessionService.hasExtension(ServiceExtensions.LayoutRules) &&
			!this.sessionService.defaults.settings.hideApplyRule &&
			!this.products().length
		) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				panelClass: 'full-width-modal-actions',
				data: {
					label: 'RULES.MODAL.APPLY',
					twoAnswers: true,
					confirmLabel: 'RULES.APPLY',
					cancelLabel: 'RULES.MODAL.CONTINUE_EDITING',
					checkBox: true,
					checkBoxLabel: 'NOT_SHOW_ANYMORE'
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer === null) return;
				if (result.checkBoxState) {
					this.sessionService.defaults.settings.hideApplyRule = result.checkBoxState;
					this.sessionService.updateSettings();
				}
				if (result.answer) {
					this.toggleLayoutRulesPanel();
				}
			});
		}

        this.socketService.addSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(this.planograms()[0].stand?.id )});

		this.productsTableSubscriptions.push(
			this.sortingProductsTableSubject.subscribe((value: string) => {
				const property: string = value.replace('-', ''),
					order: boolean = value.includes('-');
				this.sortProducts(property, order);
			}),

			this.updateProductsTableSubject.subscribe(() => {
				const displayedCount: number = this.productsTablePage * PRODUCTS_TABLE_PAGE_SIZE,
					allCount: number = this.panelProductsItems.length;

				if (allCount > displayedCount) {
					this.productsTableData.results.dataSource.push(
						...this.panelProductsItems.slice(displayedCount, displayedCount + PRODUCTS_TABLE_PAGE_SIZE)
					);
					this.productsTableData = {...this.productsTableData};
					this.productsTablePage++;
				}
			}),

			this.startDraggingProductsSubject.subscribe((products: Array<Product>) => {
				if (this.modes.equipmentEditMode()) return;
				if (products.some((product: Product) => !this.activity.isPanelProductActive(product))) {
					this.activity.clearActivePanelProducts();
					for (let product of products) {
						this.activity.addActivePanelProduct(product);
					}
				}
				this.startDragProduct(products[0]);
			}),

			this.clickProductsTableSubject.subscribe((data: ITableRowClickData) => {
				if (!this.modes.equipmentEditMode()) {
					this.setPanelProductsActive(data.element.clickable_params.dragData.element, data.event);
				}
			})
		);

		this.socketSubscriptions.push(
			this.socketService.conversationReadMessage.subscribe((data: IStandConversationDone) => {
				this.updateConversationMessage(data.id, (message: IStandConversationMessage) => {
					message.done = data.done;
				});
			}),

			this.socketService.conversationUpdateImage.subscribe((data: IStandConversationImage) => {
				this.updateConversationMessage(data.stand_conversation_id, (message: IStandConversationMessage) => {
					message.files = data.files;
				});
			}),

			this.socketService.conversationNewMessage.subscribe((data: IStandConversationMessage) => {
				if (!this.planograms().some((planogram: IPlanogram) => Number(planogram.main_stand) === data.stand)) return;

				this.appendConversationMessage(data.stand, data);
			}),

			this.productCardService.saveSubject.subscribe((product: IProductCard) => {
				this.updateProductCard(product);
				this.activity.highlightUpdate$.next();
			}),

            this.socketService.planogramSave.subscribe((response: IUserSave) => {
                if (response.session_id === this.sessionService.sessionId) return;
                const isStaff: boolean = SessionService.isStaff(response.email);
                const isNiq: boolean = SessionService.isNIQ(response.email);
                if (!SessionService.isStaff(this.sessionService.defaults.email) && isStaff) return;
                if (
                    !SessionService.isNIQ(this.sessionService.defaults.email) && 
                    !SessionService.isStaff(this.sessionService.defaults.email) && 
                    isNiq
                ) return;
                this.ignoreOtherSave.set(false);
                const savedPlanogram: IPlanogram | undefined = response.planogram_id != null
                    ? this.planograms().find((planogram: IPlanogram) => Number(planogram.main_stand) === response.planogram_id)
                    : undefined;
                const showPlanogramName: boolean = this.planograms().length > 1 && !!savedPlanogram;

                this.snackBarService.setSnackBarNotification({
                    messageText: showPlanogramName
                        ? 'COMMON.PLANOGRAM.OTHER_USER_SAVE_NAMED'
                        : 'COMMON.PLANOGRAM.OTHER_USER_SAVE',
                    translateParams: {
                        user: response.full_name,
                        ...(showPlanogramName ? { planogram: savedPlanogram.name } : {})
                    },
                    durationSeconds: 60 * 60,
                    closeButton: false,
                    closeActions: [
                        { action: 'COMMON.RELOAD_PAGE', callback: () => window.location.reload() },
                        { action: 'OK', callback: () => this.ignoreOtherSave.set(true) }
                    ]
                });
            }),

            this.socketService.equipmentSave.subscribe((response: IUserSave) => {
                if (response.session_id === this.sessionService.sessionId) return;
                const isStaff: boolean = SessionService.isStaff(response.email);
                const isNiq: boolean = SessionService.isNIQ(response.email);
                if (!SessionService.isStaff(this.sessionService.defaults.email) && isStaff) return;
                if (
                    !SessionService.isNIQ(this.sessionService.defaults.email) && 
                    !SessionService.isStaff(this.sessionService.defaults.email) && 
                    isNiq
                ) return;
                this.ignoreOtherEquipmentSave = false;
                this.snackBarService.setSnackBarNotification({
                    messageText: 'COMMON.EQUIPMENT.OTHER_USER_SAVE',
                    translateParams: { user: response.full_name },
                    durationSeconds: 60 * 60,
                    closeButton: false,
                    closeActions: [
                        { action: 'COMMON.RELOAD_PAGE', callback: () => window.location.reload() },
                        { action: 'OK', callback: () => this.ignoreOtherEquipmentSave = true }
                    ]
                });
            }),

            this.socketService.equipmentInfoSave.subscribe((response: IUserSave) => {
                if (response.session_id === this.sessionService.sessionId) return;
                const isStaff: boolean = SessionService.isStaff(response.email);
                const isNiq: boolean = SessionService.isNIQ(response.email);
                if (!SessionService.isStaff(this.sessionService.defaults.email) && isStaff) return;
                if (
                    !SessionService.isNIQ(this.sessionService.defaults.email) &&
                    !SessionService.isStaff(this.sessionService.defaults.email) &&
                    isNiq
                ) return;
                this.snackBarService.setSnackBarNotification({
                    messageText: 'COMMON.EQUIPMENT.OTHER_USER_SAVE_INFO',
                    translateParams: { user: response.full_name },
                    durationSeconds: 60 * 60,
                    closeButton: false,
                    closeActions: [
                        { action: 'COMMON.RELOAD_PAGE', callback: () => window.location.reload() },
                        { action: 'OK', callback: () => {} }
                    ]
                });
            })
		);
	}

	async loadMessages(): Promise<void> {
		const planogramId: number = this.conversationPlanogramId;
		if (!planogramId || !this.next_messages) return;

		const page: number = Number(this.next_messages);
		const response = await this.layoutService.getMessages(planogramId, page);

		if (this.conversationPlanogramId !== planogramId) return;

		this.next_messages = PlanogramEditComponent.getNextPage(response.next);
		this.chatMessages = this.chatMessages.concat(response.results);
		this.cacheCurrentConversation();
	}

	private async refreshConversation(): Promise<void> {
		const planogramId: number = this.conversationPlanogramId;
		if (!planogramId) return;

		try {
			const response = await this.layoutService.getMessages(planogramId, 1);
			if (this.conversationPlanogramId !== planogramId) return;

			const responseMessages = response.results ?? [];
			const responseIds = new Set(responseMessages.map((message) => message.id));
			const preservedMessages = this.chatMessages.filter((message) => !responseIds.has(message.id));
			const hasOlderPages = this.chatMessages.length > responseMessages.length;

			this.next_messages = hasOlderPages ? this.next_messages : PlanogramEditComponent.getNextPage(response.next);
			this.chatMessages = responseMessages.concat(preservedMessages);
			this.cacheCurrentConversation();
		} catch {
			this.chatMessages = [];
		}
	}

	private getConversationMainStandId(planogram: IPlanogram | null): number | null {
		return planogram?.main_stand ?? null;
	}

	private cacheCurrentConversation(): void {
		if (this.conversationPlanogramId == null) return;

		this.conversationCache.set(this.conversationPlanogramId, {
			chatMessages: [...this.chatMessages],
			next_messages: this.next_messages
		});
	}

	private setupStandConversationSubscriptions(planograms: Array<IPlanogram>): void {
		if (planograms.length <= 1) return;

		planograms.forEach((planogram: IPlanogram) => {
			const mainStandId: number = Number(planogram.main_stand);

			if (this.subscribedConversationStandIds.has(mainStandId)) return;

			this.subscribedConversationStandIds.add(mainStandId);
			this.socketService.addSubscription(SocketGroup.StandConversation, { stand_id: mainStandId });
		});
	}

	private teardownStandConversationSubscriptions(): void {
		this.subscribedConversationStandIds.forEach((standId: number) => {
			this.socketService.deleteSubscription(SocketGroup.StandConversation, { stand_id: standId });
		});
		this.subscribedConversationStandIds.clear();
	}

	private getConversationCacheEntry(standId: number): ConversationCacheEntry {
		return this.conversationCache.get(standId) ?? { chatMessages: [], next_messages: 1 };
	}

	private setConversationCacheEntry(standId: number, entry: ConversationCacheEntry): void {
		this.conversationCache.set(standId, entry);

		if (this.conversationPlanogramId === standId) {
			this.chatMessages = [...entry.chatMessages];
			this.next_messages = entry.next_messages;
		}
	}

	private appendConversationMessage(standId: number, message: IStandConversationMessage): void {
		const entry: ConversationCacheEntry = this.getConversationCacheEntry(standId);

		this.setConversationCacheEntry(standId, {
			...entry,
			chatMessages: [...entry.chatMessages, message]
		});
	}

	private updateConversationMessage(
		messageId: number,
		update: (message: IStandConversationMessage) => void
	): void {
		let currentChanged: boolean = false;

		this.chatMessages.forEach((message: IStandConversationMessage) => {
			if (message.id === messageId) {
				update(message);
				currentChanged = true;
			}
		});

		if (currentChanged) {
			this.chatMessages = [...this.chatMessages];
			this.cacheCurrentConversation();
		}

		this.conversationCache.forEach((entry: ConversationCacheEntry, standId: number) => {
			let changed: boolean = false;

			entry.chatMessages.forEach((message: IStandConversationMessage) => {
				if (message.id === messageId) {
					update(message);
					changed = true;
				}
			});

			if (!changed) return;

			this.setConversationCacheEntry(standId, {
				...entry,
				chatMessages: [...entry.chatMessages]
			});
		});
	}

	private async onActivePlanogramConversationChange(planogram: IPlanogram | null): Promise<void> {
		const mainStandId: number | null = this.getConversationMainStandId(planogram);

		if (this.conversationPlanogramId === mainStandId) return;

		this.cacheCurrentConversation();

		const isGroup: boolean = this.planograms().length > 1;

		if (!isGroup && this.conversationPlanogramId != null) {
			this.socketService.deleteSubscription(SocketGroup.StandConversation, { stand_id: this.conversationPlanogramId });
			this.subscribedConversationStandIds.delete(this.conversationPlanogramId);
		}

		this.conversationPlanogramId = mainStandId;

		if (mainStandId == null) {
			this.chatMessages = [];
			this.next_messages = 1;
			this.layoutOpen = false;
			return;
		}

		const cached: ConversationCacheEntry | undefined = this.conversationCache.get(mainStandId);

		if (cached) {
			this.chatMessages = [...cached.chatMessages];
			this.next_messages = cached.next_messages;

			if (this.layoutOpen) {
				await this.refreshConversation();
				this.layoutHistoryComponent?.scrollToBottom();
			}
		} else {
			this.chatMessages = [];
			this.next_messages = 1;

			if (this.layoutOpen) {
				await this.loadMessages();
				this.layoutHistoryComponent?.scrollToBottom();
			}
		}

		if (!isGroup && !this.subscribedConversationStandIds.has(mainStandId)) {
			this.socketService.addSubscription(SocketGroup.StandConversation, { stand_id: mainStandId });
			this.subscribedConversationStandIds.add(mainStandId);
		}
	}


	private applyAddProductsPanelFilters(targetPlanogram: IPlanogram | null): void {
		if (targetPlanogram) {
			if (targetPlanogram.conf?.productFilters) {
				this.addProductsFilterService.filters = targetPlanogram.conf.productFilters;
			} else {
				this.clearAddProductsPanelFilters();
			}
			return;
		}

		const planograms: Array<IPlanogram> = this.planograms();

		if (planograms.length === 1 && planograms[0].conf?.productFilters) {
			this.addProductsFilterService.filters = planograms[0].conf.productFilters;
			return;
		}

		this.clearAddProductsPanelFilters();
	}

	private clearAddProductsPanelFilters(): void {
		this.addProductsFilterService.filters = {
			categories: [],
			brands: [],
			suppliers: [],
			markers: [],
			assortment_types: [],
			product_statuses: [],
			hide_options: [],
			is_master_position: null
		};
	}

	openAddProductsPanel(targetPlanogram: IPlanogram | null = null): void {
		this.closePanel();
		this.productAddTargetPlanogram.set(targetPlanogram);
		this.applyAddProductsPanelFilters(targetPlanogram);

		if (targetPlanogram) {
			this.productAddPanelOpen.set(true);
			return;
		}

		this.productAddPanelOpen.update((open: boolean) => !open);
	}

	openAddProductsPanelForPlanogram(item: IProductsTableItem): void {
		const planogram: IPlanogram | undefined = this.planograms()
			.find((planogramItem: IPlanogram) => planogramItem.id === item.key);

		if (!planogram) return;

		this.openAddProductsPanel(planogram);
	}

	showUploadLayoutModalForPlanogram(item: IProductsTableItem): void {
		const planogram: IPlanogram | undefined = this.planograms()
			.find((planogramItem: IPlanogram) => planogramItem.id === item.key);

		if (!planogram) return;

		this.showUploadLayoutModal(planogram);
	}

	uploadExcelForPlanogram(item: IProductsTableItem): void {
		const planogram: IPlanogram | undefined = this.planograms()
			.find((planogramItem: IPlanogram) => planogramItem.id === item.key);

		if (!planogram) return;

		this.uploadExcel(planogram);
	}

	closeAddProductsPanel(): void {
		this.productAddPanelOpen.set(false);
		this.productAddTargetPlanogram.set(null);
	}

	openBrandPanel() {
		if (this.activity.panelType() == 'brand' && this.activity.panelOpened()) return this.activity.closePanel();

		if (this.planograms().length > 1) {
			const planogram: IPlanogram | undefined = this.analyticsPlanogram();
			if (planogram) {
				this.syncBrandStatisticsFromPlanogram(planogram);
				this.setRealProductPercents();
			}
		}

		this.activity.openPanel('brand');
		this.mixPanelService.trackPage(MixpanelEvents.BrandsStatistics);
		this.layoutOpen = false;
	}

	openSettingsPanel() {
		if (this.activity.settingsPanelDisabled()) return this.activity.closePanel();

		if (this.modes.drawBlockMode()) this.changeDrawBlockMode();

		this.activity.openPanel('size');
		this.layoutOpen = false;
	}

	openHighlightsPanel(): void {
		if (this.sessionService.hasFeature(ServiceFeatures.ProductsHighlight)) {
			this.activity.openPanel('highlights');
			this.layoutOpen = false;
		} else {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.ProductsHighlightsPlanogramEdit);
		}
	}

	openLabelsPanel() {
		if (this.activity.panelType() == 'labels' && this.activity.panelOpened()) return this.activity.closePanel();
		this.activity.openPanel('labels');
		this.layoutOpen = false;
	}

	openPOSMaterialsPanel(): void {
		this.activity.openPanel('pos');
		this.layoutOpen = false;
	}

	startDragProduct(product: Product) {
		if (!this.activity.isPanelProductActive(product)) {
			this.activity.clearActivePanelProducts();
			this.activity.setProductActive();
			this.activity.setDragProducts([product]);
			this.activity.setActivePanelProducts([product]);
			this.activity.closePanel();
		} else {
			this.activity.setDragProducts(this.activity.activePanelProducts());
		}
	}

	async redirectToReport() {
		const planogram: IPlanogram | undefined = this.analyticsPlanogram();
		if (!planogram) return;

		if (this.saveAble() && this.ignoreOtherSave()) {
            await lastValueFrom(this.performSaveAction());
        }
		this.loader.set(true);

		this.router
			.navigate(
				this.sessionService.getLink(['stand', 'place-predict']),
				{
					queryParams: {
						planogram_id: this.planConf?.id,
						stand_id: planogram.main_stand,
						...(this.planogramGroup?.id && { group_id: this.planogramGroup.id })
					}
				}
			)
			.finally();

		this.loader.set(false);
	}

	uploadExcel(targetPlanogram?: IPlanogram) {
		const dialogRef = this.dialog.open(ProductsAddExcelModalComponent, {
			width: '600px',
			height: 'fit-content',
			panelClass: 'upload-excel-modal',
			autoFocus: false,
			backdropClass: 'back-drop'
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (!result || (!result.products && !result.file)) return;

			this.loader.set(true);
			this.productAddPanelOpen.set(false);
			this.productAddTargetPlanogram.set(targetPlanogram ?? null);

			const products_ids = [];
			const requestParams: Partial<IAddProductsPanelParams> = {
				key: 'product_id=',
				plan_id: this.planConf?.id
			};

			if (targetPlanogram) {
				requestParams.planogram_id = targetPlanogram.main_stand;
			}

			if (result.file) {
				const response = await this.addProductsFilterService.uploadExcel(result.file);

				Array.prototype.push.apply(products_ids, response.results.products);
			}

			if (result.products) {
				Array.prototype.push.apply(products_ids, result.products);
			}

			if (!products_ids.length) {
				this.loader.set(false);
				this.productAddTargetPlanogram.set(null);
				this.snackBarService.setSnackBarNotification({
					messageText: 'PLANOGRAM_EDIT.PRODUCT_LIST_UPLOAD_ERROR',
					durationSeconds: 10
				});
				return;
			}

			let products = await lastValueFrom(
				this.addProductsFilterService.getProducts(
					requestParams,
					{ ids: products_ids.join(',') }
				)
			);

			await this.addProducts(
				products.results.map((p) => {
					p.planedPercent = 10;
					return new Product(p);
				})
			);

			this.productAddTargetPlanogram.set(null);
			this.activity.rebuildProductsTable$.next();
			this.loader.set(false);
		});
	}

	showErrorAlert(): void {
		const productErrorToTranslate: { [key in ProductErrors]: string } = {
			[ProductErrors.ShelfLeftLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_LEFT_LIMIT',
			[ProductErrors.ShelfRightLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_RIGHT_LIMIT',
			[ProductErrors.BoxLeftLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_LEFT_LIMIT',
			[ProductErrors.BoxRightLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_RIGHT_LIMIT',
			[ProductErrors.HangBarLeftLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.HANG_BAR_LEFT_LIMIT',
			[ProductErrors.HangBarRightLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.HANG_BAR_RIGHT_LIMIT',
			[ProductErrors.ShelfDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_DEPTH_LIMIT',
			[ProductErrors.BoxDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_DEPTH_LIMIT',
			[ProductErrors.HookDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.HOOK_DEPTH_LIMIT',
			[ProductErrors.ElementHeight]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_HEIGHT_LIMIT',
			[ProductErrors.TrapezeLimits]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.TRAPEZE_LIMITS',
			[ProductErrors.DependLimits]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.DEPEND_WIDTH_LIMIT',
			[ProductErrors.DependDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.DEPEND_DEPTH_LIMIT',
			[ProductErrors.CentralPillarIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.PILLAR_INTERSECTION',
			[ProductErrors.PartitionIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.PARTITION_INTERSECTION',
			[ProductErrors.SideWallsIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SIDE_WALLS_INTERSECTION',
			[ProductErrors.BoxIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_INTERSECTION',
			[ProductErrors.ProductIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.PRODUCT_INTERSECTION',
			[ProductErrors.LooseOnShelf]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.LOOSE_ERROR'
		};
		const groups: Array<{ id: number, name: string, errors: Array<{ key: string, params: Object }> }> = [];

		this.getPlanogramsToSave()
			.filter((planogram: IPlanogram) => planogram.stand.products.some((pg: ProductGroup) => pg.error))
			.forEach((planogram: IPlanogram) => {
				const sortedBaseElements: Array<Element> = planogram.stand.bases
					.sort((a: Element, b: Element) => a.getBoundingX().min - b.getBoundingX().min);
				const errors: Array<{ key: string, params: Object }> = [];

				planogram.stand.products
					.filter((pg: ProductGroup) => pg.error)
					.forEach((pg: ProductGroup) => {
						const elementIndex: number = Utils.getElementIndex(pg.element, pg.element.stand);
						const equipmentBase: Element = [pg.element, ...pg.element.getDepends()]
							.find((depend: Element) => !depend.depend);
						const equipmentIndex: number = sortedBaseElements.indexOf(equipmentBase) + 1;
						const translateParams: Object = {
							article: pg.product.article,
							element: elementIndex,
							equipment: equipmentIndex
						};

						if (pg.dependProduct) translateParams['depend'] = pg.dependProduct.product.article;

						pg.errorTypes.forEach((error: ProductErrors) => {
							errors.push({ key: productErrorToTranslate[error], params: translateParams });
						});
					});

				if (errors.length) groups.push({ id: planogram.id, name: planogram.name, errors });
			});

		this.snackBarService.setSnackBarNotification({
			durationSeconds: 2 * 60 * 60,
			messageText: this.planograms().length > 1 ? 'LAYOUT_INCORRECT_ERROR_MULTI' : 'LAYOUT_INCORRECT_ERROR',
			translateParams: this.planograms().length > 1 ? { count: groups.length } : undefined,
			content: this.extendedProductErrors,
			contentContext: {
				groups,
				multiplePlanograms: this.planograms().length > 1
			}
		});
	}

	static checkAutolayoutFreeSpace(
		target_shelf_products,
		target_space,
		groupAccordance,
		hierarchy,
		max_faces?
	) {
		const min = Math.min(...target_shelf_products.map((i) => i.ItemWidth));

		let diff = Utils.round(target_space - target_shelf_products.reduce((a, b) => a + b.Width, 0)),
			max_iterations = 100,
			iteration_count = 0;

		while (diff >= min) {
			iteration_count++;
			const zero_products = target_shelf_products
				.filter((pg) => !pg.qty)
				.map((pg) => pg.product.id.toString());
			for (let product_id of zero_products) {
				const product = target_shelf_products.find(
					(pg) => pg.product.id == product_id
				);

				if (!product) continue;

				if (product.ItemWidth > diff) continue;

				const totalWidth = Utils.round(
					groupAccordance[product.product.id].realWidth +
					product.ItemWidth
				);
				if (totalWidth > groupAccordance[product.product.id].maximumWidth)
					continue;

				product.FacesQty = 1;
				groupAccordance[product.product.id].realWidth = totalWidth;

				diff = Utils.round(diff - product.ItemWidth);

				if (diff >= min) continue;
				break;
			}

			if (diff < min) break;

			for (let product_id of hierarchy) {
				const product = target_shelf_products.find(
					(pg) => pg.product.id == product_id
				);

				if (!product) continue;

				if (product.ItemWidth > diff) continue;

				if (max_faces && product.FacesQty >= max_faces) continue;

				const totalWidth = Utils.round(
					groupAccordance[product.product.id].realWidth +
					product.ItemWidth
				);
				if (totalWidth > groupAccordance[product.product.id].maximumWidth)
					continue;

				product.FacesQty += 1;
				groupAccordance[product.product.id].realWidth = totalWidth;

				diff = Utils.round(diff - product.ItemWidth);

				if (diff >= min) continue;
				break;
			}

			if (iteration_count === max_iterations) break;
		}
	}

	async openCopyPlanogramModal(): Promise<void> {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramCopy)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.PlanogramCopying);
			return;
		}
		const planogram: IPlanogram = this.activity.planogram();
		if (this.showLoader() || !planogram) return;
		if (!planogram.stand?.products?.length && !planogram.stand?.blocks?.length) return;

		this.dialog
			.open(CopyPlanogramModalComponent, {
				width: '70%',
				autoFocus: false,
				backdropClass: 'back-drop'
			})
			.afterClosed()
			.subscribe(async (result: CopyingData) => {
				if (!result) return;
				this.loader.set(true);

				// for correct conf in copying function
				planogram.conf.reportParams = this.reportParams;
				planogram.conf.products = planogram.products().map((p: Product) => {
					return { id: p.id, planedPercent: p.planedPercent };
				});

				const planogramFrom: IPlanogram = Object.assign({}, planogram, { stand: planogram.stand.copy() }),
					planograms: Array<IPlanogram> = await lastValueFrom(
						this.planogramsService
							.bulkFetch(
								result.planograms.filter((i, idx, array) => {
									return idx == array.findIndex(e => e.planogram_id == i.planogram_id);
								})
							)
							.pipe(
								map((data: Array<IPlanogramData>) => {
									return data.map((item: IPlanogramData) => {
										return this.planogramLayoutService.getPlanogramObject(
											item,
											[],
											item.planogram_stand[0].planogram_id,
											false
										);
									});
								})
							)
					),
					notifications: Array<CopyingNotification> = await this.planogramLayoutService
						.makeCopying(planograms, planogramFrom, result);

				await lastValueFrom(this.notificationsService.createCopyPlanogramNotification({
					from_planogram_id: planogramFrom.id,
					to: notifications.map((item: CopyingNotification) => {
						const notificationsMessages: Array<string> = [];
						if (item.stand_errors?.length) notificationsMessages.push('SIMPLE_COPY_ERROR');
						if (!item.stand_errors?.length && !item.errors?.length) notificationsMessages.push('SIMPLE_COPY_SUCCESS');
						if (!item.errors?.length && result.type) notificationsMessages.push('SIMPLE_COPY_SUCCESS');
						if (item.deniedReversed) notificationsMessages.push('REVERSE_LAYOUT.DENIED');

						return {
							plan_id: item.planogram_id,
							planogram_id: item.id,
							errors: item.errors,
							messages: notificationsMessages
						}
					})
				}));

				this.mixPanelService.trackPage(MixpanelEvents.PlanogramCopyingFromEdit, {
					standId: planogramFrom.id,
					standCount: planograms.length,
					isReversed: result.isReversed,
					copyingType: result.type || 'SIMPLE'
				});
				this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Copy);

				if (this.isCopyTargetInCurrentGroup(result)) {
					await this.reloadCurrentPlanogramRoute();
					return;
				}

				this.loader.set(false);
			});
	}

	private isCopyTargetInCurrentGroup(result: CopyingData): boolean {
		const groupPlanograms = this.planograms();
		if (groupPlanograms.length < 2) return false;

		const groupMainStands = new Set(groupPlanograms.map((planogram: IPlanogram) => planogram.main_stand));

		return result.planograms.some((target) => groupMainStands.has(target.planogram_id));
	}

	private async reloadCurrentPlanogramRoute(): Promise<void> {
		if (this.saveAble() && this.ignoreOtherSave()) {
			await lastValueFrom(this.performSaveAction(false, true));
		}

		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		await this.router.navigateByUrl(this.router.url);
	}

	openSendForExecution(): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramsExecution)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.SendExecution);
			return;
		}
		if (this.showLoader() || this.modes.equipmentEditMode()) return;

		this.analysisOpen = false;

		if (this.saveAble()) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(
				ConfirmModalComponent,
				{
					width: '574px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'PLANOGRAM_EDIT.SAVE_AND_SEND_EXECUTION_CONFIRM',
						cancelLabel: 'COMMON.CANCEL',
						confirmLabel: 'GO',
						twoAnswers: true
					}
				}
			);

			dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
				if (!result?.answer) return;
				this.performSaveAction().subscribe(() => {
					this.proceedToSendForExecution();
				});
			});
			return;
		}

		this.proceedToSendForExecution();
	}

	proceedToSendForExecution(): void {
		const queryParams: Record<string, string | number> = {
			execution: this.planograms().map((p: IPlanogram) => p.main_stand).join(',')
		};
        queryParams.plan = this.planConf.id;

		if (this.planogramGroup?.id) {
			queryParams.group = this.planogramGroup.id;
			queryParams.name = this.planogramGroup.name;
		} else {
			const planogram: IPlanogram = this.planograms()[0];
			queryParams.planogram = planogram.main_stand;
			queryParams.name = this.planogramFilters.find((p: { id: number }) => p.id == planogram.main_stand)?.name ?? planogram.name;
		}

		void this.router.navigate(
			this.sessionService.getLink(['stand', 'list']),
			{ queryParams }
		);
	}

    performSaveAction(autoSave: boolean = false, forceSave: boolean = false): Observable<void> {
        if (!this.saveAble() || !this.ignoreOtherSave()) return EMPTY;

		const startSave = (): Observable<void> => {
			this.saveInProgress.set(true);

			return this.saveAction(autoSave, forceSave).pipe(
				finalize(() => {
					this.saveObserver = null;
					this.saveInProgress.set(false);
				})
			);
		};

		const save$: Observable<void> = this.saveObserver
			? this.saveObserver.pipe(last(), switchMap(() => startSave()))
			: startSave();

		this.saveObserver = save$;
		return save$;
	}

	stopProductMove(enableCanvasListening: boolean = false): void {
		this.activity.setProductMove(false);
	}

	private saveAction(autoSave: boolean = false, forceSave: boolean = false): Observable<void> {
        if (this.saveTimeout) {
			clearTimeout(this.saveTimeout);
			this.saveTimeout = null;
		}
        if (autoSave && this.activity.productMove()) {
			this.pausedInterval.set(true);
			return EMPTY;
		}

        const planogramsToSave: Array<IPlanogram> = this.getPlanogramsToSave();
        if (!planogramsToSave.length) return EMPTY;

        const productsError: boolean = planogramsToSave.some((planogram: IPlanogram) =>
			planogram.stand.products.some((pg: ProductGroup) => pg.error)
		);

		if (productsError && !forceSave) {
            this.pausedInterval.set(true);
            if (!autoSave) this.showErrorAlert();
			return EMPTY;
		}

		if (!autoSave) this.loader.set(true);

		const configurations: Array<StandConf> = planogramsToSave.map((planogram: IPlanogram) =>
			this.getSaveConfiguration(planogram)
		);

		return this.planogramsService.bulkUpdate(configurations).pipe(
			switchMap(() => {
				planogramsToSave.forEach((planogram: IPlanogram) => {
                    planogram.date = new Date().toISOString();
				});
				this.syncStandUpdatedAt();

				const makeSvgConf: Array<any> = planogramsToSave.map((planogram: IPlanogram) => {
					return this.buildMakeSvgConf(planogram);
				});
				return from(this.planogramsService.makeSvg(makeSvgConf));
			}),
			tap(() => {
				planogramsToSave.forEach((planogram: IPlanogram) =>
					this.activity.markHistorySaved(planogram.main_stand)
				);
				this.markMasterRuleOutdatedLocally(planogramsToSave);

				if (this.planogramGroup?.id) {
					this.mixPanelService.trackPage(MixpanelEvents.PlanogramGroupSave, {
						planogramsCount: this.planograms().length,
						savedPlanogramsCount: planogramsToSave.length,
						autoSave
					});
				}

				if (!autoSave) this.openSaveOnboardingModal();
			}),
			catchError(() => {
				this.snackBarService.setSnackBarNotification({
					messageText: 'PLANOGRAM_EDIT.PLANOGRAM_SAVE_ERROR',
					durationSeconds: 0,
					closeButton: true,
					closeActions: [
						{
							action: 'ERROR.TO_SUPPORT',
							callback: (): void => {
								window.$crisp.push(['do', 'chat:open']);
							}
						}
					]
				});

				return EMPTY;
			}),
			finalize(() => {
				this.loader.set(false);
			})
		);
	}

	scrollSelectedProduct(target: EventTarget): void {
		const parent: HTMLElement = Utils.findEventParent(target, 'mat-mdc-row');
		if (!parent) return;

		const { top } = parent.getBoundingClientRect(),
			rowHeight: number = 50,
			layoutPanelHeight: number = 70, // need if when layout rules and auto-layout blocked
			topDiff: number = window.innerHeight - top - rowHeight - layoutPanelHeight;

		if (topDiff < this.productPreview.nativeElement.clientHeight) {
			setTimeout(() => {
				this.productsTable.tableContainerElement.nativeElement.scrollTop +=
					this.productPreview.nativeElement.clientHeight - topDiff;
			}, 0);
		}
	}

	setPanelProductsActive(products: Array<Product>, event?: MouseEvent): void {
		if (!products?.length) return;

		this.activity.clearDragProducts();
		if (event) this.scrollSelectedProduct(event.target);
		this.addToActiveProducts(products, event?.shiftKey, navigator.platform == 'MacIntel' ? event?.metaKey : event?.ctrlKey);
		this.isNeedCloseRightPanel();
	}

	setActiveElementsFromProducts(): void {
		const standProducts: Array<ProductGroup> = PlanogramEditComponent.to1D(
			this.planograms()
                .filter((stand: IPlanogram) => stand.visible !== false)
                .map((stand: IPlanogram) => stand.stand.products)
		);
		const selectById: boolean = this.activity.productsGrouping() !== 'planograms';

		this.activity.activePanelProducts().forEach((product: Product) => {
			standProducts
				.filter((pg: ProductGroup) => selectById ? pg.product.id === product.id : pg.product === product)
				.forEach((pg: ProductGroup) => this.activity.setProductActive(pg, true));
		});
	}

	addToActiveProducts(products: Array<Product>, shiftPressed?: boolean, ctrlPressed?: boolean): void {
		this.activity.setProductActive();

		// if there is no product that was clicked on, then we clear all the selected products and exit
		if (!products?.length) {
			this.activity.clearActivePanelProducts();
			return;
		}

		// if neither ctrl nor shift is pressed - then clear all selected products and add the ones you clicked on and exit
		if (!shiftPressed && !ctrlPressed) {
			this.activity.clearActivePanelProducts();
			this.activity.setActivePanelProducts(products);
		    this.setActiveElementsFromProducts();
			return;
		}

		// if shift is pressed, then clear all selected products (if ctrl is not pressed) and add there the products
		// that are between the last added and the one that was clicked and exit
		if (shiftPressed) {
			const tableProducts: Array<IProductsTableItem> = this.productsTableData.results.dataSource;
			let startIndex: number = tableProducts.findIndex((p: IProductsTableItem) => p.clickable_params.dragData.element[0] === products[0]);
			let endIndex: number = tableProducts.findIndex((p: IProductsTableItem) => {
				return this.activity.activePanelProducts().slice(-1)[0] == p.clickable_params.dragData.element.slice(-1)[0];
			});

			if (endIndex === -1) {
				this.activity.clearActivePanelProducts();
				this.activity.setActivePanelProducts(products);
				this.setActiveElementsFromProducts();
				return;
			}

			if (startIndex > endIndex) [startIndex, endIndex] = [endIndex, startIndex];

			for (let i = startIndex; i <= endIndex; i++) {
				const itemProducts: Array<Product> = tableProducts[i].clickable_params.dragData.element;
				itemProducts.forEach((product: Product) => {
					if (!this.activity.isPanelProductActive(product)) this.activity.addActivePanelProduct(product);

				});
			}

			this.setActiveElementsFromProducts();

			return;
		}

		// if ctrl is pressed, then add to the selected products or remove the group that was clicked from there
		if (ctrlPressed) {
			const allSelected: boolean = products.every((p: Product) => p.selected);
			products.forEach((product: Product) => {
				if (allSelected) {
					this.activity.removeActivePanelProduct(product);
				} else {
					if (!this.activity.isPanelProductActive(product)) this.activity.addActivePanelProduct(product);
				}
			});
			this.setActiveElementsFromProducts();
		}
	}

	static to1D(array) {
		return Array.prototype.concat.apply([], array);
	}

	sortProducts(property: string, order: boolean): void {
		this.sortProductsParams = {
			order: order,
			property: property
		};
		this.rebuildProductsTable(true);
	}

	private async setProductsPercents(refreshPlanograms: Array<IPlanogram> = []): Promise<void> {
		if (!this.planConf?.id) return;

		const { date_from, date_to } = this.filtersService.Filters;
		const refreshStandIds: Set<number> = new Set(refreshPlanograms.map((p: IPlanogram) => p.main_stand));

		await Promise.all(this.planograms().map(async (planogram: IPlanogram) => {
			const products: Array<Product> = planogram.products();
			if (!products.length) return;

			const productIds: Array<number> = products.map((product: Product) => product.id);
			const standId: number = planogram.main_stand;
			const cache: Record<string, any> = this._placePredictCache[standId] || {};

			if (
				!refreshStandIds.has(standId) &&
				Utils.equalLists(Object.keys(cache), productIds.map(String))
			) {
				products.forEach((product: Product) => {
					product.avgSalesPerDay = cache[String(product.id)]?.avg_qty_per_day;
					product.planedPercent = cache[String(product.id)]?.percent;
				});
				return;
			}

			const reportParams = planogram.conf?.reportParams || this.reportParams;
			const response: Record<string, any> = await this.productsService.getProductsPercent(
				Object.assign({}, reportParams || {}, { date_from, date_to }),
				productIds,
				this.planConf.id,
				standId
			);

			this._placePredictCache[standId] = response;
			products.forEach((product: Product) => {
				product.avgSalesPerDay = response[String(product.id)]?.avg_qty_per_day;
				product.planedPercent = response[String(product.id)]?.percent;
			});
		}));

		this.setRealProductPercents();
	}

	setBrandsInfoVisibility() {
		if (this.activity.activeBlocks().length) {
			const block_brands = Utils.to1DArray(
				this.activity.activeBlocks().map((b) =>
					b.products.map((pg) => pg.product.brandsUniqueId)
				)
			);
			this.brandsInfo = this.brandsInfoCopy.filter((brand) =>
				block_brands.includes(brand.id)
			);
		} else {
			this.brandsInfo = [...this.brandsInfoCopy];
		}
	}

	setCategoriesInfoVisibility() {
		if (this.activity.activeBlocks().length) {
			const blockCategories = Utils.to1DArray(
					this.activity.activeBlocks().map((b) =>
						b.products.map((pg) => pg.product.categoriesUniqueId)
					)
				),
				blockBrands = Utils.to1DArray(
					this.activity.activeBlocks().map((b) =>
						b.products.map((pg) => pg.product.brandsUniqueId)
					)
				);
			this.categoriesInfo = this.categoriesInfoCopy.filter((category) =>
				blockCategories.includes(category.id)
			);
			this.categoriesInfo.forEach((item) => {
				if (item.childrens && item.childrens.length) {
					item.childrens = item.childrens.filter((brand) =>
						blockBrands.includes(brand.id)
					);
				}
			});
		} else {
			this.categoriesInfo = Utils.deepCopy(this.categoriesInfoCopy) as any;
		}
	}

	setRealProductPercents() {
		const statisticsPlanograms: Array<IPlanogram> = this.getBrandStatisticsPlanograms();
		if (!statisticsPlanograms.length) return;

		const withoutBrandsId = '-1',
			brandsObj = {},
			categoryObj = {},
			linearMeters: number = statisticsPlanograms
				.reduce((total: number, planogram: IPlanogram) => total + planogram.stand.LinearMeters, 0),
			planogramProducts: Array<ProductGroup> = statisticsPlanograms
				.map((planogram: IPlanogram) => planogram.stand.products)
				.flat(),
			products: Array<Product> = statisticsPlanograms
				.flatMap((planogram: IPlanogram) => planogram.products());

		products
			.forEach((product) => {
				const brandId: string = product.brandsUniqueId || withoutBrandsId,
					categoryId: string = product.categoriesUniqueId || withoutBrandsId,
					productMeters: number = planogramProducts
						.filter((pg: ProductGroup) => pg.product.id === product.id && pg.availability)
						.reduce((total: number, pg: ProductGroup) => total + pg.Width, 0),
					realProductPercent: number = linearMeters ? Utils.round(productMeters / linearMeters * 100, 3) : 0;

				if (!brandsObj.hasOwnProperty(brandId)) {
					brandsObj[brandId] = {
						id: brandId,
						name: product.brandsUniqueName || 'NO_BRAND',
						real: realProductPercent,
						plan: product.planedPercent || 0,
						recom: this.brandsRecommendedPercent[brandId] || 0
					};
				} else {
					brandsObj[brandId].real = Utils.round(brandsObj[brandId].real + realProductPercent, 3);
					brandsObj[brandId].plan = Utils.round(brandsObj[brandId].plan + (product.planedPercent || 0), 3);
				}

				const newBrand = {
					id: brandId,
					name: product.brandsUniqueName || 'NO_BRAND',
					real: realProductPercent,
					plan: product.planedPercent || 0,
					recom: this.categoriesRecommendedPercent[categoryId]?.childrens[brandId] || 0
				};

				if (!categoryObj.hasOwnProperty(categoryId)) {
					categoryObj[categoryId] = {
						id: categoryId,
						name: product.categoriesUniqueName || 'WITHOUT_CATEGORY',
						recom: this.categoriesRecommendedPercent[categoryId]?.recom || 0,
						childrens: [newBrand]
					};
				} else {
					if (categoryObj[categoryId].childrens.every((item) => item.id !== newBrand.id)) {
						categoryObj[categoryId].childrens = [...categoryObj[categoryId].childrens, newBrand];
					} else {
						const children = categoryObj[categoryId].childrens.find((item) => item.id == newBrand.id);
						children.real = Utils.round(children.real + realProductPercent, 3);
						children.plan = Utils.round(children.plan + (product.planedPercent || 0), 3);
					}
				}
			});

		this.brandsInfoCopy = Object.values(brandsObj);
		this.categoriesInfoCopy = Object.values(categoryObj);
		this.brandsInfoCopy.sort((a, b) => a.plan - b.plan);
		this.brandsInfoCopy.forEach((brand) => {
			brand.plan = brand.plan.toFixed(2);
			brand.real = brand.real.toFixed(2);
			if (brand.recom > 0) brand.recom = Utils.round(Number(brand.recom), 2);
			this.brandsRecommendedPercent[brand.id] = brand.recom;
		});
		this.categoriesInfoCopy.forEach((category) => {
			category.real = Utils.sum(category.childrens.map((b) => Number(b.real)));
			category.plan = Utils.sum(category.childrens.map((b) => Number(b.plan)));
			category.recom = Utils.round(category.recom, 2);
			this.categoriesRecommendedPercent[category.id] = {
				recom: category.recom || 0,
				childrens: {}
			};

			if (category.childrens.length) {
				category.childrens.forEach((brand) => {
					brand.plan = Number(brand.plan).toFixed(2);
					brand.real = category.real ? (Number(brand.real) / category.real) * 100 : Number(brand.real);
					brand.real = brand.real.toFixed(2);
					brand.recom = Utils.round(Number(brand.recom), 2);
				});
				category.childrens.forEach((child) => {
					this.categoriesRecommendedPercent[category.id].childrens[child.id] = child.recom || 0;
				});
			}
		});

		this.setBrandsInfoVisibility();
		this.setCategoriesInfoVisibility();
	}

	public hasOpenedDetailedWidgets(): boolean {
		return this.detailedElementWidgetOverlayService.hasOpened();
	}

	private openPlanogramCanvasOverlays(): void {
		const container: HTMLElement = this.standLayoutWorkplace?.nativeElement;

		if (!container || this.planograms().length <= 1) return;

		this.planogramCanvasOverlayService.setContainer(container);
		this.planograms().forEach((planogram: IPlanogram) => this.openPlanogramOverlay(planogram));
	}

	private openPlanogramOverlay(planogram: IPlanogram): void {
		const container: HTMLElement = this.standLayoutWorkplace?.nativeElement;

		if (!container || this.planogramCanvasOverlayService.isOpened(planogram)) return;

		this.planogramCanvasOverlayService.open({
			component: PlanogramCanvasWidgetComponent,
			overlay: this.overlay,
			parentInjector: this.injector,
			hostContainer: container,
			planogram,
			editable: true
		});
	}

    onPlanogramTabClick(planogram: IPlanogram): void {
		if (this.layoutRules) return;

        const previousPlanogram: IPlanogram | null = this.activity.planogram();

        if (
            this.modes.equipmentEditMode()
            && previousPlanogram
            && previousPlanogram !== planogram
            && previousPlanogram.stand.id === planogram.stand.id
        ) {
            this.activity.syncEquipmentStandFromSource(previousPlanogram, planogram);
            this.activity.clearEquipmentDistanceLines();
            this.activity.requestCanvasUpdate([previousPlanogram, planogram]);
        }

        this.activity.setPlanogram(planogram);
        if (this.planogramCanvasOverlayService.isOpened(planogram)) {
            this.overlayZIndexService.setZIndex(this.planogramCanvasOverlayService.opened().find(item => item.planogram.main_stand === planogram.main_stand).overlay);
        } else {
            this.openPlanogramOverlay(planogram);
        }
        this.planogramCanvasOverlayService.scrollToPlanogram(planogram.main_stand);
    }

	arrangeOpenedPlanograms(): void {
		if (this.isArrangePlanogramsDisabled()) return;

		this.detailedElementWidgetOverlayService.closeAll();
		this.planogramCanvasOverlayService.arrangeOpenedPlanograms(this.getDefaultPlanogramMainStandIds());
	}

	protected isArrangePlanogramsDisabled(): boolean {
		if (this.layoutRules || this.planogramCanvasOverlayService.opened().length < 2) return true;

		return this.planogramCanvasOverlayService.isOpenedPlanogramsInDefaultArrangement(this.getDefaultPlanogramMainStandIds());
	}

	protected getArrangePlanogramsTooltipKey(): string {
		if (this.planogramCanvasOverlayService.opened().length < 2) {
			return 'COMMON.PLANOGRAM.ARRANGE_PLANOGRAMS_EXPAND_MORE';
		}

		if (this.planogramCanvasOverlayService.isOpenedPlanogramsInDefaultArrangement(this.getDefaultPlanogramMainStandIds())) {
			return 'COMMON.PLANOGRAM.ARRANGE_PLANOGRAMS_ALREADY_ARRANGED';
		}

		return 'COMMON.PLANOGRAM.ARRANGE_PLANOGRAMS';
	}

	private getDefaultPlanogramMainStandIds(): Array<number> {
		return this.planograms().map((planogram: IPlanogram) => planogram.main_stand);
	}

	updateProductsLayout(params: ILayoutConfigFormParams): void {
		const activeProducts: Array<ProductGroup> = this.activity.activeProducts();
		const updateCompressionParams: Partial<ICompressionParams> = {};
		const productsByStand = new Map<EquipmentBase, Array<ProductGroup>>();

		activeProducts.forEach((activeProduct: ProductGroup) => {
			const stand: EquipmentBase = activeProduct.element.stand;
			const standProducts: Array<ProductGroup> = productsByStand.get(stand) ?? [];

			standProducts.push(activeProduct);
			productsByStand.set(stand, standProducts);
		});

		const allElements: Array<Element> = [];
		const affectedMainStands: Array<number> = [];

		productsByStand.forEach((standActiveProducts: Array<ProductGroup>, stand: EquipmentBase) => {
			const elements: Array<Element> = this.applyProductsLayoutToStand(
				stand,
				standActiveProducts,
				params,
				updateCompressionParams
			);

			allElements.push(...elements);

			const planogram: IPlanogram | undefined = this.findPlanogramByStand(stand);
			if (planogram) affectedMainStands.push(planogram.main_stand);
		});

		if (Object.keys(updateCompressionParams).length) {
			const productIds: Array<number> = Utils.unique(activeProducts.map((pg: ProductGroup) => pg.product.id));
			const planogramsToRefresh: Array<IPlanogram> = this.planograms().filter((planogram: IPlanogram) =>
				planogram.products().some((product: Product) => productIds.includes(product.id))
			);

			planogramsToRefresh.forEach((planogram: IPlanogram) => {
				planogram.products()
					.filter((product: Product) => productIds.includes(product.id))
					.forEach((product: Product) => Object.assign(product, updateCompressionParams));

				const elements: Array<Element> = Utils.unique(
					planogram.stand.products
						.filter((pg: ProductGroup) => productIds.includes(pg.product.id))
						.map((pg: ProductGroup) => pg.element)
				);
				if (!elements.length) return;

				allElements.push(...this.activity.normalizeElements(planogram.stand, elements));
				affectedMainStands.push(planogram.main_stand);
			});

			this.productsService
				.setCompression(updateCompressionParams, { ids: productIds.join(',') })
				.subscribe(() => void this.setProductsPercents(planogramsToRefresh));
		}

		this.activity.refreshActiveProducts();
		this.setRealProductPercents();
		Utils.unique(affectedMainStands).forEach((mainStand: number) => this.historyGenerate(mainStand));
		this.activity.requestCanvasUpdate(this.planograms(), allElements.map((el: Element) => el.id));
	}

	private applyProductsLayoutToStand(
		stand: EquipmentBase,
		standActiveProducts: Array<ProductGroup>,
		params: ILayoutConfigFormParams,
		updateCompressionParams: Partial<ICompressionParams>
	): Array<Element> {
		standActiveProducts.forEach((activeProduct: ProductGroup) => {
			const index: number = stand.products.indexOf(activeProduct),
				standProduct: ProductGroup = stand.products[index];

			if (!standProduct) return;

			const oldFacesQty: number = activeProduct.FacesQty;
			if (params.faces) standProduct.FacesQty = params.faces;
			if (params.depth) standProduct.placementDepth = params.depth;
			if (params.height) standProduct.placementHeight = params.height;
            if (!Utils.isNullOrUndefined(params.angle) && standProduct.angle !== params.angle) {
                standProduct.angle = params.angle;
                // TODO: need apply change element for new begin after rotate
            }
			if (params.hasOwnProperty('packing')) standProduct.selectPacking = params.packing;
			if (params.weight) standProduct.weight = params.weight;

			let next: Array<CompressionType> = standProduct.applied_compressions.slice();
			if (!Utils.isNullOrUndefined(params.enableDepthCompression)) {
				next = next.filter((t: CompressionType) => t !== CompressionType.Depth);
				if (params.enableDepthCompression) next.push(CompressionType.Depth);
			}
			if (!Utils.isNullOrUndefined(params.enableHeightCompression)) {
				next = next.filter((t: CompressionType) => t !== CompressionType.Height);
				if (params.enableHeightCompression) next.push(CompressionType.Height);
			}
			if (!Utils.isNullOrUndefined(params.enableWidthCompression)) {
				next = next.filter((t: CompressionType) => t !== CompressionType.Width);
				if (params.enableWidthCompression) next.push(CompressionType.Width);
			}
			standProduct.applied_compressions = next;

			if (!params.onlyLocalCompression) {
                standProduct.height_compression_coef = null;
                standProduct.width_compression_coef = null;
                standProduct.depth_compression_coef = null;
        
				if (!Utils.isNullOrUndefined(params.height_compression_coef)) {
					updateCompressionParams.height_compression_coef = params.height_compression_coef;
					standProduct.product.height_compression_coef = params.height_compression_coef;
				}

				if (!Utils.isNullOrUndefined(params.width_compression_coef)) {
					updateCompressionParams.width_compression_coef = params.width_compression_coef;
					standProduct.product.width_compression_coef = params.width_compression_coef;
				}

				if (!Utils.isNullOrUndefined(params.depth_compression_coef)) {
					updateCompressionParams.depth_compression_coef = params.depth_compression_coef;
					standProduct.product.depth_compression_coef = params.depth_compression_coef;
				}
			} else {
				standProduct.height_compression_coef = params.height_compression_coef 
                    ?? standProduct.height_compression_coef 
                    ?? standProduct.product.height_compression_coef;
                
                standProduct.width_compression_coef = params.width_compression_coef 
                    ?? standProduct.width_compression_coef 
                    ?? standProduct.product.width_compression_coef;

                standProduct.depth_compression_coef = params.depth_compression_coef 
                    ?? standProduct.depth_compression_coef 
                    ?? standProduct.product.depth_compression_coef;
			}

			if (params.allDepth) {
				standProduct.placementDepth = activeProduct.element.getMaxPlacementDepth(activeProduct);
			}

			standProduct.qty = standProduct.placementDepth * (params.faces || oldFacesQty);
		});

        const elementsToNormalize = Utils.unique(
            standActiveProducts.map((pg: ProductGroup) => pg.element)
        );

		elementsToNormalize.forEach((el) => {
			const groupsToShift = el.products.filter((group) =>
				group.some((pg) => standActiveProducts.includes(pg))
			);
			groupsToShift.forEach((group) =>
				el.shiftProductGroups([group], Utils.to1DArray(group))
			);
		});
		const elements: Array<Element> = this.activity.normalizeElements(stand, elementsToNormalize);

		let dependedProducts: Array<ProductGroup> = [];

		standActiveProducts.forEach(
			(el: ProductGroup) =>
				(dependedProducts = [
					...dependedProducts,
					...Utils.findDependProducts(el, stand.products)
				])
		);
		stand.products
			.sort((a, b) => a.getBoundingY().min - b.getBoundingY().min)
			.forEach((p) => {
				if (dependedProducts.includes(p)) {
					p.begin.y = p.dependProduct
						? p.dependProduct.getBoundingY().max
						: p.element.getBoundingY().max;
				}
			});

		stand.blocks.forEach((b) => {
			const blockProductErrors = b.setProducts(
				Utils.to1DArray(
					stand.blocks.filter((block) => block != b).map((block) => block.products)
				)
			);

			if (blockProductErrors.length) this.activity.showDeleteMessage$.next({ block: b, errors: blockProductErrors });
		});

		return elements;
	}

	blockUpdateConfig(params) {
		const block = this.activity.activeBlocks()[0] || this.activity.selectedBlock();
		if (!block) return;

		const probe: Block = block.copy();
		probe.color = params.color;
		probe.updateConfig(params);

		const intersections = this.canDrawBlock(
			probe,
			block.stand.blocks.filter((b) => b != block)
		);

		if (intersections.length) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'BLOCK_OVER_BLOCKS_ERROR'
			});
			return;
		}

		block.color = params.color;
		block.updateConfig(params);
		this.activity.refreshActiveBlocks();

		const planogram: IPlanogram | undefined = this.findPlanogramByStand(block.stand);
		if (!planogram) return;

		this.activity.normalizeElements(planogram.stand, block.elements);
		this.activity.requestCanvasUpdate([planogram]);
		this.historyGenerate(planogram.main_stand);
	}

	POSTextMaterialConfigUpdate(materialData: POSTextMaterial): void {
		if (this.activity.activePosMaterials().length !== 1) return;
		const material: POSTextMaterial = this.activity.panelPOSMaterial() as POSTextMaterial;
		material.name = materialData.name;
		material.angle = materialData.angle;
		material.color = materialData.color;
		material.fontSize = materialData.fontSize;
		material.width = materialData.width;
		material.height = materialData.height;
		material.backgroundColor = materialData.backgroundColor;
		material.horizontalAlign = materialData.horizontalAlign;
		material.verticalAlign = materialData.verticalAlign;
		this.activity.requestCanvasUpdate().subscribe({
			complete: () => this.activity.setPOSMaterialActive(this.activity.panelPOSMaterial())
		});
		this.activity.historyGenerate$.next();
	}

	POSImageMaterialConfigUpdate(configData: POSImageMaterialConfigData): Observable<CanvasUpdateRequest> {
		if (this.activity.activePosMaterials().length !== 1) return;
        this.POSImageMaterialsUpdate(configData.material).subscribe({
			complete: () => this.activity.setPOSMaterialActive(this.activity.panelPOSMaterial())
		});
		this.POSMaterialsService.update(configData.material.id, configData.dirtyData).subscribe({
			error: () => this.snackBarService.setErrorNotification()
		});
	}

	POSImageMaterialsUpdate(material: POSImageMaterial): Observable<CanvasUpdateRequest> {
        for (const planogram of this.planograms()) {
            planogram.stand.POSMaterials
			.filter((m: POSMaterial) => m instanceof POSImageMaterial && m.id === material.id)
			.forEach((m: POSImageMaterial) => {
				m.name = material.name;
				m.image = material.image;
				m.width = material.width;
				m.height = material.height;
				m.description = material.description;
				m.opacity = material.opacity;
			});

			this.activity.getHistoryState(planogram.main_stand).entries.forEach((item: HistoryInstance) => {
				item.POSMaterials
					.filter((m: POSMaterial) => m instanceof POSImageMaterial && m.id === material.id)
					.forEach((m: POSImageMaterial) => {
						m.name = material.name;
						m.image = material.image;
						m.width = material.width;
						m.height = material.height;
						m.description = material.description;
						m.opacity = material.opacity;
					});
			});
        }

		return this.activity.requestCanvasUpdate([], [], undefined, [PlanogramCanvasLayer.PosMaterials]);
	}

	async openPlanogramGroup(): Promise<void> {
		if (this.saveAble() && this.ignoreOtherSave()) {
			this.closePanel();
			await lastValueFrom(this.performSaveAction());
		}

		this.mixPanelService.trackPage(MixpanelEvents.PlanogramGroupPanelOpen, {
			source: 'planogram-edit',
			mode: 'edit'
		});
		this.phSidePanelService.open(PlanogramGroupsPanelComponent, {
			hasBackdrop: true,
			width: '50%',
			minWidth: '750px',
			data: {
				group: this.planogramGroup,
				planId: this.planConf?.id,
				hideEditLink: true,
				onCloseResult: async (result: { changed: boolean; deleted: boolean, separateId?: number }) => {
                    if (result.separateId) {
                        void this.router.navigate(
                            this.sessionService.getLink(['stand', 'edit', result.separateId]),
                            { queryParams: { planogram: this.planConf.id } }
                        );
                        return;
                    }
					if (result.deleted) {
						await this.router.navigate(this.sessionService.getLink(['stand', 'list']));
						return;
					}

					if (!result.changed) return;

					this.router.routeReuseStrategy.shouldReuseRoute = () => false;
					this.router.onSameUrlNavigation = 'reload';
					await this.router.navigateByUrl(this.router.url);
				}
			}
		});
	}

	public isPOSImageMaterial(material: POSMaterial): material is POSImageMaterial {
		return material instanceof POSImageMaterial;
	}

	public isPOSTextMaterial(material: POSMaterial): material is POSTextMaterial {
		return material instanceof POSTextMaterial;
	}

	redirectStand() {
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		return this.router.navigate(
			this.sessionService.getLink(['stand', 'edit', this.selectedStand]),
			{ relativeTo: this.route, queryParams: { planogram: this.planConf?.id }, queryParamsHandling: 'merge' }
		);
	}

	redirectPlanogram() {
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		return this.router.navigate(
			this.sessionService.getLink(['stand', 'edit', this.route.snapshot.params.id]),
			{ queryParams: { planogram: this.selectedPlanogram } }
		);
	}

	showUploadLayoutModal(targetPlanogram: IPlanogram | null): void {
		if (!targetPlanogram) return;

		const planogram: IPlanogram = targetPlanogram;

		if (planogram.stand.blocks.length) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EXCEL_UPLOAD_BLOCKS_ERROR',
					twoAnswers: true,
					confirmLabel: 'YES',
					cancelLabel: 'NO'
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer) {
					this.activity.setActiveBlocks(planogram.stand.blocks);
					this.activity.deleteItems(planogram)
						.then(() => firstValueFrom(this.activity.requestCanvasUpdate()))
						.then(() => this.uploadExcelModal(planogram));
				}
			});
		} else if (planogram.stand.products.length) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EXCEL_UPLOAD_WARNING_MESSAGE',
					confirmLabel: 'YES',
					cancelLabel: 'NO',
					twoAnswers: true
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer) {
					this.activity.setActiveProducts(planogram.stand.products);
					this.activity.deleteItems(planogram)
						.then(() => firstValueFrom(this.activity.requestCanvasUpdate()))
						.then(() => this.uploadExcelModal(planogram));
				}
			});
		} else {
			this.uploadExcelModal(planogram);
		}
	}

	uploadExcelModal(targetPlanogram?: IPlanogram) {
		const planogram: IPlanogram = targetPlanogram ?? this.planograms()[0];
		if (!planogram) return;

		const dialogRef = this.dialog.open(ExcelToLayoutComponent, {
			width: '600px',
			height: 'fit-content',
			autoFocus: false,
			panelClass: 'upload-excel-modal',
			backdropClass: 'back-drop',
			data: {
				stand: planogram,
				planogram: this.planConf?.id
			}
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (!result) return;
			this.loader.set(true);
			this.productAddPanelOpen.set(false);
			this.productAddTargetPlanogram.set(null);

			planogram.products.set(result.origin_products);
			planogram.conf = {
				...planogram.conf,
				reportParams: this.reportParams,
				productFilters: this.addProductsFilterService.filters,
				products: planogram.products().map((p: Product) => ({
					id: p.id,
					planedPercent: p.planedPercent
				}))
			};
			this.applyExcelPlacementToPlanogram(planogram, result.products);
			this.setRealProductPercents();
			this.activity.setPlanogram(planogram);

			planogram.stand.allElements.forEach((el: Element) => {
				if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) {
					el.normalize(planogram.stand);
				}
			});

			const placementProducts: Array<IProductConf> = this.productsService.getProductsToConf(
				planogram.id,
				planogram.stand.products
			);
			const conf: StandConf = this.planogramsService.getConf(planogram, placementProducts, {
				blocks: [],
				conf: {
					reportParams: this.reportParams,
					productFilters: this.addProductsFilterService.filters,
					products: planogram.products().map((p: Product) => ({
						id: p.id,
						planedPercent: p.planedPercent
					}))
				}
			});

			await this.planogramsService.updatePlacementShelf(planogram.id, conf);
			await this.planogramsService.makeSvg([this.buildMakeSvgConf(planogram)]);

			planogram.date = new Date().toISOString();
			this.syncStandUpdatedAt();
			this.markMasterRuleOutdatedLocally([planogram]);

			this.historyGenerate(planogram.main_stand);
			this.activity.markHistorySaved(planogram.main_stand);

			// reset zoom for correct image capture
			await firstValueFrom(this.activity.requestCanvasUpdate());

			this.loader.set(false);

			this.activity.rebuildProductsTable$.next();
			this.getAbcReport();
		});
	}

	private applyExcelPlacementToPlanogram(planogram: IPlanogram, placementProducts: Array<IProductConf>): void {
		planogram.stand.products = [];
		planogram.stand.setBlocks([]);

		[...placementProducts]
			.sort((a: IProductConf, b: IProductConf) => a.position_y - b.position_y)
			.forEach((placement: IProductConf) => {
				const product: Product | undefined = planogram.products()
					.find((item: Product) => item.id === placement.master_product);
				if (!product) return;

				const element: Element | undefined = planogram.stand.allElements
					.find((item: Element) => item.id === placement.element_id);
				if (!element) return;

				const productGroup: ProductGroup = new ProductGroup({
					product,
					element,
					begin: {
						x: placement.position_x,
						y: placement.position_y,
						z: placement.position_z
					},
					qty: placement.qty,
					rotation: placement.rotation as ProductGroupRotation,
					angle: placement.angle,
					placementHeight: placement.placement_height,
					weight: placement.weight,
					selectPacking: placement.packing,
					height_compression_coef: placement.height_compression_coef,
					width_compression_coef: placement.width_compression_coef,
					depth_compression_coef: placement.depth_compression_coef,
					applied_compressions: placement.applied_compressions,
					dependProduct: Utils.getDependProduct(placement, planogram.stand.products, element),
					display: true,
					detailedRotationPoint: this.sessionService.personalSettings.productRotationPoint
				});

				productGroup.placementDepth = placement.placement_depth || 1;
				if (!productGroup.element) return;

				planogram.stand.products.push(productGroup);
			});

		planogram.stand.availableElements.forEach((element: Element) => {
			if (NORMALIZED_ELEMENTS.includes(element.constructor.name)) {
				element.normalize(planogram.stand);
			}
		});
	}

	async canDeactivate(): Promise<boolean> {
        if (this.saveObserver) {
			this.loader.set(true);
			await lastValueFrom(this.saveObserver, { defaultValue: undefined });
			return true;
		}

		if (!this.activity.hasAnyHistoryChanges() || !this.ignoreOtherSave()) return true;
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(
			ConfirmModalComponent,
			{
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'MUST_SAVE_CHANGES',
					cancelLabel: 'NO',
					confirmLabel: 'YES',
					twoAnswers: true
				}
			}
		);
		const result: IConfirmModalAnswer = await lastValueFrom(dialogRef.afterClosed());
		if (!result.answer && this.planogramFilters.length) {
            const planogramIndex = this.planogramList.findIndex((item) => item.id == this.planograms()[0].main_stand);
            this.planogramFilters = ~planogramIndex ? this.changeIndex(this.planogramList, planogramIndex, 0) : this.planogramList;
		}
		return Boolean(result.answer);
	}

	changeIndex(array, fromIndex, toIndex) {
		const element = array[fromIndex];
		array.splice(fromIndex, 1);
		array.splice(toIndex, 0, element);

		return array;
	}

	static insertProductsOnStand(attrs: AutoLayoutParams) {
		const {
			stands,
			blocks,
			productOptions,
			percentDictionary,
			hierarchy,
			cachedPlacePredict,
			groupProperty,
			plannedBrands,
			plannedBrandsPercent,
			plannedCategories,
			plannedCategoriesPercent,
			hasReceipts,
			personalSettings
		} = attrs;

		return (group, idx) => {
			let group_positions = group.limits,
				target_shelves = [],
				target_hooks = [],
				target_holes = [],
				target_boxes = [];

			const sortShelves = () => {
				return (a, b) => {
					if (Math.abs(Utils.round(b.shapes[0].begin.y - a.shapes[0].begin.y)) < 0.01) {
						return Utils.round(a.shapes[0].begin.x - b.shapes[0].begin.x);
					}
					return Utils.round(b.shapes[0].begin.y - a.shapes[0].begin.y);
				};
			};

			if (blocks.length) {
				const block = blocks[idx];

				if (!block) return;

				target_shelves = block.elements
					.filter((el) => {
						if (['ShelfElement', 'PalletElement'].includes(el.constructor.name)) {
							const baseWithCentralPillar: boolean = el.relatedElements
								.some((e: Element) => e.parent.is(EquipmentElements.CentralPillar));

							return !el.depend?.parent.is(EquipmentElements.CentralPillar) && !baseWithCentralPillar;
						}

						return false;
					})
					.sort(sortShelves());

				target_hooks = block.elements
					.filter((el) => el.constructor.name == 'HookElement')
					.sort(sortShelves());

				target_holes = block.elements
					.filter((el) => el.constructor.name == 'HoleElement')
					.sort(sortShelves());

				target_boxes = block.elements
					.filter((el) => el.constructor.name == 'BoxElement')
					.sort(sortShelves());
			} else {
				target_shelves = stands[0].stand.availableElements
					.filter((i) => {
						if (['ShelfElement', 'PalletElement'].includes(i.constructor.name)) {
							const baseWithCentralPillar: boolean = i.relatedElements
								.some((e: Element) => e.parent.is(EquipmentElements.CentralPillar));
							const shelfOnCentralPillar: boolean = i.depend?.parent.is(EquipmentElements.CentralPillar);

							return !shelfOnCentralPillar && !baseWithCentralPillar && i.shapes[0].inRange(group_positions);
						}

						return false;
					})
					.sort(sortShelves());

				target_boxes = stands[0].stand.availableElements.filter(
					(el) =>
						el.constructor.name == 'BoxElement' &&
						el.shapes[0].inRange(group_positions) &&
						!el.products.length
				).sort(sortShelves());
			}

			if (
				!target_shelves.length &&
				!target_hooks.length &&
				!target_boxes.length &&
				!target_holes.length
			)
				return;

			target_shelves.forEach((s) => {
				if (s.constructor.name == 'ShelfElement') {
					s.setPlacementHeight(stands[0].stand);
				}
			});

			let target_shelves_boundings = Utils.to1DArray(
					target_shelves.map((s) => {
						const elementsOnShelf =
								s.constructor.name == 'ShelfElement'
									? s.getElementsOnShelf.filter((el) =>
										el.shapes.some((s) => s.inRange(group_positions))
									)
									: [],
							obj = s.getBoundingX(true),
							siblings = s.getSiblingElements;

						if (obj.min < group_positions.from) {
							obj.min = group_positions.from;
						}
						if (obj.max > group_positions.to) {
							obj.max = group_positions.to;
						}

						if (elementsOnShelf.length) {
							const shapes = Utils.to1DArray(
								elementsOnShelf.map((el) => el.shapes)
							)
								.filter(
									(el) =>
										el.isPointOn(null, s.getBoundingY().min) ||
										el.isPointOn(null, s.getBoundingY().max)
								)
								.filter((el) => el.inRange(group_positions))
								.map((s) => s.getBoundingX())
								.sort((a, b) => a.min - b.min);

							const shapes_boundings = shapes.map((shape, idx) => {
								if (!idx) {
									return {
										min: obj.min,
										max: shape.min,
										width: Utils.round(shape.min - obj.min),
										shelf: s
									};
								}

								return {
									min: shapes[idx - 1].max,
									max: shape.min,
									width: Utils.round(shape.min - shapes[idx - 1].max),
									shelf: s
								};
							});
							shapes_boundings.push({
								min: shapes[shapes.length - 1].max,
								max: obj.max,
								width: Utils.round(obj.max - shapes[shapes.length - 1].max),
								shelf: s
							});

							return shapes_boundings
								.filter((sh) => sh.width > 0)
								.map((sh) => {
									sh.rightSibling = siblings.find(
										(sib) =>
											Math.abs(Utils.round(sib.shapes[0].getBoundingX().min - sh.max)) < 0.01 &&
											target_shelves.includes(sib) &&
											!sib.isElementOnShelfStart
									);
									sh.leftSibling = siblings.find(
										(sib) =>
											Math.abs(Utils.round(sh.min - sib.shapes[0].getBoundingX().max)) < 0.01 &&
											target_shelves.includes(sib) &&
											!sib.isElementOnShelfEnd
									);
									return sh;
								});
						}

						obj.rightSibling = siblings.find(
							(sib) =>
								Math.abs(Utils.round(sib.shapes[0].getBoundingX().min - obj.max)) < 0.01 &&
								target_shelves.includes(sib) &&
								!sib.isElementOnShelfStart
						);
						obj.leftSibling = siblings.find(
							(sib) =>
								Math.abs(Utils.round(obj.min - sib.shapes[0].getBoundingX().max)) < 0.01 &&
								target_shelves.includes(sib) &&
								!sib.isElementOnShelfEnd
						);
						if (obj.max < obj.min) obj.max = obj.min;
						obj.width = Utils.round(obj.max - obj.min);

						return Object.assign({}, obj, { shelf: s });
					})
				),
				groupMeters = Utils.round(
					target_shelves_boundings.reduce((a, b) => a + Math.abs(b.width), 0)
				),
				groupedProducts = [],
				weightProducts = group.products.filter((product) => product.is_loose);

			if (groupProperty == 'none' && !blocks.length) {
				let totalPercent = 0;

				if (plannedBrands.length) {
					plannedBrands
						.sort((a, b) => plannedBrandsPercent[b] - plannedBrandsPercent[a])
						.forEach((brand) => {
							if (totalPercent >= 100) return;
							const realPercent =
								totalPercent + plannedBrandsPercent[brand] < 100
									? plannedBrandsPercent[brand]
									: 100 - totalPercent;
							groupedProducts.push({
								percent: realPercent,
								maximumWidth: (groupMeters * realPercent) / 100,
								realWidth: 0,
								products: group.products.filter(
									(product: Product) =>
										String(product.brandsUniqueId) == brand && !product.is_loose
								)
							});
							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					if (totalPercent < 100) {
						const otherProducts = Utils.to1DArray(
								groupedProducts.map((g) => g.products)
							),
							restProducts = group.products.filter(
								(product) =>
									!otherProducts.includes(product) && !product.is_loose
							),
							grouped = Utils.groupBy(restProducts, this.autoLayoutGroupMapper['brand'])
								.map((brand) => {
									return {
										percent: productOptions.useSales
											? Utils.round(
												brand.reduce(
													(a, b) => a + percentDictionary[b.id],
													0
												) * 100,
												3
											)
											: Utils.round(
												(brand.length / restProducts.length) *
												(100 - totalPercent),
												3
											),
										products: brand
									};
								})
								.sort((a, b) => b.percent - a.percent),
							restPercent = Utils.round(
								grouped.reduce((a, b) => a + b.percent, 0),
								3
							),
							percentAddition =
								100 - totalPercent > restPercent
									? (100 - totalPercent - restPercent) / grouped.length
									: 0;
						grouped.forEach((brand) => {
							if (totalPercent >= 100) return;
							const realPercent =
								totalPercent + brand.percent + percentAddition < 100
									? brand.percent + percentAddition
									: 100 - totalPercent;
							groupedProducts.push({
								percent: realPercent,
								maximumWidth: (groupMeters * realPercent) / 100,
								realWidth: 0,
								products: brand.products
							});
							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					}
				}
				if (plannedCategories.length) {
					plannedCategories
						.filter((cat) => plannedCategoriesPercent[cat].recom > 0)
						.sort(
							(a, b) =>
								plannedCategoriesPercent[b].recom -
								plannedCategoriesPercent[a].recom
						)
						.forEach((category) => {
							if (totalPercent >= 100) return;
							const categoryProducts = group.products.filter(
									(product: Product) =>
										product.categoriesUniqueId == category && !product.is_loose
								),
								realPercent =
									totalPercent + plannedCategoriesPercent[category].recom < 100
										? plannedCategoriesPercent[category].recom
										: 100 - totalPercent;

							const categoryGroups = [];
							let categoryPercent = 0;
							const brands = Utils.groupBy(categoryProducts, this.autoLayoutGroupMapper['brand'])
								.map((brand) => {
									return {
										percent: productOptions.useSales
											? realPercent *
											Utils.round(
												brand.reduce(
													(a, b) => a + percentDictionary[b.id],
													0
												),
												3
											)
											: realPercent *
											Utils.round(brand.length / categoryProducts.length, 3),
										plannedPercent: plannedCategories.includes(
											String(brand[0].categoriesUniqueId)
										)
											? (realPercent *
												(plannedCategoriesPercent[brand[0].categoriesUniqueId]
													.childrens[brand[0].brandsUniqueId] || 0)) /
											100
											: 0,
										products: brand
									};
								})
								.sort(
									(a, b) =>
										b.plannedPercent - a.plannedPercent || b.percent - a.percent
								);

							let totalBrandsPercent = Utils.round(
									brands.reduce(
										(a, b) => a + (b.plannedPercent || b.percent),
										0
									),
									3
								),
								brandPercentAddition =
									totalBrandsPercent < realPercent
										? (realPercent - totalBrandsPercent) /
										brands.filter((b) => !b.plannedPercent).length
										: 0;

							brands.forEach((brand) => {
								if (categoryPercent >= realPercent) return;
								let brandPercent =
									categoryPercent +
									(brand.plannedPercent ||
										brand.percent + brandPercentAddition) <
									realPercent
										? brand.plannedPercent ||
										brand.percent + brandPercentAddition
										: realPercent - categoryPercent;
								categoryGroups.push({
									percent: brandPercent,
									maximumWidth: (groupMeters * brandPercent) / 100,
									realWidth: 0,
									products: brand.products
								});
								categoryPercent = Utils.round(
									categoryPercent + brandPercent,
									3
								);
							});

							const otherProducts = Utils.to1DArray(
									categoryGroups.map((g) => g.products)
								),
								restProducts = categoryProducts.filter(
									(product) =>
										!otherProducts.includes(product) && !product.is_loose
								);

							if (restProducts.length) {
								categoryGroups.push({
									percent: realPercent - categoryPercent,
									maximumWidth:
										(groupMeters * (realPercent - categoryPercent)) / 100,
									realWidth: 0,
									products: restProducts
								});
							}

							groupedProducts = groupedProducts.concat(categoryGroups);

							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					if (totalPercent < 100) {
						const otherProducts = Utils.to1DArray(
								groupedProducts.map((g) => g.products)
							),
							restProducts = group.products.filter(
								(product) =>
									!otherProducts.includes(product) && !product.is_loose
							),
							grouped = Utils.groupBy(restProducts, this.autoLayoutGroupMapper['category'])
								.map((group) => {
									return {
										percent: Utils.round(
											group.reduce((a, b) => a + percentDictionary[b.id], 0) *
											100,
											3
										),
										products: group
									};
								})
								.sort((a, b) => b.percent - a.percent),
							restPercent = Utils.round(
								grouped.reduce((a, b) => a + b.percent, 0),
								3
							),
							percentAddition =
								100 - totalPercent > restPercent
									? (100 - totalPercent - restPercent) / grouped.length
									: 0;
						grouped.forEach((group) => {
							if (totalPercent >= 100) return;
							const realPercent =
								totalPercent + group.percent + percentAddition < 100
									? group.percent + percentAddition
									: 100 - totalPercent;

							const categoryGroups = [];
							let categoryPercent = 0;
							const brands = Utils.groupBy(group.products, this.autoLayoutGroupMapper['brand'])
								.map((brand: Array<Product>) => {
									return {
										percent: productOptions.useSales
											? realPercent *
											Utils.round(
												brand.reduce(
													(a, b) => a + percentDictionary[b.id],
													0
												),
												3
											)
											: realPercent *
											Utils.round(brand.length / group.products.length, 3),
										plannedPercent: plannedCategories.includes(
											String(brand[0].categoriesUniqueId)
										)
											? (realPercent *
												(plannedCategoriesPercent[brand[0].categoriesUniqueId]
													.childrens[brand[0].brandsUniqueId] || 0)) /
											100
											: 0,
										products: brand
									};
								})
								.sort(
									(a, b) =>
										b.plannedPercent - a.plannedPercent || b.percent - a.percent
								);

							let totalBrandsPercent = Utils.round(
									brands.reduce(
										(a, b) => a + (b.plannedPercent || b.percent),
										0
									),
									3
								),
								brandPercentAddition =
									totalBrandsPercent < realPercent
										? (realPercent - totalBrandsPercent) /
										brands.filter((b) => !b.plannedPercent).length
										: 0;
							brands.forEach((brand) => {
								if (categoryPercent >= realPercent) return;
								const brandPercent =
									categoryPercent +
									(brand.plannedPercent ||
										brand.percent + brandPercentAddition) <
									realPercent
										? brand.plannedPercent ||
										brand.percent + brandPercentAddition
										: realPercent - categoryPercent;
								categoryGroups.push({
									percent: brandPercent,
									maximumWidth: (groupMeters * brandPercent) / 100,
									realWidth: 0,
									products: brand.products
								});
								categoryPercent = Utils.round(
									categoryPercent + brandPercent,
									3
								);
							});

							const otherProducts = Utils.to1DArray(
									categoryGroups.map((g) => g.products)
								),
								restProducts = group.products.filter(
									(product) => !otherProducts.includes(product)
								);

							if (restProducts.length) {
								categoryGroups.push({
									percent: realPercent - categoryPercent,
									maximumWidth:
										(groupMeters * (realPercent - categoryPercent)) / 100,
									realWidth: 0,
									products: restProducts
								});
							}

							groupedProducts = groupedProducts.concat(categoryGroups);

							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					}
				}
				if (totalPercent < 100) {
					const otherProducts = Utils.to1DArray(
						groupedProducts.map((g) => g.products)
					);
					groupedProducts.push({
						percent: 100 - totalPercent,
						maximumWidth: (groupMeters * (100 - totalPercent)) / 100,
						realWidth: 0,
						products: group.products.filter(
							(product) => !otherProducts.includes(product) && !product.is_loose
						)
					});
				}
			} else if (groupProperty == 'category' && !blocks.length) {
				let groupPercent = 0,
					groupProducts = group.products.filter((product) => !product.is_loose);

				const brands = Utils.groupBy(groupProducts, PlanogramEditComponent.autoLayoutGroupMapper['brand'])
					.map((brand) => {
						return {
							percent: productOptions.useSales
								? Utils.round(
									brand.reduce((a, b) => a + percentDictionary[b.id], 0) *
									100,
									3
								)
								: Utils.round((brand.length / groupProducts.length) * 100, 3),
							plannedPercent: plannedCategories.includes(
								String(brand[0].categoriesUniqueId)
							)
								? plannedCategoriesPercent[brand[0].categoriesUniqueId].childrens[
								brand[0].brandsUniqueId
								] || 0
								: 0,
							products: brand
						};
					})
					.sort(
						(a, b) =>
							b.plannedPercent - a.plannedPercent || b.percent - a.percent
					);

				let totalBrandsPercent = Utils.round(
						brands.reduce((a, b) => a + (b.plannedPercent || b.percent), 0),
						3
					),
					brandPercentAddition =
						totalBrandsPercent < 100
							? (100 - totalBrandsPercent) /
							brands.filter((b) => !b.plannedPercent).length
							: 0;

				brands.forEach((brand) => {
					if (groupPercent >= 100) return;
					let brandPercent =
						groupPercent +
						(brand.plannedPercent || brand.percent + brandPercentAddition) <
						100
							? brand.plannedPercent || brand.percent + brandPercentAddition
							: 100 - groupPercent;
					if (brandPercent == 0) return;
					groupedProducts.push({
						percent: brandPercent,
						maximumWidth: (groupMeters * brandPercent) / 100,
						realWidth: 0,
						products: brand.products
					});
					groupPercent = Utils.round(groupPercent + brandPercent, 3);
				});

				if (groupPercent < 100) {
					const otherProducts = Utils.to1DArray(
						groupedProducts.map((g) => g.products)
					);
					groupedProducts.push({
						percent: 100 - groupPercent,
						maximumWidth: (groupMeters * (100 - groupPercent)) / 100,
						realWidth: 0,
						products: group.products.filter(
							(product) => !otherProducts.includes(product) && !product.is_loose
						)
					});
				}
			} else {
				groupedProducts.push({
					percent: 100,
					maximumWidth: groupMeters,
					realWidth: 0,
					products: group.products.filter((product) => !product.is_loose)
				});
			}

			let groupAccordance = {},
				pg_groups: any = groupedProducts.map((group) => {
					const recommendPercent = group.products.reduce((t, p) => t + percentDictionary[p.id], 0) * 100,
						percentMultiplier = recommendPercent ? group.percent / recommendPercent : 0;

					return Utils.shuffle<Product>(group.products)
						.filter(
							(product) =>
								product.width > 0 &&
								product.height > 0 &&
								product.depth > 0 &&
								product.width < Utils.round(group_positions.to - group_positions.from)
						)
						.map((product, idx, list) => {
							const availableWidth: number = productOptions.useSales && percentMultiplier
									? groupMeters * percentDictionary[product.id] * percentMultiplier
									: group.maximumWidth / list.length,
								facesQty: number = Math.floor(Utils.round(availableWidth / product.width)),
								realFaces: number = productOptions.maxFacesQty && facesQty > productOptions.maxFacesQty
									? productOptions.maxFacesQty
									: facesQty;

							group.realWidth = Utils.round(group.realWidth + realFaces * product.width);

							groupAccordance[product.id] = group;

							return {
								id: product.id,
								qty: realFaces,
								product: product
							};
						});
				});

			pg_groups = Utils.to1DArray(pg_groups).map((p) => {
				const product = p.product,
					pg = new ProductGroup({
						product,
						element: target_shelves[0] || target_hooks[0] || target_holes[0],
						begin: { x: 0, y: 0, z: 0 },
						applied_compressions: product.applied_compressions
					});

				pg.FacesQty = p.qty;
				return pg;
			});

			let weight_groups = Utils.to1DArray(weightProducts);

			if (target_hooks.length) {
				let targetProducts = Utils.to1DArray(groupedProducts.map((g) => g.products || []));

				switch (productOptions.sortBy) {
					case 'ItemHeight': targetProducts.sort(Utils.sortBy('height', productOptions.sortType)); break;
					case 'price': targetProducts.sort(Utils.sortBy('avgPrice', productOptions.sortType)); break;
					default: targetProducts.sort((a, b) => hierarchy.findIndex((h) => a.id.toString() == h) - hierarchy.findIndex((h) => b.id.toString() == h)); break;
				}

				targetProducts
					.forEach((product, idx) => {
						if (target_hooks.length <= idx) return;
						const pg = new ProductGroup({
							product,
							element: target_hooks[idx],
							begin: { x: 0, y: 0, z: 0 },
							applied_compressions: product.applied_compressions
						});
						if (!~personalSettings.productDepthHook) {
							pg.placementDepth = pg.element.getMaxPlacementDepth(pg);
						}
						pg.FacesQty = 1;
						stands[0].stand.products.push(pg);
					});
			}

			if (target_holes.length) {
				const targetProducts = Utils.to1DArray(groupedProducts.map((g) => g.products || []));

				switch (productOptions.sortBy) {
					case 'ItemHeight': targetProducts.sort(Utils.sortBy('height', productOptions.sortType)); break;
					case 'price': targetProducts.sort(Utils.sortBy('avgPrice', productOptions.sortType)); break;
					default: targetProducts.sort((a, b) => hierarchy.findIndex((h) => a.id.toString() == h) - hierarchy.findIndex((h) => b.id.toString() == h)); break;
				}

				targetProducts
					.forEach((product, idx) => {
						if (target_holes.length <= idx) return;
						const pg = new ProductGroup({
							product,
							element: target_holes[idx],
							begin: { x: 0, y: 0, z: 0 },
							applied_compressions: product.applied_compressions
						});
						pg.FacesQty = 1;
						stands[0].stand.products.push(pg);
					});
			}

			if (target_boxes.length && weight_groups.length) {
				weight_groups
					.sort(
						(a, b) =>
							hierarchy.findIndex((h) => a.id.toString() == h) -
							hierarchy.findIndex((h) => b.id.toString() == h)
					)
					.forEach((p, idx) => {
						if (target_boxes.length <= idx) return;
						const pg = new ProductGroup({
							product: p,
							element: target_boxes[idx],
							begin: { x: 0, y: 0, z: 0 },
							applied_compressions: p.applied_compressions
						});
						pg.FacesQty = 1;
						stands[0].stand.products.push(pg);
					});
			}

			PlanogramEditComponent.checkAutolayoutFreeSpace(
				pg_groups,
				groupMeters,
				groupAccordance,
				hierarchy,
				productOptions.maxFacesQty
			);

			if (hasReceipts) {
				if (productOptions.sortBy == 'golden_shelf') {
					pg_groups.sort(
						(a, b) =>
							cachedPlacePredict[b.product.id].turnover -
							cachedPlacePredict[a.product.id].turnover
					);
					target_shelves_boundings.sort((a, b) => {
						const goldenA = GoldenShelf.isPointOn(a.shelf.shapes[0].begin.y),
							goldenB = GoldenShelf.isPointOn(b.shelf.shapes[0].begin.y);

						return goldenA && !goldenB ? -1 : !goldenA && goldenB ? 1 : 0;
					});
					/*} else if (['ItemHeight', 'price', 'none'].includes(productOptions.sort_by) && groupProperty !== 'none') {
						pg_groups.sort(Utils.sortBy(productOptions.sort_by, productOptions.sort_type));*/
				} else {
					pg_groups = Utils.shuffle(pg_groups);
				}
			} else {
				pg_groups.sort(Utils.sortBy('ItemHeight'));
			}

			if (!target_shelves_boundings.length) return;

			let shelf_idx = 0,
				{ min: from_x } = target_shelves_boundings[0];

			const getMaxSiblingPoint = (idx) => {
				if (target_shelves_boundings[idx].rightSibling) {
					const sibIdx = target_shelves_boundings.findIndex(
						(t) => t.shelf == target_shelves_boundings[idx].rightSibling
					);
					if (sibIdx == -1) {
						return target_shelves_boundings[idx].max;
					}
					return getMaxSiblingPoint(sibIdx);
				}
				return target_shelves_boundings[idx].max;
			};

			const getMinSiblingPoint = (idx) => {
				if (target_shelves_boundings[idx].leftSibling) {
					const sibIdx = target_shelves_boundings.findIndex(
						(t) => t.shelf == target_shelves_boundings[idx].leftSibling
					);
					if (sibIdx == -1) {
						return target_shelves_boundings[idx].max;
					}
					return getMinSiblingPoint(sibIdx);
				}
				return target_shelves_boundings[idx].min;
			};

			pg_groups.forEach((pg) => {
				if (pg.qty == 0) return;
				let target_shelf = target_shelves_boundings[shelf_idx].shelf,
					pg_copy = pg.copy();

				pg_copy.Element = target_shelf;

				while (
					shelf_idx < target_shelves_boundings.length - 1 &&
					Utils.round(pg.Width + from_x) > getMaxSiblingPoint(shelf_idx)
					) {
					pg_copy = pg.copy();
					pg_copy.Element = target_shelf;

					let free_space = Utils.round(getMaxSiblingPoint(shelf_idx) - from_x),
						free_faces_qty = Math.floor(Utils.round(free_space / pg.ItemWidth));

					if (free_faces_qty) {
						const sliced_pg = pg.copy();

						if (!target_shelf) break;

						sliced_pg.Element = target_shelf;
						sliced_pg.begin.x = from_x;
						sliced_pg.begin.y = target_shelf.getBoundingY().max;
						sliced_pg.begin.z = target_shelf.getBoundingZ().max;

						if (
							productOptions.checkSizes &&
							target_shelf.constructor.name == 'ShelfElement' &&
							target_shelf.hasShelfIntersections(sliced_pg, from_x)
						)
							break;

						if (target_shelf.products.some(pr => pr.some(p => p.product.id == sliced_pg.product.id))) break;

						sliced_pg.placementDepth = target_shelf.getMaxPlacementDepth(sliced_pg, true);

						if (!sliced_pg.placementDepth || isNaN(sliced_pg.placementDepth)) break;

						if (!!~personalSettings.productDepthShelf) sliced_pg.placementDepth = 1;

						sliced_pg.FacesQty = blocks.length
							? free_faces_qty
							: Math.abs(free_faces_qty);
						sliced_pg.element = target_shelf;

						stands[0].stand.products.push(sliced_pg);

						pg.FacesQty -= free_faces_qty;

						while (
							Utils.round(sliced_pg.begin.x + sliced_pg.Width) >
							target_shelves_boundings[shelf_idx].max
							) {
							++shelf_idx;
							target_shelf = target_shelves_boundings[shelf_idx].shelf;
						}
						from_x = Utils.round(sliced_pg.begin.x + sliced_pg.Width);
					}

					if (shelf_idx != target_shelves_boundings.length - 1) {
						++shelf_idx;
						target_shelf = target_shelves_boundings[shelf_idx].shelf;
						from_x = target_shelves_boundings[shelf_idx].min;
					}
				}

				pg_copy = pg.copy();
				pg_copy.Element = target_shelf;
				pg_copy.begin.x = from_x;
				pg_copy.begin.y = target_shelf.getBoundingY().max;
				pg_copy.begin.z = target_shelf.getBoundingZ().max;

				if (
					productOptions.checkSizes &&
					target_shelf.constructor.name == 'ShelfElement' &&
					target_shelf.hasShelfIntersections(pg_copy, from_x)
				)
					return;

				if (target_shelf.products.some(pr => pr.some(p => p.product.id == pg_copy.product.id))) return;

				let pg_faces = pg.FacesQty;

				const shelf_max_point = getMaxSiblingPoint(shelf_idx);

				if (Math.abs(Utils.round(shelf_max_point - from_x)) < pg.Width) {
					pg_faces = Math.floor(
						Math.abs(Utils.round((shelf_max_point - from_x) / pg.ItemWidth))
					);
				}

				if (!pg_faces) return;
				if (!target_shelf) return;

				pg.Element = target_shelf;

				pg.begin.x = from_x;
				pg.begin.y = target_shelf.getBoundingY().max;
				pg.begin.z = target_shelf.getBoundingZ().max;
				pg.placementDepth = target_shelf.getMaxPlacementDepth(pg, true);

				if (!pg.placementDepth || isNaN(pg.placementDepth)) return;

				if (!!~personalSettings.productDepthShelf) pg.placementDepth = 1;

				pg.FacesQty = Math.abs(pg_faces);

				if (!from_x) {
					pg.begin.x = target_shelves_boundings[shelf_idx].min;
					from_x = Utils.round(from_x + pg.begin.x + pg.Width);
				} else if (
					from_x &&
					shelf_idx &&
					from_x == target_shelves_boundings[shelf_idx].min &&
					!blocks.length &&
					target_shelves_boundings[shelf_idx].leftSibling
				) {
					pg.begin.x = Utils.max(
						target_shelves_boundings[shelf_idx].leftSibling.products.map(
							(p) => p[0].getBoundingX().max
						)
					);
					from_x = Utils.round(from_x + pg.Width);
				} else {
					pg.begin.x = from_x;
					from_x = Utils.round(from_x + pg.Width);
				}

				while (
					from_x > target_shelves_boundings[shelf_idx].max &&
					shelf_idx < target_shelves_boundings.length - 1
					) {
					++shelf_idx;
					target_shelf = target_shelves_boundings[shelf_idx].shelf;
					if (!target_shelves_boundings[shelf_idx].leftSibling) {
						from_x = target_shelves_boundings[shelf_idx].min;
					}
				}

				stands[0].stand.products.push(pg);
			});

			groupedProducts.forEach((group) => {
				const ids = group.products.map((p) => p.article);
				group.realWidth = Utils.round(
					stands[0].stand.products.reduce((t, p) => {
						if (ids.includes(p.product.article)) {
							return Utils.round(t + p.Width);
						} else {
							return t;
						}
					}, 0)
				);
			});

			if (!blocks.length) {
				// перерахунок вільного місця на полицях
				const last_shelf_occupied_space =
						Utils.round(from_x - target_shelves_boundings[shelf_idx].min),
					empty_shelves = target_shelves_boundings.slice(
						shelf_idx,
						target_shelves_boundings.length
					),
					empty_meters =
						Utils.round(
							empty_shelves.reduce((a, b) => a + Math.abs(b.width), 0) - last_shelf_occupied_space
						),
					productsIds = stands[0].stand.products
						.sort((a, b) => b.plannedPercent - a.plannedPercent)
						.map((p) => p.product.id.toString());

				// визначення невикладених товарів та перерахунок фейсів для кожного з них
				let unlaid_pg_groups = pg_groups.filter(
					(pg) => !productsIds.includes(pg.product.id.toString())
				);
				unlaid_pg_groups.forEach((pg) => {
					const facesQty = productOptions.useSales
						? Math.floor(Utils.round((empty_meters * percentDictionary[pg.product.id]) / pg.product.width)) || 1
						: Math.floor(Utils.round(empty_meters / unlaid_pg_groups.length / pg.product.width)) || 1;
					pg.FacesQty =
						productOptions.maxFacesQty &&
						facesQty > productOptions.maxFacesQty
							? productOptions.maxFacesQty
							: facesQty;
				});

				PlanogramEditComponent.checkAutolayoutFreeSpace(
					unlaid_pg_groups,
					empty_meters,
					groupAccordance,
					hierarchy,
					productOptions.maxFacesQty
				);

				// докладання невиставленого товару на пусті полиці
				target_shelves_boundings.forEach((b, idx) => {
					const siblingMaxPoint = getMaxSiblingPoint(idx),
						siblingMinPoint = getMinSiblingPoint(idx),
						siblingProducts = [
							...b.shelf.products,
							...b.shelf.getSiblings.reduce(
								(p, s) => [...p, ...s.products],
								[]
							)
						].filter(
							(p) =>
								siblingMinPoint <= p[0].getBoundingX().min &&
								p[0].getBoundingX().max <= siblingMaxPoint
						);
					let productsMax = siblingProducts.length
							? Utils.max(siblingProducts.map((p) => p[0].getBoundingX().max))
							: b.min,
						diff = Utils.round(b.max - productsMax),
						min = unlaid_pg_groups.length
							? Math.min.apply(
								null,
								unlaid_pg_groups.map((pg) => pg.Width)
							)
							: 0,
						max_iterations = 10,
						iteration_count = 0,
						unlaid_hierarchy = hierarchy.filter(
							(p) => !productsIds.includes(p)
						);

					if (productsMax > b.max) return;
					while (diff > min) {
						iteration_count++;

						for (let product_id of unlaid_hierarchy) {
							const pg = unlaid_pg_groups.find(
								(pg) => pg.product.id == product_id
							);

							if (!pg) continue;

							const group = groupAccordance[pg.product.id],
								groupDiff = Utils.round(group.maximumWidth - group.realWidth);

							if (Utils.round(pg.ItemWidth * pg.FacesQty) > groupDiff) continue;

							let pg_faces = pg.FacesQty;
							if (Math.abs(Utils.round(siblingMaxPoint - productsMax)) < pg.Width) {
								pg_faces = Math.floor(
									Math.abs(Utils.round((siblingMaxPoint - productsMax) / pg.ItemWidth))
								);
							}
							if (!pg_faces) return;

							pg.begin.x = productsMax;
							pg.begin.y = b.shelf.getBoundingY().max;
							pg.begin.z = b.shelf.getBoundingZ().max;
							pg.placementDepth = b.shelf.getMaxPlacementDepth(pg, true);
							pg.element = b.shelf;

							if (!pg.placementDepth || isNaN(pg.placementDepth)) return;

							if (!!~personalSettings.productDepthShelf) pg.placementDepth = 1;

							pg.FacesQty = Math.abs(pg_faces);

							if (
								productOptions.checkSizes &&
								b.shelf.constructor.name == 'ShelfElement' &&
								b.shelf.hasShelfIntersections(pg)
							)
								continue;

							if (pg.getBoundingX().max > siblingMaxPoint) continue;

							stands[0].stand.products.push(pg);
							unlaid_pg_groups = unlaid_pg_groups.filter(
								(p) => p.product.id !== pg.product.id
							);
							productsIds.push(product_id);
							productsMax = Utils.round(productsMax + pg.Width);
							diff = Utils.round(diff - pg.Width);
							group.realWidth = Utils.round(group.realWidth + pg.Width);

							if (diff > min) continue;
							break;
						}

						if (iteration_count === max_iterations) break;
					}
				});
				// додавання фейсів товарам, якщо на полиці залишилось вільне місце
				target_shelves_boundings.forEach((b, idx) => {
					if (b.leftSibling) return;
					const siblingMaxPoint = getMaxSiblingPoint(idx),
						siblingMinPoint = getMinSiblingPoint(idx),
						siblingProducts = [
							...b.shelf.products,
							...b.shelf.getSiblings.reduce(
								(p, s) => [...p, ...s.products],
								[]
							)
						].filter(
							(p) =>
								siblingMinPoint <= p[0].getBoundingX().min &&
								p[0].getBoundingX().max <= siblingMaxPoint
						);
					let productsWidth = siblingProducts.length
							? siblingProducts.reduce((a, b) => a + b[0].Width, 0)
							: 0,
						diff = Utils.round(siblingMaxPoint - siblingMinPoint - productsWidth),
						min = siblingProducts.length
							? Math.min.apply(
								null,
								b.shelf.products.map((i) => i[0].ItemWidth)
							)
							: 0,
						max_iterations = 10,
						iteration_count = 0;

					while (diff > min) {
						iteration_count++;

						for (let product_id of hierarchy) {
							const product = siblingProducts.find(
								(pg) => pg[0].product.id == product_id
							);

							if (!product) continue;

							const group = groupAccordance[product[0].product.id],
								groupDiff = Utils.round(group.maximumWidth - group.realWidth);

							if (product[0].ItemWidth > diff) continue;

							if (product[0].ItemWidth > groupDiff) continue;

							if (
								productOptions.maxFacesQty &&
								product[0].FacesQty >= productOptions.maxFacesQty
							)
								continue;

							product[0].FacesQty += 1;
							diff = Utils.round(diff - product[0].ItemWidth);
							group.realWidth = Utils.round(group.realWidth + product[0].ItemWidth);

							if (diff > min) continue;
							break;
						}

						if (iteration_count === max_iterations) break;
					}
				});
			}

			if (!['none', 'golden_shelf'].includes(productOptions.sortBy)) {
				PlanogramEditComponent.updateProductsPosition(
					target_shelves_boundings,
					productOptions,
					productOptions.sortBy
				);
			}
		};
	}

	static updateProductsPosition(boundings, productOptions: IAutoLayoutParams, type) {
		const sorted_groups =
			PlanogramEditComponent.getSortedGroups(boundings);

		sorted_groups.forEach((group) => {
			const min_x = Math.min.apply(
				null,
				group.map((pg) => pg.begin.x)
			);
			let products;
			switch (type) {
				case 'ItemHeight':
					const sortedProducts = group.sort(
							Utils.sortBy(productOptions.sortBy, productOptions.sortType)
						),
						groupedProducts = Utils.groupBy(sortedProducts, type, false);

					groupedProducts.forEach((g) => {
						if (products) {
							products = [...products, ...Utils.shuffle(g)];
						} else {
							products = [...Utils.shuffle(g)];
						}
					});
					break;
				case 'price':
					const priceSortedProducts = group.sort(
							Utils.sortBy('avgPrice', productOptions.sortType)
						),
						priceGroupedProducts = Utils.groupBy(priceSortedProducts, type, false);

					priceGroupedProducts.forEach((g) => {
						if (products) {
							products = [...products, ...Utils.shuffle(g)];
						} else {
							products = [...Utils.shuffle(g)];
						}
					});
					break;
				case 'shuffle':
					products = Utils.shuffle(group);
					break;
			}
			products.reduce((a, b) => {
				b.begin.x = a;
				return b.getBoundingX().max;
			}, min_x);
		});
	}

	static getSortedGroups(boundings): Array<Array<ProductGroup>> {
		const sibling_boundings = [];
		let sibling_boundings_index = 0;
		boundings.forEach((b) => {
			if (!sibling_boundings[sibling_boundings_index]) {
				sibling_boundings.push({
					min: b.min,
					max: b.max,
					shelves: []
				});
			}
			sibling_boundings[sibling_boundings_index].shelves.push(b.shelf);
			if (!b.rightSibling) {
				sibling_boundings[sibling_boundings_index].max = b.max;
				sibling_boundings_index++;
			}
		});
		const sorted_groups = [];
		sibling_boundings.forEach((b, idx) => {
			if (!sorted_groups[idx]) sorted_groups[idx] = [];
			b.shelves.forEach((s) => {
				sorted_groups[idx].push(
					...s.products
						.filter(
							(pg) =>
								pg[0].getBoundingX().min >= b.min - EPS_3 &&
								pg[0].getBoundingX().max <= b.max + EPS_3
						)
						.map((p) => p[0])
				);
			});
		});
		return sorted_groups.filter((g) => g.length);
	}

	async autoLayout(data?: IAutoLayoutParams) {
		this.blockAutolayout = true;
		this.loader.set(true);
        const planogram: IPlanogram | undefined = this.resolveActivePlanogram() ?? this.activity.planogram();

		if (!planogram) {
			this.loader.set(false);
			this.blockAutolayout = false;
			return;
		}

		if (planogram.stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'AUTO_LAYOUT_HANG_BAR_WARNING'
			});
		}

		if (planogram.stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'AUTO_LAYOUT_ISLAND_RACK_WARNING'
			});
		}

		this.mixPanelService.trackPage(MixpanelEvents.AutoLayout);

		let selectedBlocks: Array<Block> = this.activity.activeBlocks()
			.filter((block: Block) => planogram.stand.blocks.includes(block));

		if (this.activity.selectedBlock() && planogram.stand.blocks.includes(this.activity.selectedBlock())) {
			selectedBlocks = [this.activity.selectedBlock()];
		}

		if (!selectedBlocks.length) selectedBlocks = planogram.stand.blocks;

		let isDateChanged: boolean = false;
		if (data) {
			if (data.useSales) {
				this.reportParams = {
					qty: data.coefficientSalesQty,
					profit: data.coefficientProfit,
					receipts_qty: data.coefficientReceiptsQty,
					turnover: data.coefficientTurnover
				};
				isDateChanged = !this.cachedAutolayoutParams.period || !Utils.equalObjects(data.period, this.cachedAutolayoutParams.period);
			}
			Utils.assignObject(this.cachedAutolayoutParams, data);
			this.cachedAutolayoutParams = { ...this.cachedAutolayoutParams };
		}

		const percentDictionary: any = this.cachedReportData || {},
			productOptions: IAutoLayoutParams = this.cachedAutolayoutParams,
			productsIds: Array<number> = planogram.products().map((product: Product) => product.id),
			reportParams = Object.assign({}, this.reportParams, { products: productsIds.join(',') }),
			finalProducts: Array<{ products: Array<Product>, width: number, limits?: Range }> = [];

		if (!Utils.equalObjects(this.cachedReportParams, reportParams) || isDateChanged) {
			const { date_from, date_to } = this.filtersService.Filters,
				response: any = await this.productsService.getProductsPercent(
					Object.assign({}, this.reportParams || {}, { date_from, date_to }),
					productsIds,
					this.planConf?.id,
					planogram.main_stand
				);

			for (let key in response) {
				if (response.hasOwnProperty(key)) percentDictionary[key] = (response[key].percent || 0) / 100;
			}

			this._placePredictCache[planogram.main_stand] = response;
			this.cachedPlacePredict = response;
			this.cachedReportData = percentDictionary;
			this.cachedReportParams = reportParams;
		}

		const hierarchy: Array<string> = Object.keys(percentDictionary)
				.sort((a, b) => percentDictionary[b] - percentDictionary[a]),
			isBrandsPlanned: boolean = !this.brandsPanelDataGrouped &&
				Object.values(this.brandsRecommendedPercent).some((el: number) => el > 0),
			plannedBrands = isBrandsPlanned
				? Object.keys(this.brandsRecommendedPercent)
					.filter((key: string) => this.brandsRecommendedPercent[key] > 0)
				: [],
			isCategoriesPlanned: boolean = this.brandsPanelDataGrouped &&
				Object.values(this.categoriesRecommendedPercent)
					.some((el: any) => el.recom > 0 || Object.values(el.childrens).some((ch: number) => ch > 0)),
			plannedCategories: Array<string> = isCategoriesPlanned
				? Object.keys(this.categoriesRecommendedPercent)
					.filter((key: string) => this.categoriesRecommendedPercent[key].recom > 0 ||
						Object.values(this.categoriesRecommendedPercent[key].childrens).some((ch: number) => ch > 0))
				: [];

		let useProducts: Array<Product> = [...planogram.products()];
		if (this.sessionService.hasFeature(ServiceFeatures.MasterPositions) && !productOptions.useMasterPositions) {
			useProducts = useProducts.filter((product: Product) => !product.is_master_position);
		}

		if (selectedBlocks.length) {
			const activeBlocksProducts: Array<ProductGroup> = Utils.to1DArray(
				selectedBlocks.map((b: Block) => b.products)
			);

			planogram.stand.products = planogram.stand.products
				.filter((pg: ProductGroup) => !activeBlocksProducts.includes(pg));

			selectedBlocks.forEach((b: Block) => {
				const groupBy: any = b.getGroupByOption(this.autoLayoutHierachy);
				let output: Array<Product> = [...useProducts];

				if (!b.hasEmptySettings) {
					output = output.filter((p: Product) => b.isProductSuitable(p))
				} else {
					switch (groupBy) {
						case 'category': {
							output = output.filter((p: Product) => Utils.to1DArray(p.categories).some(i => b.categories.includes(i.id)));
							break;
						}
						case 'brand': {
							output = output.filter((p: Product) => p.brands.some(i => b.brands.includes(i.id)));
							break;
						}
						case 'supplier': {
							output = output.filter((p: Product) => p.suppliers.some(i => b.suppliers.includes(i.id)));
							break;
						}
					}
				}

				output = Utils.to1DArray(Utils.groupBy(output, PlanogramEditComponent.autoLayoutGroupMapper[this.autoLayoutExtraMapper[groupBy]]));

				finalProducts.push({
					products: Utils.shuffle(output),
					width: b.width,
					limits: { from: b.boundingX.min, to: b.boundingX.max }
				});
			});
		} else {
			planogram.stand.products = [];

			const groupProperty = productOptions.groupBy,
				groupedProducts = Utils.groupBy(useProducts, PlanogramEditComponent.autoLayoutGroupMapper[groupProperty])
					.map((group) =>
						Utils.to1DArray(
							Utils.groupBy(group, PlanogramEditComponent.autoLayoutGroupMapper[this.autoLayoutExtraMapper[groupProperty]])
						)
					),
				isProductsGrouped: boolean = ['category', 'brand'].includes(groupProperty) &&
					(isBrandsPlanned || isCategoriesPlanned);

			if (isProductsGrouped) {
				const groupPercentDictionary =
						groupProperty == 'category'
							? this.categoriesRecommendedPercent
							: this.brandsRecommendedPercent,
					recommendedPercents = groupedProducts.reduce((p, group: Array<Product>) => {
						const id =
							groupProperty == 'category'
								? group[0].categoriesUniqueId || '-1'
								: group[0].brandsUniqueId || '-1';
						p[id] = group.reduce((a, b) => a + percentDictionary[b.id], 0);
						return p;
					}, {}),
					panelIds = groupProperty == 'category'
						? this.categoriesInfoCopy.map((c) => c.id)
						: this.brandsInfoCopy.map((b) => b.id),
					sortedIds = Object.keys(groupPercentDictionary)
						.filter((item) => panelIds.includes(String(item)))
						.sort((a, b) => {
							if (groupProperty == 'category') {
								return groupPercentDictionary[b].recom - groupPercentDictionary[a].recom ||
									recommendedPercents[b] - recommendedPercents[a];
							} else {
								return groupPercentDictionary[b] - groupPercentDictionary[a] ||
									recommendedPercents[b] - recommendedPercents[a];
							}
						}),
					groupedProductsSorted = [];

				groupedProducts.forEach((group: Array<Product>) => {
					const id = groupProperty == 'category' ? group[0].categoriesUniqueId || '-1' : group[0].brandsUniqueId || '-1',
						index = sortedIds.indexOf(String(id));

					groupedProductsSorted[index] = { id, products: group };
				});

				finalProducts.push(
					...this.setFragmentsForGroupedProducts(
						groupedProductsSorted,
						recommendedPercents,
						groupProperty,
						groupPercentDictionary,
						productOptions.useSales,
						planogram.stand.Width
					)
				);
			} else {
				const standWidth = planogram.stand.Width;
				groupedProducts.forEach((group) => {
					if (productOptions.useSales) {
						finalProducts.push({
							products: group,
							width: Utils.round(
								group.reduce((a, b) => Utils.round(a + percentDictionary[b.id]), 0) *
								standWidth
							)
						});
					} else {
						finalProducts.push({
							products: group,
							width: Utils.round((group.length / useProducts.length) * standWidth)
						});
					}
				});
			}

			const reindexProducts = [...finalProducts],
				reindexValues = Utils.shuffle(Object.entries(finalProducts)).reduce(
					(a, b, idx) => {
						a[b[0]] = idx;
						return a;
					},
					{}
				);

			for (let idx in reindexValues) {
				if (reindexValues.hasOwnProperty(idx))
					finalProducts[reindexValues[idx]] = reindexProducts[idx];
			}

			finalProducts.forEach((group, index) => {
				const from = index ? finalProducts[index - 1].limits.to : 0;
				group.limits = { from, to: Utils.round(from + group.width) };
			});
		}

		finalProducts.forEach(
			PlanogramEditComponent.insertProductsOnStand({
				stands: [planogram],
				blocks: selectedBlocks,
				productOptions,
				percentDictionary,
				hierarchy,
				cachedPlacePredict: this.cachedPlacePredict,
				dataGrouped: this.brandsPanelDataGrouped,
				groupProperty: productOptions.groupBy,
				plannedBrands,
				plannedBrandsPercent: this.brandsRecommendedPercent,
				plannedCategories,
				plannedCategoriesPercent: this.categoriesRecommendedPercent,
				hasReceipts: this.sessionService.hasFeature(ServiceFeatures.Receipts),
				personalSettings: this.sessionService.personalSettings
			})
		);

		this.activity.normalizeElements(planogram.stand);

		this.getAbcReport();
		void this.setProductsPercents();
		this.setRealProductPercents();
		this.activity.setProductActive();
		this.historyGenerate();
		this.activity.requestCanvasUpdate().subscribe({
			complete: () => {
				this.loader.set(false);
				this.blockAutolayout = false;
			}
		});
	}

	applyLayoutRule({ data, rule, stand, planogram }: { data: any; rule: any; stand: EquipmentBase; planogram?: IPlanogram }) {
		this.activity.setLayoutRulesProducts(this.layoutRules ? data : {});

		this.activity.normalizeElements(stand, [], true);

		this.renderBehavior.next({
			rule,
			stand,
			planogram: planogram
				?? this.layoutRulesPlanogram()
				?? this.findPlanogramByStand(stand)
				?? this.resolveActivePlanogram()
		});
	}

	saveLayoutRule(id: number) {
		this.activeRule.saved = true;
		if (!this.activeRule.id) {
			this.activeRule.id = id;
			this.activity.forEachHistoryEntry((historyObj: HistoryInstance) => historyObj.rule.id = id);
		}
		this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleSave, {
			ruleName: this.activeRule.name
		});
	}

	setFragmentsForGroupedProducts(
		groupedProducts,
		recommendedPercents,
		groupProperty,
		groupPercentDictionary,
		takeIntoPrice,
		standWidth: number
	) {
		const fragments = [],
			zeroGroupsLength = groupedProducts
				.filter((group) =>
					groupProperty == 'category'
						? groupPercentDictionary[group.id].recom == 0
						: groupPercentDictionary[group.id] == 0
				)
				.reduce((a, b) => a + b.products.length, 0),
			totalPlannedPercent =
				Object.values<any>(groupPercentDictionary)
					.map((prop) => (groupProperty == 'category' ? prop.recom : prop))
					.reduce((a, b) => Utils.round(a + b), 0) / 100,
			totalRecommendedPercent = Object.keys(groupPercentDictionary)
				.filter((key) =>
					groupProperty == 'category'
						? groupPercentDictionary[key].recom == 0
						: groupPercentDictionary[key] == 0
				)
				.reduce((p, key) => recommendedPercents[key] + p, 0);

		let previousElementsSum = 0;

		for (let group of groupedProducts) {
			if (previousElementsSum >= standWidth) break;

			const productItemValue =
				groupProperty == 'category'
					? groupPercentDictionary[group.id].recom
					: groupPercentDictionary[group.id];

			if (groupProperty == 'category') {
				const groupedData = Utils.groupBy(group.products, PlanogramEditComponent.autoLayoutGroupMapper['brand']);
				groupedData.forEach((g, idx) =>
					g.forEach(
						(p) =>
							(p.plannedPercent = this.getBrandPlannedPercent(
								p,
								groupedData[idx].length
							))
					)
				);
			}

			if (productItemValue) {
				fragments.push({
					products: group.products,
					width: Utils.round(
						Math.min(
							standWidth - previousElementsSum,
							(productItemValue / 100) * standWidth
						)
					)
				});
			} else {
				let recommendPercent,
					restPercent = totalPlannedPercent < 1 ? 1 - totalPlannedPercent : 0;

				if (takeIntoPrice) {
					recommendPercent = recommendedPercents[group.id];
					if (restPercent > totalRecommendedPercent) {
						recommendPercent =
							(recommendPercent / totalRecommendedPercent) * restPercent;
					}
				} else {
					recommendPercent =
						(restPercent * group.products.length) / zeroGroupsLength;
				}

				const width = Utils.round(
					Math.min(
						standWidth - previousElementsSum,
						recommendPercent * standWidth
					)
				);

				fragments.push({
					products: group.products,
					width: isNaN(width) ? 0 : width
				});
			}
			previousElementsSum += fragments[fragments.length - 1].width;
		}

		return fragments;
	}

	getBrandPlannedPercent(product: Product, length) {
		const categoryId = product.categoriesUniqueId || '-1',
			brandId = product.brandsUniqueId || '-1',
			productCategory = this.categoriesRecommendedPercent[categoryId];

		if (productCategory) {
			return productCategory.childrens[brandId] / 100 / length;
		}
		return 0;
	}

	getChangedProducts(n_id, s_id, p_id) {
		return this.layoutService.getNotificationStandById(n_id, s_id, p_id)
			.then((data) => {
				this.changedProducts = data.notification_conf.data.find(
					(i) => i.id == Number(s_id)
				);
			})
			.then(() => {
				this.changedProducts.conf.data.forEach((p) => {
					this.activity.addRotatedProduct(p.product_rotate_to.id);
				});
			});
	}

	openReportConfigs() {
		this.activity.openPanel('autolayout');
	}

	showEditInstrumentWarning() {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'EDIT_INSTRUMENT_WARNING_MESSAGE',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
			if (result.answer) {
				this.mixPanelService.trackPage(MixpanelEvents.EquipmentEditFromPlanogram);
				return this.router.navigate(
					this.sessionService.getLink(['equipment', 'edit', this.activity.planogram()?.stand.id]),
					{
						queryParams: {
							stand_id: this.activity.planogram()?.id,
							planogram_id: this.planConf?.id,
							from: this.planogramGroup?.id ? 'stand-group' : 'stand',
							...(this.planogramGroup?.id && { group_id: this.planogramGroup.id })
						}
					}
				);
			}
		});
	}

	revertStand(id: number): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramRevert)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.RevertPlanogram);
			return;
		}

		const revert = (id: number) => {
			this.loader.set(true);
			this.planogramsService.revertStand(id).subscribe(() => {
				this.mixPanelService.trackPage(MixpanelEvents.PlanogramRevert);
				window.location.reload();
			});
		};

		if (this.saveAble() && this.ignoreOtherSave()) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'SAVING_BEFORE_REVERT_WARNING',
					confirmLabel: 'YES',
					cancelLabel: 'NO',
					twoAnswers: true
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer === null) return;
				if (result.answer) await lastValueFrom(this.performSaveAction(false, true));
				revert(id);
			});
		} else {
			revert(id);
		}
	}

	async openLayout(): Promise<void> {
		if (!this.activity.planogram()) return;

		const wasOpen: boolean = this.layoutOpen;
		this.layoutOpen = !wasOpen;

		if (!wasOpen && this.layoutOpen) {
			await this.refreshConversation();
			this.layoutHistoryComponent?.scrollToBottom();
		}
	}

	async openEfficiencyAnalysis() {
		this.analysisOpen = !this.analysisOpen;
	}

	private async reloadEfficiencyAnalysisData(): Promise<void> {
		if (!this.analyticsPlanogram()) {
			this.productsAnalytic = [];
			return;
		}

		this.panelLoader = true;
		this.productsAnalytic = await this.getProductsEfficiencyAnalysisData();
		this.panelLoader = false;
	}

	getAnalyticsPanelProducts(): Array<Product> {
		const products: Array<Product> = this.panelProducts ?? [];
		const planogram: IPlanogram | undefined = this.analyticsPlanogram();

		if (!planogram || this.planograms().length <= 1) {
			return products;
		}

		const planogramProducts: Set<Product> = new Set(planogram.products());

		return products.filter((product: Product) => planogramProducts.has(product));
	}

	async getProductsEfficiencyAnalysisData() {
		const planogram: IPlanogram | undefined = this.analyticsPlanogram();
		if (!planogram) return;

		const defaults: any = this.sessionService.defaults,
			date_filters = {
				date_from: moment(defaults.date_to)
					.subtract('30', 'day')
					.format('YYYY-MM-DD'),
				date_to: defaults.date_to
			};

		return await this.productsService.getProductsForEfficiencyAnalysis({
			planogram: Number(planogram.planogram_id),
			stand: planogram.id,
			products: this.getAnalyticsPanelProducts().map((p: Product) => p.id),
			percents: [
				{ a: 80, b: 15, c: 5 },
				{ a: 80, b: 15, c: 5 },
				{ a: 80, b: 15, c: 5 },
				{ a: 80, b: 15, c: 5 }
			],
			date_from: date_filters.date_from,
			date_to: date_filters.date_to,
			turnover_coef: this.reportParams.turnover,
			profit_coef: this.reportParams.profit,
			qty_coef: this.reportParams.qty,
			receipts_qty_coef: this.reportParams.receipts_qty
		});
	}

	async setNewPlannedPercent(event: any) {
		this.categoriesRecommendedPercent = {};
		this.brandsRecommendedPercent = {};

		event.brands.forEach(
			(b) => (this.brandsRecommendedPercent[b.id] = b.recom)
		);
		event.categories.forEach((c) => {
			this.categoriesRecommendedPercent[c.id] = {
				recom: c.recom || 0,
				childrens: {}
			};
			if (c.childrens.length) {
				c.childrens.forEach(
					(child) =>
						(this.categoriesRecommendedPercent[c.id].childrens[child.id] =
							child.recom || 0)
				);
			}
		});

		this.brandsPanelDataGrouped = event.brandsPanelDataGrouped;

		this.getBrandStatisticsPlanograms().forEach((planogram: IPlanogram) => {
			const conf: any = {
				...planogram.conf,
				products: planogram.products().map((p: Product) => ({
					id: p.id,
					planedPercent: p.planedPercent
				})),
				reportParams: this.reportParams,
				productFilters: this.addProductsFilterService.filters,
				brandsRecommendedPercent: this.brandsRecommendedPercent,
				categoriesRecommendedPercent: this.categoriesRecommendedPercent,
				brandsPanelDataGrouped: this.brandsPanelDataGrouped,
				layout_meters: planogram.stand.layoutMeters
			};

			planogram.conf = conf;
			this.planogramsService.updateStandField(planogram.id, { conf });
		});
	}

	applyLabelsConfig(values: ILabelsState) {
		this.activity.updateLabelsState(values);
		this.showTags();
		this.activity.closePanel();
	}

	refreshBlockElements(block: Block) {
		block.cache.elements = null;
		return block.elements;
	}

	getProductSizes() {
		return Utils.getProductSizes(this.activity.previewProduct(), 120);
	}

	isNeedCloseRightPanel() {
		if (this.activity.panelType() == 'layoutrules') return;
		if (this.activity.panelType() == 'brand') return;
		if (this.activity.activeBlocks().length) return;
		if (this.activity.dragProducts().length) return;
		if (this.activity.settingsPanelDisabled()) this.activity.closePanel();
	}

	openInitOnboardingModal(): void {
		if (
			!this.sessionService.hasFeature(ServiceFeatures.Onboarding)
			|| this.sessionService.settings.demoOnboardingPlanogramOpened
		) return;

		this.dialog
			.open<OnboardingModalComponent, IOnboardingModalData>(OnboardingModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					doneLabel: 'ONBOARDING_MODAL.OPENED_PLANOGRAM',
					titleLabel: 'ONBOARDING_MODAL.LAYOUT_PRODUCTS',
					descriptionLabel: 'ONBOARDING_MODAL.LAYOUT_PRODUCTS_DESCRIPTION',
					imageUrl: '/static/img/onboarding/layoutProducts.jpg',
					mainButtonLabel: 'COMMON.ROGER_THAT',
					mixpanelEvent: MixpanelEvents.OnboardingPlacePlanogram
				}
			})
			.afterClosed().subscribe(() => {
				this.sessionService.settings.demoOnboardingPlanogramOpened = true;
				this.sessionService.updateSettings();
			});
	}

	openSaveOnboardingModal(): void {
		if (
			!this.sessionService.hasFeature(ServiceFeatures.Onboarding)
			|| this.sessionService.settings.demoOnboardingPlanogramSaved
		) return;

		this.dialog
			.open<OnboardingModalComponent, IOnboardingModalData>(OnboardingModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					doneLabel: 'ONBOARDING_MODAL.CREATED_PLANOGRAM',
					titleLabel: 'ONBOARDING_MODAL.EXPORT_PLANOGRAM',
					descriptionLabel: 'ONBOARDING_MODAL.EXPORT_PLANOGRAM_DESCRIPTION',
					imageUrl: '/static/img/onboarding/exportPlanogram.jpg',
					secondaryButtonLabel: 'COMMON.DO_IT_LATER',
					mainButtonLabel: 'COMMON.GO_TO_VIEW_MODE',
					mixpanelEvent: MixpanelEvents.OnboardingCreatePlanogram
				}
			})
			.afterClosed().subscribe((result: boolean) => {
				this.sessionService.settings.demoOnboardingPlanogramSaved = true;
				this.sessionService.updateSettings();
				if (result) {
					this.router
						.navigate(
							this.sessionService.getLink(['stand', 'view', this.standId]),
							{ queryParams: { planogram: this.planConf?.id } }
						)
						.finally();
				}
			});
	}

	showTags() {
		if (this.activity.labelsState().abcTurnover || this.activity.labelsState().abcReceipts) {
			this.getAbcReport();
		} else {
			this.activity.requestCanvasUpdate();
		}
	}

	reverseLayout(): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.ReversedLayout);
			return;
		}
        const planogram: IPlanogram = this.activity.planogram();
		this.isRenderingReversed = true;
		planogram.stand = this.planogramLayoutService.getReversed(planogram.stand);

		this.activity.normalizeElements(planogram.stand, []);
		this.activity.planogramUpdate$.next(planogram);

		this.activity.historyGenerate$.next();

		this.activity.requestCanvasUpdate().subscribe({
			complete: () => {
				if (this.activity.activeProducts().length) this.activity.setProductActive();

				timer(0).subscribe(() => {
					this.isRenderingReversed = false;
				});
			}
		});

		this.mixPanelService.trackPage(MixpanelEvents.MirrorLayoutFromPlanogramEdit);
	}

	getFreeHoles(element: Element): Array<Element> {
		const elementDepend = element.parent.type == 'Hole' ? element.depend : element;
		let sortedHoles = element.stand.allElements
			.filter((el) => el.depend == elementDepend && !el.products.length)
			.sort((a, b) =>
				b.shapes[0].begin.y - a.shapes[0].begin.y ||
				a.shapes[0].begin.x - b.shapes[0].begin.x
			);

		if (element.parent.type == 'Hole') {
			const thisHoleIndex: number = sortedHoles.findIndex((el: Element) => el === element);
			sortedHoles = sortedHoles.filter((element, index) => index >= thisHoleIndex);
		}

		return sortedHoles;
	}

	async changeHighlightOption(value: HighlightOptionsValue) {
		this.closePanel();

		this.activity.setHighlightValue(value);
		this.highlightProductsService.setColors(this.products());

		const highlightFunc = this.highlightProductsService.applyHighlight(
			this.products(),
			this.planograms().map((planogram: IPlanogram) => planogram.main_stand),
			this.planConf?.id
		);
		if (highlightFunc[this.activity.highlightValue()]) {
			this.activity.setHighlightedProducts(await highlightFunc[this.activity.highlightValue()]());
			this.mixPanelService.trackPage(
				MixpanelEvents.HighlightProductsOnPlanogramEdit,
				{ type: this.activity.highlightValue() }
			);
		} else {
			this.activity.clearHighlightedProducts();
		}
		setTimeout(() => this.activity.requestCanvasUpdate().subscribe());
	}

	// resize left panel block start
	closeLeftPanel(): void {
		if (this.leftPanelClosed) return;

		this.leftPanelClosed = true;
		this.closeAddProductsPanel();
		this.sessionService.defaults.settings.planogramEditLeftPanelClosed = true;
		this.sessionService.updateSettings();
	}

	openLeftPanel(): void {
		if (!this.leftPanelClosed) return;

		this.leftPanelClosed = false;
		this.sessionService.defaults.settings.planogramEditLeftPanelClosed = false;
		this.sessionService.updateSettings();
	}

	onResizeStart(event: MouseEvent): void {
		this.isResizing = true;
		this.startX = event.clientX;
		this.startWidth = event.target['parentElement'].clientWidth;

		window.addEventListener('mousemove', this.onResizing);
		window.addEventListener('mouseup', this.onResizeEnd);
	}

	private onResizing = (event: MouseEvent): void => {
		event.preventDefault();

		if (this.isResizing) {
			const movementX: number = event.clientX - this.startX,
				newWidth: number = Math.min(Math.max(this.startWidth + movementX, 420), 1000); // min(), max() need for add products panel
			this.leftResizablePanel.nativeElement.style.width = `${newWidth}px`;
			this.leftPanelSavedWidth = newWidth;
		}
	}

	private onResizeEnd = () => {
		this.sessionService.defaults.settings.planogramEditLeftPanelWidth = this.leftPanelSavedWidth;
		this.sessionService.updateSettings();
		this.isResizing = false;
		window.removeEventListener('mousemove', this.onResizing);
		window.removeEventListener('mouseup', this.onResizeEnd);
	}
	// resize left panel block end

	arbitraryLayoutHandler(planogram: EquipmentBase): void {
		if (planogram.arbitraryLayout && !this.sessionService.defaults.settings.hideArbitraryWarning) {
			this.dialog
				.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '700px',
					height: 'auto',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'PLANOGRAM_EDIT.ARBITRARY_LAYOUT_CONFIRM',
						twoAnswers: true,
						confirmLabel: 'COMMON.CONFIRM',
						cancelLabel: 'COMMON.CANCEL',
						checkBox: true,
						checkBoxLabel: 'NOT_SHOW_ANYMORE'
					}
				})
				.afterClosed()
				.subscribe((result: IConfirmModalAnswer) => {
					if (!result.answer) return;

					if (result.checkBoxState) {
						this.sessionService.defaults.settings.hideArbitraryWarning = result.answer;
						this.sessionService.updateSettings();
					}

					this.setArbitraryLayout(planogram);
				});
		} else {
			this.setArbitraryLayout(planogram);
		}
	}

	setArbitraryLayout(planogram: EquipmentBase): void {
		planogram.arbitraryLayout = !planogram.arbitraryLayout;

		this.mixPanelService.trackPage(MixpanelEvents.ArbitraryLayout, { status: planogram.arbitraryLayout });

		if (planogram.arbitraryLayout) planogram.alignment = AlignmentTypes.Default;

		this.activity.normalizeElements(planogram);
		this.historyGenerate();
		this.activity.requestCanvasUpdate();
		this.closePanel();
	}

	async saveNewEquipment(): Promise<void> {
		if (!this.saveAble() || this.isSaveNewEquipmentDisabled()) return;

		const planogram: IPlanogram = this.activity.planogram() ?? this.planograms()[0];
		if (!planogram) return;

		if (this.saveTimeout) {
			clearTimeout(this.saveTimeout);
			this.saveTimeout = null;
		}

		const id: number = await this.createEquipment(planogram, true);

		if (!id) {
			this.saveAble.set(true);
			return;
		}

		try {
			this.socketService.deleteSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(planogram.stand?.id )});
			planogram.stand.id = id;
			this.socketService.addSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(planogram.stand?.id )});

			await this.planogramsService.partialUpdate(this.getSaveConfiguration(planogram), this.planConf?.id);
			this.markMasterRuleOutdatedLocally([planogram]);
			await lastValueFrom(this.performSaveAction(false, true));
			this.equipmentUsage = [planogram.id];
			this.finalizeEquipmentEditHistory();
			this.toggleEquipmentEditMode();
		} finally {
			this.loader.set(false);
		}
	}

	async saveCurrentEquipment(): Promise<void> {
		const changedByEquipmentId: Map<number, IPlanogram> = this.getChangedPlanogramsByEquipmentId();
		if (!changedByEquipmentId.size || !this.ignoreOtherEquipmentSave) return;

		const confirmLabel: string = await lastValueFrom<string>(this.translate.get('PLANOGRAMS_LIST.QUANTITY'));

		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EQUIPMENT_EDIT.SAVE_CURRENT_EQUIPMENT_CONFIRM',
					secondLabel: `${confirmLabel}: ${this.getTotalAffectedPlanogramsCount()}`,
					cancelLabel: 'COMMON.CANCEL',
					confirmLabel: 'REPLACE',
					twoAnswers: true
				}
			})
			.afterClosed()
			.subscribe(async (result: IConfirmModalAnswer) => {
				if (!result.answer) {
					this.saveAble.set(this.activity.hasAnyHistoryChanges());
					return;
				}

				this.loader.set(true);
				this.mixPanelService.trackPage(MixpanelEvents.EquipmentSaveCurrentFromPlanogram);
				this.saveAble.set(false);

				try {
					const uniqueChangedByEquipmentId: Map<number, IPlanogram> = this.getChangedPlanogramsByEquipmentId();
					if (!uniqueChangedByEquipmentId.size) return;

					const configurations: Array<StandConf> = [];
					const updatedPlanograms: Array<IPlanogram> = [];
					const processedPlanogramIds: Set<number> = new Set();
					const equipmentNotifications: Array<{ equipment_id: number; planograms: Array<{ plan_id: number; planogram_id: number }> }> = [];

					const equipmentsToSave: Array<EquipmentBase> = [...uniqueChangedByEquipmentId.values()]
						.map((planogram: IPlanogram) => planogram.stand);
					await this.saveEquipments(equipmentsToSave);

					for (const sourcePlanogram of uniqueChangedByEquipmentId.values()) {
						const beginState: HistoryInstance | null = this.getEquipmentEditBeginState(sourcePlanogram);
						if (!beginState) continue;

					const usageIds: Array<number> = this.getEquipmentUsageForPlanogram(sourcePlanogram);
					const sessionPlanograms: Array<IPlanogram> = usageIds
						.map((id: number) => this.getPlanogramById(id))
						.filter((planogram: IPlanogram | undefined): planogram is IPlanogram => !!planogram);
					const pageSiblings: Array<IPlanogram> = this.activity.getPlanogramsWithSameEquipment(
						sourcePlanogram.stand.id,
						sourcePlanogram
					);
					const externalPlanograms: Array<IPlanogram> = await this.fetchExternalPlanograms(
						usageIds.filter((id: number) => !this.getPlanogramById(id) && id !== sourcePlanogram.id)
					);
					const affectedPlanogramsById: Map<number, IPlanogram> = new Map([
						[sourcePlanogram.id, sourcePlanogram],
						...sessionPlanograms.map((planogram: IPlanogram) => [planogram.id, planogram] as const),
						...pageSiblings.map((planogram: IPlanogram) => [planogram.id, planogram] as const),
						...externalPlanograms.map((planogram: IPlanogram) => [planogram.id, planogram] as const)
					]);
					const affectedPlanograms: Array<IPlanogram> = [...affectedPlanogramsById.values()];

					affectedPlanograms.forEach((planogram: IPlanogram) => {
						if (planogram.id === sourcePlanogram.id) return;

						this.activity.syncEquipmentStandFromSource(sourcePlanogram, planogram);
					});

					affectedPlanograms.forEach((planogram: IPlanogram) => {
						if (processedPlanogramIds.has(planogram.id)) return;

						configurations.push(this.buildPlanogramStandConf(planogram));
						updatedPlanograms.push(planogram);
						processedPlanogramIds.add(planogram.id);
					});

					const planogramsWithErrors: Array<IPlanogram> = affectedPlanograms
						.filter((planogram: IPlanogram) => planogram.stand.products.some((pg: ProductGroup) => pg.error))
						.filter((planogram: IPlanogram) => planogram.planogram_stand?.length);

					if (planogramsWithErrors.length) {
						const errorIndicators: Array<any> = planogramsWithErrors
							.filter((planogram: IPlanogram) => planogram.id !== sourcePlanogram.id)
							.map((planogram: IPlanogram) => ({
								planogram: planogram.id,
								name: 'EQUIPMENT_CHANGED',
								type: 'ERROR'
							}));

						if (errorIndicators.length) {
							this.planogramsService.createIndicators(errorIndicators).subscribe();
						}

						equipmentNotifications.push({
							equipment_id: sourcePlanogram.stand.id,
							planograms: planogramsWithErrors.map((item: IPlanogram) => ({
								plan_id: item.planogram_id,
								planogram_id: item.id
							}))
						});
					}
				}

				if (configurations.length) {
					await lastValueFrom(this.planogramsService.bulkUpdate(configurations));
					this.markMasterRuleOutdatedLocally(updatedPlanograms);
					await this.planogramsService.makeSvg(
						this.planogramsService.getDefaultSvgConf(updatedPlanograms)
					);
					this.activity.requestCanvasUpdate(
						updatedPlanograms.filter((planogram: IPlanogram) => !!this.getPlanogramById(planogram.id))
					);
				}

				equipmentNotifications.forEach((notification) => {
					this.notificationsService.createEquipmentEditNotification(notification).subscribe();
				});

				this.finalizeEquipmentEditHistory();
				this.toggleEquipmentEditMode();
				} finally {
					this.loader.set(false);
				}
			});
	}

	cancelEquipmentChanges(): void {
		this.mixPanelService.trackPage(MixpanelEvents.EquipmentEditCancel);

		const changedPlanograms: Array<IPlanogram> = this.getPlanogramsWithEquipmentChanges();

		if (!changedPlanograms.length) {
			this.toggleEquipmentEditMode();
			return;
		}

		Promise.all(
			changedPlanograms.map((planogram: IPlanogram) => {
				const beginState: HistoryInstance | null = this.getEquipmentEditBeginState(planogram);
				if (!beginState) return Promise.resolve();

				return this.historyApply(beginState, planogram);
			})
		).then(() => this.toggleEquipmentEditMode());
	}

	async createEquipment(planogram: IPlanogram, retainLoader: boolean = false): Promise<number> {
		const dialogRef = this.dialog.open(EquipmentCreateNameModalComponent, {
			width: '450px',
			autoFocus: true,
			backdropClass: 'back-drop'
		});

		const result = await lastValueFrom(dialogRef.afterClosed());

		if (!result) return null;

		this.mixPanelService.trackPage(MixpanelEvents.EquipmentSaveAsNewFromPlanogram);
		await this.renderSaveTemplate(planogram.stand);

		const configurations: IEquipmentConfigurations = EquipmentService
			.getEquipmentConfigurations(planogram.stand);

		const image = this.saveTemplate.getImage();
		const payload: any = {
			name: result.name,
			code: planogram.stand.code,
			producer: planogram.stand.producer,
			empty: false,
			linear_conf: configurations.linearConf,
			conf: configurations.conf,
			new_conf: configurations.newConf,
			type: planogram.stand.type
		};

		if (image) {
			payload.image = image;
		}

		this.loader.set(true);
		const newEquipment: IEquipment = await lastValueFrom(this.equipmentService.create(payload));

		if (!retainLoader) {
			this.loader.set(false);
		}

		return newEquipment.id;
	}

	async saveEquipments(equipments: Array<EquipmentBase>): Promise<void> {
		const uniqueEquipments: Array<EquipmentBase> = [...new Map(
			equipments.map((equipment: EquipmentBase) => [equipment.id, equipment])
		).values()];

		if (!uniqueEquipments.length) return;

		const updates: Array<IEquipmentUpdatePayload> = [];

		for (const equipment of uniqueEquipments) {
			updates.push(await this.buildEquipmentUpdatePayload(equipment));
		}

		await lastValueFrom(this.equipmentService.bulkSave(updates));
	}

	private async buildEquipmentUpdatePayload(equipment: EquipmentBase): Promise<IEquipmentUpdatePayload> {
		const configurations: IEquipmentConfigurations = EquipmentService.getEquipmentConfigurations(equipment as Equipment);

		await this.renderSaveTemplate(equipment);

		const equipmentImage: string | null = this.saveTemplate?.getImage() ?? null;
		const params: Record<string, unknown> = {
			empty: false,
			linear_conf: configurations.linearConf,
			conf: configurations.conf,
			new_conf: configurations.newConf
		};

		if (equipmentImage) {
			params.image = equipmentImage;
		}

		return { id: equipment.id, params };
	}

	private async renderSaveTemplate(equipment: EquipmentBase): Promise<void> {
		if (!this.saveTemplate) return;

		this.saveTemplate.stand = [equipment as Equipment];

		for (let attempt: number = 0; attempt < 20; attempt++) {
			if (await this.saveTemplate.updateScene()) {
				this.saveTemplate.render();
				return;
			}

			await new Promise((resolve: (value: void) => void) => setTimeout(resolve, 50));
		}

		this.saveTemplate.render();
	}

	private syncStandUpdatedAt(): void {
		const dates: Array<string> = this.planograms()
			.map((planogram: IPlanogram) => planogram.date)
			.filter((date: string | undefined): date is string => !!date);

		if (!dates.length) {
			this.standUpdatedAt.set(null);
			return;
		}

		const latestDate: string = dates.reduce((latest: string, date: string) =>
			moment(date).isAfter(moment(latest)) ? date : latest
		);

		this.standUpdatedAt.set(latestDate);
	}

	private syncMasterRuleIndicator(planogram: IPlanogram | null | undefined): void {
		const indicator: IPlanogramIndicator | undefined = (planogram?.indicators ?? [])
			.find((item: IPlanogramIndicator): boolean => item.type === 'MASTER_RULE');

		this.masterRuleIndicator.set(indicator ? resolveIndicatorVisual(indicator) : null);
	}

	private markMasterRuleOutdatedLocally(planograms: Array<IPlanogram>): void {
		for (const planogram of planograms) {
			for (const indicator of planogram.indicators ?? []) {
				if (indicator.type === 'MASTER_RULE' && indicator.name === 'MASTER_RULE_APPLIED') {
					indicator.name = 'MASTER_RULE_OUTDATED';
				}
			}
		}

		if (this.planograms().length === 1) {
			this.syncMasterRuleIndicator(this.planograms()[0]);
		}
	}

	private getPlanogramsToSave(): Array<IPlanogram> {
		return this.planograms().filter((planogram: IPlanogram) =>
			this.activity.hasHistoryChanges(planogram.main_stand)
		);
	}

	private buildMakeSvgConf(planogram: IPlanogram): any {
		const imagesMode: boolean = this.resolveImagesMode(planogram);
		const posMaterialsMode: boolean = this.resolvePosMaterialsMode(planogram);
		const sequenceNumberMode: boolean = this.resolveSequenceNumberMode(planogram);
		const transparentBoxesMode: boolean = this.resolveTransparentBoxesMode(planogram);
		const sizesMode: boolean = this.resolveSizesMode(planogram);

		return {
			main_stand_id: planogram.main_stand,
			article_photo_mode: !imagesMode,
			sequence_number_mode: sequenceNumberMode,
			transparent_boxes: transparentBoxesMode,
			show_pos_materials: posMaterialsMode,
			view_mode: this.activity.focusedCanvas().element ? 'top' : 'direct',
			element_id: this.activity.focusedCanvas().element ?? undefined,
			planogram_id: Number(planogram.planogram_id),
			size_mode: sizesMode,
			show_stock_qty: this.activity.labelsState().stockQty,
			show_prices: this.activity.labelsState().price,
			show_abc_turnover: this.activity.labelsState().abcTurnover,
			show_abc_receipts: this.activity.labelsState().abcReceipts,
			abc_data: this.abcDataByStand[planogram.main_stand]
		};
	}

	private resolveImagesMode(planogram: IPlanogram): boolean {
		if (this.planograms().length === 1 && this.planogramCanvas) {
			return this.planogramCanvas.imagesMode();
		}

		return this.planogramCanvasOverlayService.getImagesMode(planogram) ?? this.modes.imagesMode();
	}

	private resolvePosMaterialsMode(planogram: IPlanogram): boolean {
		if (this.planograms().length === 1 && this.planogramCanvas) {
			return this.planogramCanvas.posMaterialsMode();
		}

		return this.planogramCanvasOverlayService.getPosMaterialsMode(planogram) ?? true;
	}

	private resolveSequenceNumberMode(planogram: IPlanogram): boolean {
		if (this.planograms().length === 1 && this.planogramCanvas) {
			return this.planogramCanvas.sequenceNumberMode();
		}

		return this.planogramCanvasOverlayService.getSequenceNumberMode(planogram) ?? this.modes.sequenceNumberMode();
	}

	private resolveTransparentBoxesMode(planogram: IPlanogram): boolean {
		if (this.planograms().length === 1 && this.planogramCanvas) {
			return this.planogramCanvas.transparentBoxesMode();
		}

		return this.planogramCanvasOverlayService.getTransparentBoxesMode(planogram) ?? this.modes.transparentBoxesMode();
	}

	private resolveSizesMode(planogram: IPlanogram): boolean {
		if (this.planograms().length === 1 && this.planogramCanvas) {
			return this.planogramCanvas.sizesMode();
		}

		return this.planogramCanvasOverlayService.getSizesMode(planogram) ?? this.modes.sizesMode();
	}

	getSaveConfiguration(planogram: IPlanogram): StandConf {
		const productsConfiguration: Array<IProductConf> = this.productsService.getProductsToConf(planogram.id, planogram.stand.products);
        const isActiveCanvas: boolean = this.activity.focusedCanvas().planogram == planogram.main_stand;

		return this.planogramsService.getConf(planogram, productsConfiguration, {
			id: planogram.id,
			main_stand: planogram.main_stand,
			blocks: planogram.stand.blocks.map((block: Block) => block.toJSON()),
			view_mode: isActiveCanvas && this.activity.focusedCanvas().element ? 'top': 'direct',
			element_id: isActiveCanvas ? (this.activity.focusedCanvas().element ?? undefined) : undefined,
			size_mode: this.modes.sizesMode(),
			conf: {
				products: planogram.products().map((p: Product) => ({ id: p.id, planedPercent: p.planedPercent })),
				reportParams: planogram.conf?.reportParams ?? this.reportParams,
				productFilters: this.addProductsFilterService.filters,
				selectedColumns: planogram.conf.selectedColumns || [],
				brandsRecommendedPercent: this.planograms().length > 1
					? (planogram.conf?.brandsRecommendedPercent ?? {})
					: this.brandsRecommendedPercent,
				categoriesRecommendedPercent: this.planograms().length > 1
					? (planogram.conf?.categoriesRecommendedPercent ?? {})
					: this.categoriesRecommendedPercent,
				brandsPanelDataGrouped: planogram.conf?.brandsPanelDataGrouped
			}
		});
	}
}
